import { COMPARISON_BLOCKS, rawSnapshotChanges, currencyChanges, publishedComparisonProof } from "./lib/snapshot-compare";
import { TalentPageActions, TALENT_BLOCKS } from "./components/version/TalentPageActions";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "./components/shell/AppShell";
import { snapshotDocs } from "./lib/search-content";
import { Panel, SectionTitle } from "./components/ui/Panel";
import { Tag } from "./components/ui/Tag";
import { ACT_ITEM_TILE } from "../../src/game/act-item-tile.ts";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { type CardPack } from "./lib/card";
import { playerLevelRange } from "./lib/card-drop";
import { REWARD_LABEL, cardRef, setCardCatalog, setFrameCatalog, setRewardLocale, newReward, type Reward } from "./lib/reward";
import type { AchievementDict } from "./lib/achievement";
import { ApiError, checkVersionApi, fetchAchievementMetrics, onAuthLost } from "./lib/api";
import type { GateLost } from "./pages/Gate";
import { startSessionKeeper } from "./lib/session-keeper";
import { CostCurveEditor } from "./components/data/CostCurveEditor";
import { pettownSeries } from "./lib/pettown-curves";
import { EconomyCurves } from "./components/data/EconomyCurves";
import type { CurveMetric } from "./lib/economy-curves";
import { ParamForm } from "./components/data/ParamForm";
import {
  costGroups, DEFAULT_DICE, DEFAULT_FIGHT, DEFAULT_PETTOWN, diceGroups, fightGroups, type PettownParams,
  type DiceParams, type FightParams,
} from "./lib/params";
import { blankTile, type Tile } from "./lib/tiles";
import { defaultAuction, newPeriod, type AuctionDefaults, type AuctionMap } from "./lib/auction";
import { rivalColor, type RivalDef } from "./lib/fixtures";
import { validate } from "./lib/validate";
import { AddLevelDialog } from "./components/data/AddLevelDialog";
import { BatchGridDialog } from "./components/data/BatchGridDialog";
import { RenameRivalDialog } from "./components/data/RenameRivalDialog";
import { RemoveRivalDialog } from "./components/data/RemoveRivalDialog";
import { AssetDialog, type AssetCell } from "./components/data/AssetDialog";
import { LevelNamesDialog } from "./components/data/LevelNamesDialog";
import { DEFAULT_GLOBALS, fmt, levelTargetRolls, levelRuntimeB, makeLevel, renumber, type Globals } from "./lib/model";
import { BScaleContext, type BScale } from "./components/data/BCoins";
import type { Level } from "./lib/model";
import { money, toBig, type Money } from "./lib/big";
import { suggestGMilli } from "./lib/g-milli";
import { ALL_ITEMS, findItem, isTilePage, VERSIONED_PAGES } from "./lib/nav";
import { useHashRoute } from "./lib/useHashRoute";
import { probeHealth, type Health } from "./lib/health";
import { TzProvider, tzOfAgent } from "./lib/tz-context";
import { I18nProvider, loadLang, saveLang, useT, type MsgKey, type UiLang } from "./lib/i18n";
import {
  activateVersionApi, createDraftApi, createLevelApi, moveLevelApi, deleteLevelApi, deletePackApi, deleteRewardApi, fetchAgents,
  patchSelectorsApi,
  fetchLevelsOf, createFirstDraftApi, createFirstFromAgentApi, pruneDraftsApi, fetchInitLocks, type InitLocks, fetchVersions, type PublishMerge, mergeConflictsOf, putSpinApi, putTileApi, newAuctionPeriodApi, patchCommonApi, patchLevelApi, publishVersionApi,
  putAuctionLevelApi, putPackApi, putPackDropsApi, putRewardApi, setApiA0, validationErrorsOf, type ValidationErrorRow,
  putRivalsApi, putRivalCellsApi, putPettownRivalPricesApi,
  createPuzzleApi, putPuzzleApi, deletePuzzleApi, putPuzzlePricesApi,
  createIdolPhotoApi, putIdolPhotoApi, deleteIdolPhotoApi,
  createIdolVipPhotoApi, putIdolVipPhotoApi, deleteIdolVipPhotoApi,
  createIdolVipVideoApi, putIdolVipVideoApi, deleteIdolVipVideoApi,
  createStageApi, putStageApi, deleteStageApi, createStageImageApi, putStageImageApi, deleteStageImageApi,
  createPetApi, putPetApi, deletePetApi,
  putDailyRewardsApi, putSocialChannelsApi, putSocialEventTypesApi, putFramesApi, putBoardCellsApi, putLampDropsApi, putIntimacyApi, fetchSnapshotOf, type Agent, type SnapshotBlock,
  putPettownStarsApi, putPettownSlotsApi, putAdoptPoolsApi, putPetStarsApi,
  putMatchModesApi, putBattleStagesApi, putAbilityCardsApi, putTalentNodesApi,
} from "./lib/api";
import { DEFAULT_INTIMACY, DEFAULT_LAMP, DEFAULT_PET_STAR, type IntimacyParams, type LampParams, type PetStarParams } from "./lib/params";
import type { PetStarStep } from "./lib/petStar";
import type { IntimacyLevel, IntimacySource, IntimacySourceDef } from "./lib/intimacy";
import {
  attachRivalGrids, changedRivalLevels, rivalCellsOf, detectBulkAppend, diffCommon, diffToPatch, toAuctions, toDice, toFight, toLevels,
  initialVersion, toNewLevel, toIntimacy, toLamp, toPack, toPetStar, toPettown, toReward, toServerPack, toServerPeriods, toServerReward, toVersion, vid, vnum,
} from "./lib/server-config";
import { toTiles, toServerTile } from "./lib/server-tiles";
import { newSpin, toSpin, toServerSpin, type SpinCell } from "./lib/board";
import { toPuzzle, toServerPuzzleBody, type Puzzle, type PuzzlePrice } from "./lib/puzzle";
import {
  toIdolPhoto, toIdolVipPhoto, toIdolVipVideo, toServerIdolPhotoBody, toServerIdolVipPhotoBody, toServerIdolVipVideoBody,
  type IdolPhoto, type IdolVipPhoto, type IdolVipVideo,
} from "./lib/idol-album";
import {
  belongsTo, resolveStageId, toServerStageBody, toServerStageImageBody, toStage, toStageImage, type Stage, type StageImage,
} from "./lib/stage-level";
import { EMPTY_DAILY, type DailyRewards } from "./lib/dailyReward";
import { newChannel, newEventType, type SocialChannel, type SocialEventType } from "./lib/social";
import { newFrame, type AvatarFrame } from "./lib/frame";
import type { BoardCell } from "./lib/board-cells";
import { withTemplate, type LampDrop } from "./lib/lamp-drops";
import { EMPTY_TOP_BONUS_IMG, newAbilityCard, newBattleStage, newMatchMode, type AbilityCard, type BattleStage, type MatchMode } from "./lib/match";
import { newTalentNode, talentStart, type TalentNode } from "./lib/talent";
import { normalizePet, toPet, toServerPet, type Pet } from "./lib/pet";
import {
  deriveStars, newSlot, normalizeOverrides, setStarOverride, starsForSave,
  type PettownSlot, type PettownStar,
} from "./lib/pettown";
import { adoptFailPoint, type AdoptPool } from "./lib/adoptPool";
import { PettownParamsPage } from "./pages/PettownParamsPage";
import { IdleIncomePage } from "./pages/IdleIncomePage";
import { PettownStarPage } from "./pages/PettownStarPage";
import { PettownSlotPage } from "./pages/PettownSlotPage";
import { bootSession, currentToken, isExpired, liveSession, SESSION_STORAGE_KEY } from "./lib/session";
import { takeJustLoggedIn } from "./lib/landing";
import { TOAST_EVENT, type ToastDetail } from "./lib/toast";
import { isDirty } from "./lib/dirty";
import { applyReceipts, emptyReceipts, merge3, reconcileLostCreates, refineBase, rekeyByLevelUid, retryDelay, type CreateReceipts, type SaveState } from "./lib/autosave";
import type { Module } from "./lib/achievement";
import { issueTarget } from "./lib/issue-target";
import { AuditDrawer } from "./components/shell/AuditDrawer";
import { DICE_LENS, FIGHT_LENS } from "./lib/apiLensIds";
import { VersionLogDrawer } from "./components/version/VersionLog";
import { flushLiveDrafts, setLiveDraftScope } from "./components/data/useLiveDraft";
import { colorOf, findField, sameCollabView, useCollab, type CollabPeer, type SavedEvent } from "./lib/collab";
import { LEARNING_PAGES } from "./lib/collab-view";
import { CollabContext } from "./components/version/CollabContext";
import { CollabLayer, FollowBanner, MergedNotice } from "./components/version/Collab";
import { SECTION_KEYS } from "./components/version/changeLabels";
import { Gate } from "./pages/Gate";
import { DiscordBindPage } from "./pages/DiscordBindPage";
import { DiscordBindGuard } from "./pages/DiscordBindRequired";
import { HelpVideosPage } from "./pages/HelpVideosPage";
import { startLearningSync } from "./lib/learningSync";
import { Modal, Drawer } from "./components/ui/Overlay";
import { Button } from "./components/ui/Button";
import { Notice } from "./components/ui/Notice";
import { VersionStrip } from "./components/version/VersionStrip";
import { TrashDialog } from "./components/version/TrashDialog";
import { VersionHistory } from "./components/version/VersionHistory";
import { DiffView } from "./components/version/DiffView";
import { PublishCheck } from "./components/version/PublishCheck";
import { ActivateDialog, PublishDialog, RollbackResult, type PublishDiff } from "./components/version/VersionDialogs";
import { refreshModeLabel } from "./components/version/RefreshModePicker";
import type { RefreshMode } from "./lib/refresh-mode";
import { cloneSnap, type Snapshot, type Version, type VersionStore } from "./lib/version";
import { accountKey, bootA0, clearSession, rememberA0 } from "./lib/session";
import { deleteVersionApi, fetchTrace, logAction, pushLearningProgress } from "./lib/api";
import { ApiLensPanel } from "./components/apilens/ApiLens";
import { countPending } from "./lib/pending";

// 页面级代码分割：配置台功能面广，用户一次只看一页，不应在首屏解析全部模块。
const AccountsPage = lazy(() => import("./pages/AccountsPage").then((m) => ({ default: m.AccountsPage })));
const ComingSoon = lazy(() => import("./pages/ComingSoon").then((m) => ({ default: m.ComingSoon })));
const CostPage = lazy(() => import("./pages/CostPage").then((m) => ({ default: m.CostPage })));
const BuildingPage = lazy(() => import("./pages/BuildingPage").then((m) => ({ default: m.BuildingPage })));
const LevelPage = lazy(() => import("./pages/LevelPage").then((m) => ({ default: m.LevelPage })));
const RewardPage = lazy(() => import("./pages/RewardPage").then((m) => ({ default: m.RewardPage })));
const AchievementPage = lazy(() => import("./pages/AchievementPage").then((m) => ({ default: m.AchievementPage })));
const ErheTaskPage = lazy(() => import("./pages/ErheTaskPage").then((m) => ({ default: m.ErheTaskPage })));
const ActEventPage = lazy(() => import("./pages/ActEventPage").then((m) => ({ default: m.ActEventPage })));
const CardPage = lazy(() => import("./pages/CardPage").then((m) => ({ default: m.CardPage })));
const IdolListPage = lazy(() => import("./pages/IdolListPage").then((m) => ({ default: m.IdolListPage })));
const PuzzlePage = lazy(() => import("./pages/PuzzlePage").then((m) => ({ default: m.PuzzlePage })));
const PuzzlePricePage = lazy(() => import("./pages/PuzzlePricePage").then((m) => ({ default: m.PuzzlePricePage })));
const IdolPhotoPage = lazy(() => import("./pages/IdolPhotoPage").then((m) => ({ default: m.IdolPhotoPage })));
const StageImagesPage = lazy(() => import("./pages/StageImagesPage").then((m) => ({ default: m.StageImagesPage })));
const IdolVipPhotoPage = lazy(() => import("./pages/IdolVipPhotoPage").then((m) => ({ default: m.IdolVipPhotoPage })));
const IdolVipVideoPage = lazy(() => import("./pages/IdolVipVideoPage").then((m) => ({ default: m.IdolVipVideoPage })));
const DailyRewardPage = lazy(() => import("./pages/DailyRewardPage").then((m) => ({ default: m.DailyRewardPage })));
const SocialChannelPage = lazy(() => import("./pages/SocialChannelPage").then((m) => ({ default: m.SocialChannelPage })));
const SocialEventTypePage = lazy(() => import("./pages/SocialEventTypePage").then((m) => ({ default: m.SocialEventTypePage })));
const FramePage = lazy(() => import("./pages/FramePage").then((m) => ({ default: m.FramePage })));
const BoardCellPage = lazy(() => import("./pages/BoardCellPage").then((m) => ({ default: m.BoardCellPage })));
const ItemDropPage = lazy(() => import("./pages/ItemDropPage").then((m) => ({ default: m.ItemDropPage })));
const LampDropPage = lazy(() => import("./pages/LampDropPage").then((m) => ({ default: m.LampDropPage })));
const IntimacyPage = lazy(() => import("./pages/IntimacyPage").then((m) => ({ default: m.IntimacyPage })));
const PetPage = lazy(() => import("./pages/PetPage").then((m) => ({ default: m.PetPage })));
const PetStarPage = lazy(() => import("./pages/PetStarPage").then((m) => ({ default: m.PetStarPage })));
const MatchModePage = lazy(() => import("./pages/MatchModePage").then((m) => ({ default: m.MatchModePage })));
const MatchMonitorPage = lazy(() => import("./pages/MatchMonitorPage").then((m) => ({ default: m.MatchMonitorPage })));
const BattleStagePage = lazy(() => import("./pages/BattleStagePage").then((m) => ({ default: m.BattleStagePage })));
const TalentPage = lazy(() => import("./pages/TalentPage").then((m) => ({ default: m.TalentPage })));
const AbilityCardPage = lazy(() => import("./pages/AbilityCardPage").then((m) => ({ default: m.AbilityCardPage })));
/** 选择器的空值。写成常量而不是内联字面量：emptyBlocks 的键由守卫按字面量取，嵌套键会被当成独立快照块 */
// 建筑体系与另外两个选择器同类（都在 cfg_main_common 的一列上，走同一条 PATCH），
// 所以并在一起而不是各留一份 state——两份的话「有没有改动」要判两次，迟早漏一处
// 造价来源默认公式（leo 2026-09-17）：读到空串（迁移前的老版本）也按公式显示，不让页面一片红
/** 各业务块（avail 的键）对应快照里的哪几个字段：读回缺块时按它只替换缺的那几块 */
const BLOCK_KEYS: Record<string, Array<keyof Snapshot>> = {
  common: ["dice", "fight", "pettown", "petStar", "intimacy", "lamp", "selectors"],
  rewards: ["rewards"], cards: ["itemPacks", "idolPacks"], auctions: ["auctions"], tiles: ["tiles"], spins: ["spins"],
  puzzles: ["puzzles", "puzzlePrices"], daily: ["dailyRewards"], socialChannels: ["socialChannels"],
  socialEventTypes: ["socialEventTypes"], frames: ["frames"], boardCells: ["boardCells"], lampDrops: ["lampDrops"],
  intimacy: ["intimacyLevels", "intimacySources"],
  idolPhotos: ["idolPhotos"], idolVipPhotos: ["idolVipPhotos"], idolVipVideos: ["idolVipVideos"], stages: ["stages", "stageImages"], pets: ["pets"], petStars: ["petStars"], adoptPools: ["adoptPools"],
  pettownStars: ["pettownStars"], pettownSlots: ["pettownSlots"], matchModes: ["matchModes"], battleStages: ["battleStages"],
  abilityCards: ["abilityCards"], talentNodes: ["talentNodes"], rivals: ["rivals", "pettownRivalPrices"],
};

/** 基线的特殊值：有一笔写入结果未知、还没对上账。和任何工作副本都不相等，于是一直算「有没存上的」 */
const UNRECONCILED = "__unreconciled__";

/** 停手多久后自动保存 */
const AUTOSAVE_DEBOUNCE_MS = 1500;

const EMPTY_SELECTORS = { gameMode: "", buildingSet: "default", buildingSystem: "", buildingPresentation: "buildings", costSource: "formula", costCurve: "", topBonusImg: EMPTY_TOP_BONUS_IMG };
const InitConfigPage = lazy(() => import("./pages/InitConfigPage").then((m) => ({ default: m.InitConfigPage })));
const AuctionPage = lazy(() => import("./pages/AuctionPage").then((m) => ({ default: m.AuctionPage })));
const TileOverview = lazy(() => import("./pages/TilePage").then((m) => ({ default: m.TileOverview })));
const TilePage = lazy(() => import("./pages/TilePage").then((m) => ({ default: m.TilePage })));
const ReportPage = lazy(() => import("./pages/ReportPage").then((m) => ({ default: m.ReportPage })));
const PlayerPage = lazy(() => import("./pages/PlayerPage").then((m) => ({ default: m.PlayerPage })));
const RechargePage = lazy(() => import("./pages/RechargePage").then((m) => ({ default: m.RechargePage })));
const ClientStatePage = lazy(() => import("./pages/ClientStatePage").then((m) => ({ default: m.ClientStatePage })));
const SimPage = lazy(() => import("./pages/SimPage").then((m) => ({ default: m.SimPage })));
const SpinPage = lazy(() => import("./pages/BoardPage").then((m) => ({ default: m.SpinPage })));
const DebugTracePage = lazy(() => import("./pages/DebugTracePage").then((m) => ({ default: m.DebugTracePage })));
const EconSwitchPage = lazy(() => import("./pages/EconSwitchPage").then((m) => ({ default: m.EconSwitchPage })));
const AgentsPage = lazy(() => import("./pages/AgentsPage").then((m) => ({ default: m.AgentsPage })));
const ShopItemsPage = lazy(() => import("./pages/ShopItemsPage").then((m) => ({ default: m.ShopItemsPage })));
const AssetInfoPage = lazy(() => import("./pages/AssetInfoPage").then((m) => ({ default: m.AssetInfoPage })));
const ShopSectionsPage = lazy(() => import("./pages/ShopSectionsPage").then((m) => ({ default: m.ShopSectionsPage })));
const IapPage = lazy(() => import("./pages/IapPage").then((m) => ({ default: m.IapPage })));
const MailPage = lazy(() => import("./pages/MailPage").then((m) => ({ default: m.MailPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const PushEventsPage = lazy(() => import("./pages/PushEventsPage").then((m) => ({ default: m.PushEventsPage })));
const HotUpdatePage = lazy(() => import("./pages/HotUpdatePage").then((m) => ({ default: m.HotUpdatePage })));
const PiggyPage = lazy(() => import("./pages/PiggyPage").then((m) => ({ default: m.PiggyPage })));
const PayChainPage = lazy(() => import("./pages/PayChainPage").then((m) => ({ default: m.PayChainPage })));
const ShareLinksPage = lazy(() => import("./pages/ShareLinksPage").then((m) => ({ default: m.ShareLinksPage })));
const DevTokenPage = lazy(() => import("./pages/DevTokenPage").then((m) => ({ default: m.DevTokenPage })));
const DevPanelPage = lazy(() => import("./pages/DevPanelPage").then((m) => ({ default: m.DevPanelPage })));
const GmPage = lazy(() => import("./pages/GmPage").then((m) => ({ default: m.GmPage })));

/**
 * 语言状态必须住在 I18nProvider **外面**。
 * 早先写成 <I18nProvider lang={loadLang()}>，它只在这里首次渲染时求值一次；
 * 语言在子组件里改，本组件不重渲染，provider 的 lang 就永远不变——
 * 表现是按钮的字会切、页面内容纹丝不动。
 */
export default function App() {
  const [uiLang, setUiLang] = useState<UiLang>(loadLang);
  /*
   * 用着用着票失效了（过期、被服务端拒、换票失败）：清掉会话、整页换成登录页，登录完回到原来这一页。
   * 不让各页自己显示「配置读取失败：unauthorized」——那页上的重试换不来新票，人只能干等。
   * 放在这一层而不是 Inner 里：Inner 的门禁 return 在一长串 hook 中间，会话中途变了
   * 再走那条 return 就是 hook 数对不上，整页白屏。
   */
  const [authLost, setAuthLost] = useState<GateLost | null>(null);
  useEffect(() => {
    onAuthLost((reason) => { clearSession(); setAuthLost(reason); });
    return () => onAuthLost(null);
  }, []);
  // 有人在用就在过期前续票（lib/session-keeper.ts）；票存 localStorage，各标签页共用，任一标签页续了大家都用新的
  useEffect(() => startSessionKeeper(), []);
  /*
   * 票是所有标签页共用的（lib/session.ts）。别的标签页退出了，这一页还开着配置界面、接着发请求只会一串 401，
   * 直接换成登录页；别的标签页登录了（或换了人），这一页整页重进，按新的票重新初始化。
   * storage 事件只在**别的**标签页改了存储时触发，自己续票、自己退出不会走到这里。
   */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SESSION_STORAGE_KEY && e.key !== null) return;
      const now = currentToken();
      // 续票是同一个人换了张新票，什么都不用做。别处登录了（原来没票，这一页停在登录页）就整页重进；
      // 别处换了人：这一页不自己刷新——可能有没保存的改动——换成登录页并说明，由人决定
      if (!now) setAuthLost((cur) => cur ?? { kind: "elsewhere" });
      else if (!e.oldValue) location.reload();
      else if (accountKey(now) !== accountKey(e.oldValue)) setAuthLost({ kind: "switched" });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  if (authLost) {
    return (
      <I18nProvider lang={uiLang}>
        <Gate lost={authLost} uiLang={uiLang} onUiLang={(l) => { setUiLang(l); saveLang(l); }} />
      </I18nProvider>
    );
  }
  return (
    <I18nProvider lang={uiLang}>
      <DiscordBindGuard uiLang={uiLang} onUiLang={(l) => { setUiLang(l); saveLang(l); }}>
        <Inner uiLang={uiLang} onUiLang={(l) => { setUiLang(l); saveLang(l); }} />
      </DiscordBindGuard>
    </I18nProvider>
  );
}

function Inner({ uiLang, onUiLang }: { uiLang: UiLang; onUiLang: (l: UiLang) => void }) {
  const t = useT();
  // 先接收中台票并恢复登录前的 hash，路由才会从目标课程开始初始化。
  const [session] = useState(bootSession);
  const [page, setPage] = useHashRoute();
  /*
   * 保存失败时「是哪一页出的问题」。
   *
   * 保存是一串顺序请求（关卡 → 全局参数 → 选择器 → 各业务块），任何一步被服务端拒
   * 就整体失败。原来只弹一句服务端原话，运营看到「buildingSet 须为 1~32 位小写字母」
   * 完全不知道该去哪一页改什么——那个字段甚至不在他刚编辑的页面上。
   * 现在记下失败发生在哪一块，跳过去并在页顶标红。
   */
  const [saveError, setSaveError] = useState<{ page: string; msg: string } | null>(null);
  // A0 由运营在顶栏选；super-admin 的操作员 token 不绑定代理。
  // 进来先默认选第一个可用代理——不选中的话整页没有租户上下文，
  // 时区、库都无从确定，界面看着正常却什么也读不了。
  const [agent, setAgent] = useState<string | null>(() => bootA0());
  /** 首次解析 A0 时已经拉过的代理结果，交给顶栏复用，避免为了显示名字再打一次接口。 */
  const [agentOptions, setAgentOptions] = useState<Agent[]>([]);
  const [health, setHealth] = useState<Health>({ state: "checking" });
  /*
   * 一进来就没有有效凭据时——**不自动跳转。** 横幅已经写清了原因并给了「去 super-admin」按钮，
   * 跳不跳交给人。
   *
   * 之前是 2.5 秒后 location.replace：结果任何不带 token 的直接访问都会被弹走
   * ——本地调试打不开、想看一眼配置也不行，而且横幅一闪而过根本读不完。
   * 自动劫持导航省下的那一次点击，远不值这些代价。
   */

  /*
   * 记住的 A0 必须仍在这个账号能看到的代理列表里。换了账号、或者权限被收回之后，
   * sessionStorage 里那个旧值会**静默生效**——界面照常显示、请求照常发，只是
   * 全都打在一个不该看的租户上，而现场没有任何提示（codex 复审指出）。
   * 不在列表里就清掉，让下面那个 effect 走正常的兜底。
   */
  useEffect(() => {
    if (!session.token) return;
    let alive = true;
    const wanted = agent;
    void (async () => {
      try {
        // 记住的 A0 用自身作关键词查：代理总数超过上游单页上限时，拉默认前 50
        // 再判「不存在」会误清一个真实可见代理。
        let list = await fetchAgents(wanted ?? "");
        if (!alive) return;
        if (wanted) {
          const hit = list.find((x) => x.a0 === wanted);
          if (hit) { setAgentOptions(list); return; }
          console.warn("记住的 A0 不在当前可见列表里，已清除", wanted);
          rememberA0(null);
          list = await fetchAgents();
          if (!alive) return;
        }
        setAgentOptions(list);
        // 既没有 ?a0= 也没有记住的选择时，才回落到列表第一个。
        const first = list[0];
        if (first) { setAgent(first.a0); rememberA0(first.a0); }
      } catch { /* 拉不到就保留现状；顶栏打开时还能重试搜索 */ }
    })();
    return () => { alive = false; };
    // 只做一次会话启动解析；用户在顶栏切 A0 不应再次拉取默认列表
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.token]);
  // 假数据先跑通交互；接后端时换成 /admin/api 拉取
  // 没凭据时不构造假数据：useState 的初始化函数只在首次渲染跑，
  // 但那一次也发生在门禁之前——空数组起步，进了门再填。
  const [levels, setLevels] = useState<Level[]>([]);
  /*
   * 超管标记由服务端给（GET /debug-trace 的 is_super_admin），前端不猜也不缓存
   * 到本地：本地存的身份等于给了「改个 localStorage 就多一个菜单」的路子。
   * 拿不到就当不是超管——开发向菜单少显示一次没有代价，多显示一次有。
   */
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isRoot, setIsRoot] = useState(false);
  const [dice, setDice] = useState<DiceParams>(DEFAULT_DICE);
  const [fight, setFight] = useState<FightParams>(DEFAULT_FIGHT);
  const [rewards, setRewards] = useState<Reward[]>([]);
  /** 任务 / 成就配置页的下拉字典（指标等），来自服务端代码常量 */
  /** 任务成就页的服务端字典（指标 / 类型 / 窗口 / 探测器 / 条件替换…）。拉不到就只显示 key */
  const [achDict, setAchDict] = useState<AchievementDict | null>(null);
  const [itemPacks, setItemPacks] = useState<CardPack[]>([]);
  const [idolPacks, setIdolPacks] = useState<CardPack[]>([]);
  const [auctions, setAuctions] = useState<AuctionMap>({});
  /*
   * 副玩法格来自后端（/config/tiles），随版本快照走。
   *
   * 以前这里是 buildTiles()——本地假数据，改完保存不报错、刷新就没了，
   * 而页面看不出任何异常。spin 同理：它的格子也在 cfg_tile 里。
   */
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [spins, setSpins] = useState<SpinCell[]>([]);
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [puzzlePrices, setPuzzlePrices] = useState<PuzzlePrice[]>([]);
  const [dailyRewards, setDailyRewards] = useState<DailyRewards>(EMPTY_DAILY);
  const [socialChannels, setSocialChannels] = useState<SocialChannel[]>([]);
  // 萌宠小镇：体系选择器 + 全局参数 + 两张表。体系为空串 = 草稿还没选（发布闸会拦）
  const [pettown, setPettown] = useState<PettownParams>({ ...DEFAULT_PETTOWN });
  /** 主建筑星级表的**稀疏覆盖**（目标骰次 / 手填价 / 格数 / 外观图 / B 值），按星号排序、空行不留；页面按累计星数上限铺满 */
  const [pettownStars, setPettownStars] = useState<PettownStar[]>([]);
  /** 竞品列定义（随版本存）：数值在 levels[].rivals / pettownRivalPrices */
  const [rivals, setRivals] = useState<RivalDef[]>([]);
  /** 萌宠小镇竞品逐星价：列 id → { 星号 → 价 }。列定义在 rivals */
  const [pettownRivalPrices, setPettownRivalPrices] = useState<Record<string, Record<number, Money>>>({});
  const [pettownSlots, setPettownSlots] = useState<PettownSlot[]>([]);
  const [adoptPools, setAdoptPools] = useState<AdoptPool[]>([]);
  const [petStars, setPetStars] = useState<PetStarStep[]>([]);
  const [petStar, setPetStar] = useState<PetStarParams>({ ...DEFAULT_PET_STAR });
  /** 领养池成员表的行源：本版本角色名册。跟池一起拉，不进快照（它是 cfg_pet 的投影，不是本页的编辑对象） */
  const [socialEventTypes, setSocialEventTypes] = useState<SocialEventType[]>([]);
  const [frames, setFrames] = useState<AvatarFrame[]>([]);
  const [boardCells, setBoardCells] = useState<BoardCell[]>([]);
  const [lampDrops, setLampDrops] = useState<LampDrop[]>([]);
  // idol 亲密度：曲线 + 来源进快照；全局那一列（单个 idol 每日上限）跟 common 走
  const [intimacyLevels, setIntimacyLevels] = useState<IntimacyLevel[]>([]);
  const [intimacySources, setIntimacySources] = useState<IntimacySource[]>([]);
  const [intimacy, setIntimacy] = useState<IntimacyParams>({ ...DEFAULT_INTIMACY });
  const [lamp, setLamp] = useState<LampParams>({ pityThreshold: null });
  /** 来源注册表（代码定义，服务端随块回）。不进快照：它不是本页的编辑对象，版本之间也不会不同 */
  const [intimacySourceDefs, setIntimacySourceDefs] = useState<IntimacySourceDef[]>([]);
  // idol 关卡相册 / VIP 照片 / VIP 视频：三张逐行表（同拼图，ID 服务端分配）
  const [idolPhotos, setIdolPhotos] = useState<IdolPhoto[]>([]);
  const [idolVipPhotos, setIdolVipPhotos] = useState<IdolVipPhoto[]>([]);
  const [idolVipVideos, setIdolVipVideos] = useState<IdolVipVideo[]>([]);
  // 关卡多图（改版）：关卡 + 关卡图片两张逐行表（ID 服务端分配），同一个可用开关 avail.stages
  const [stages, setStages] = useState<Stage[]>([]);
  const [stageImages, setStageImages] = useState<StageImage[]>([]);
  // 多人对战三张表：匹配参数（一玩法一行）、战阶、道具卡。solo 代理拉不到（服务端按 game_mode 拒），块就不可用
  const [matchModes, setMatchModes] = useState<MatchMode[]>([]);
  const [battleStages, setBattleStages] = useState<BattleStage[]>([]);
  const [abilityCards, setAbilityCards] = useState<AbilityCard[]>([]);
  // 营地天赋树节点（萌宠小镇）。空表 = 不开天赋树
  const [talentNodes, setTalentNodes] = useState<TalentNode[]>([]);
  // 声明必须在 takeSnap 之前——晚了渲染时踩 TDZ，整页白屏且类型检查/单测全绿
  // （wiring.test.ts 钉住了这条）
  const [pets, setPets] = useState<Pet[]>([]);
  /** 初始化配置的两个选择器（cfg_main_common 两列）。gameMode 空串 = 存量未选 */
  const [selectors, setSelectors] = useState(EMPTY_SELECTORS);
  const [curveMetric, setCurveMetric] = useState<CurveMetric>("netDice");
  const [curveExpanded, setCurveExpanded] = useState(true);
  const viewCurve = (metric: CurveMetric) => { setCurveMetric(metric); setCurveExpanded(true); requestAnimationFrame(() => document.getElementById("economy-curves")?.scrollIntoView({ block: "nearest", behavior: "smooth" })); };
  const [versions, setVersions] = useState<Version[]>([]);
  const [store, setStore] = useState<VersionStore>({});
  const [curVid, setCurVid] = useState("");
  /** 正在看的版本的金额口径（docs/economy-g-model.md §2.4）：model 2 时关卡页 / 萌宠星级页多一列「单掷金币 g」 */
  const curEcon = useMemo(() => {
    const v = versions.find((x) => x.id === curVid);
    return { model: v?.economyModel === 2 ? 2 as const : 1 as const, scale: v?.moneyScale ?? null, legacyEMilli: v?.legacyEMilli ?? null };
  }, [versions, curVid]);
  /** model 2 版本的刻度（配置金额 × num/den = 几骰），给盘面对账用 */
  const econScale = useMemo(() => (curEcon.scale ? [BigInt(curEcon.scale.num), BigInt(curEcon.scale.den)] as const : null), [curEcon.scale]);
  /** 萌宠小镇星级表的完整视图：公式 / 手填 / 各竞品价并排，price 已按客户端在用的那套选好 */
  const pettownStarsView = useMemo(
    () => deriveStars(pettown.maxStar, pettownStars, dice.E, dice.rho, { costSource: selectors.costSource, rivalPrices: pettownRivalPrices, incomeGrowthBps: Math.round((dice.incomeK ?? 0) * 10000), incomeAnchorB: dice.incomeAnchor ?? 0, unifiedB:dice.unifiedB ?? 0, targetRollsBase:dice.targetBase ?? 0, targetRollsGrowthBps:Math.round((dice.targetGrowth ?? 0)*10000), costCurve:selectors.costCurve, economyModel: curEcon.model }),
    [pettown.maxStar, pettownStars, dice.E, dice.rho, dice.incomeK, dice.incomeAnchor, dice.unifiedB, dice.targetBase, dice.targetGrowth, selectors.costCurve, selectors.costSource, pettownRivalPrices],
  );
  /**
   * 服务端原样的 tile 行，按 tile_type_id 存。
   *
   * 写回时要把前端没映射的字段（params、bands 里的细项）原样带上——
   * 只发 UI 认识的那几个字段等于把其余的清空。
   */
  const serverTilesRef = useRef<Record<string, import("./lib/server-tiles").ServerTile>>({});
  /**
   * 同上，按「代理|版本」各存一份。保存玩法格时用**正在保存的那一版**自己的底座：只有一份全局的话，
   * 拉别的版本（切版本、迟到的快照、来回切已缓存的版本）会把它换掉，下一次保存就拿别的版本的隐藏字段写进当前版本
   */
  const serverTilesByVerRef = useRef<Record<string, { revision: number; tiles: Record<string, import("./lib/server-tiles").ServerTile> }>>({});
  // ↑ tiles 必须和其余快照块声明在一起：takeSnap() 会读它，
  // 而 takeSnap 在下面几十行就被 dirty 的 useMemo 调用——声明晚了会在渲染时
  // 踩 TDZ（Cannot access before initialization），整个 App 挂掉，白屏且无处报错。
  /*
   * 版本生命周期（对应后端 cfg_version）：
   * versions 是元数据链，store 存各版本的不可变**快照**（关卡 + 全局参数 + 奖励 +
   * 卡牌 + 竞拍——后端一版配置横跨全部内容表，前端与之对齐），当前各 state 是
   * 「正在查看版本」的工作副本。只有 draft 可编辑；保存产生新版本，发布设为 active。
   */
  /**
   * 「修改记录」抽屉：盯哪一版、范围、只看谁；null = 没开。
   * 保存状态与「更多」开当前版，下拉行尾开那一版，任何地方点人名开「全部版本 · 只看他」
   */
  const [logView, setLogView] = useState<{ vid: string; scope: "one" | "all"; who?: string; blocks?: readonly string[] } | null>(null);
  /** 从修改记录点「看完整日志」：这一版上的全部操作（建版本、发布、接口留痕）；从「全部版本」过去时不限版本、只限人 */
  const [logFull, setLogFull] = useState<{ vid: string | null; who: string | null } | null>(null);
  const openPersonLog = (who: string) => setLogView({ vid: curVid, scope: "all", who });
  /**
   * 初始化那两个单选还能不能改。代理级事实，跟着代理走、不跟版本走。
   * null = 还没读到或读失败 → 按不锁处理：宁可让人改一次被发布闸拦，
   * 也不能因为一次读失败把界面锁死。
   */
  const [initLocks, setInitLocks] = useState<InitLocks | null>(null);
  const [baseline, setBaseline] = useState("");
  /** 发布被拒清单点「去修改」时要直接打开的那条通关奖励 */
  const [rewardFocus, setRewardFocus] = useState<{ id: string } | null>(null);
  // 离开奖励页就作废，别让下次手动进来时又自己弹出那一关
  useEffect(() => { if (page !== "reward") setRewardFocus(null); }, [page]);
  const [verUi, setVerUi] = useState<
    | { kind: "publish" } | { kind: "history" } | { kind: "check" } | { kind: "prune" } | { kind: "trash" }
    | { kind: "activate"; v: Version }
    | { kind: "diffPair"; left: Snapshot; right: Snapshot; la: string; lb: string; leftId?: string; rightId?: string; changes?: import("./lib/autosave").Change[]; samePublishedContent?: boolean; checksumConflict?: boolean }
    | { kind: "rollback"; v: Version; left: Snapshot; right: Snapshot; changes?: import("./lib/autosave").Change[] }
    | { kind: "rollbackResult"; id: string; src: string }
    | { kind: "delete"; id: string }
    | { kind: "confirmSwitch"; vid: string; back?: "history" }
    | { kind: "staleBase"; v: Version; isRollback: boolean }
    | { kind: "pubErrors"; rows: ValidationErrorRow[]; from?: "publish" | "check" }
    /** 发布前合并线上改动有冲突：草稿一格没动，给「保留草稿的值并发布」 */
    | { kind: "mergeConflicts"; message: string; mode: RefreshMode; onto: number }
    /*
     * 激活被拒。版本已经 published、内容改不了，所以出路有两条，**取决于是哪条规则**：
     * - 版本外的事实变了（idol 名册 / 授权批次）→ 处理完重试激活；
     * - 版本自身的问题（如 PUZZLE_ID_IDENTITY 与已发布版本冲突）→ 重试不会好，
     *   只能基于它新建草稿改掉。抽屉两个入口都给，让人按清单自己判断。
     */
    | { kind: "actErrors"; rows: ValidationErrorRow[]; v: string }
    | null
  >(null);
  const [toast, setToastRaw] = useState<{ msg: string; bad?: boolean } | null>(null);
  /** 第二参 true = 失败，红框。失败也用绿框的话，人扫一眼以为保存成功了 */
  const setToast = (msg: string, bad?: boolean) => setToastRaw({ msg, bad });
  // 刚登录进来说一句，带上名字：确认登录成功、也确认进来的是哪个身份（换账号时最容易弄混）
  useEffect(() => {
    if (session.token && !isExpired(liveSession(session)) && takeJustLoggedIn()) setToast(t("app.welcome", { name: session.operator ?? "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * 别人改了版本状态时的提醒。
   *
   * 版本条上的「线上生效: vN」原本只在页面加载时抓一次，之后再不刷新——
   * 别人发布并激活了新版本，正在编辑的人**完全看不出来**（leo 2026-08-27）。
   * 后果不只是显示旧：一是可能对着已经不是最新的线上版本做对比/回滚判断；
   * 二是「基于旧版本新建草稿」那道警告比的就是这个值，它一旦过期，
   * 那道警告也跟着失灵。
   *
   * 只提醒不自动改页面：正在编辑的人手上可能有未保存改动，静默重载会吞掉它。
   */
  const [drift, setDrift] = useState<{ was: string | null; now: string | null } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const h = setTimeout(() => setToastRaw(null), 2600);
    return () => clearTimeout(h);
  }, [toast]);
  // 深处组件（如课程播放弹窗的「复制链接」）经 lib/toast.ts 的窗口事件借用这条提示
  useEffect(() => {
    const onToast = (e: Event) => { const d = (e as CustomEvent<ToastDetail>).detail; if (d?.msg) setToastRaw({ msg: d.msg, bad: d.bad }); };
    window.addEventListener(TOAST_EVENT, onToast);
    return () => window.removeEventListener(TOAST_EVENT, onToast);
  }, []);
  /** true = 数据来自后端 cfg_version；false = 后端不可达/无数据，跑本地假数据 */
  /**
   * true = 后端正常，但这个代理一版配置都没有。
   *
   * 这是合法状态，不是错误：每份配置都属于某个真实代理，没有全局那份可继承，
   * 所以新代理天然是空的。界面要把人引去建第一版，而不是拿演示数据糊上——
   * 糊上之后策划分不清自己在看真配置还是假数据。
   */
  const [unconfigured, setUnconfigured] = useState(false);
  /**
   * 这一版的内容是否已经拉完。
   *
   * 必须与「关卡数为 0」分开：以前配置一定非空，关卡为 0 只可能是还没拉完，
   * 于是直接拿它当加载中。现在第一版天然是空的，混用会让新建完的版本
   * 永久停在「正在载入配置…」——而唯一能加第一关的入口正好在那块下面。
   */
  const [snapLoaded, setSnapLoaded] = useState(false);
  /** 眼前的快照属于哪个代理。切代理的头一帧 agent 已经是新的、snapLoaded 和 curVid 还是旧代理的——跨代理跳转靠它等新代理真加载完 */
  const [snapAgent, setSnapAgent] = useState<string | null>(null);
  /**
   * 读配置失败的原因。非空 = 整页只显示错误，不渲染任何配置。
   *
   * 以前这里退回一份演示数据。那比报错更糟：页面看着一切正常，策划对着
   * 一份不存在的配置改半天，保存时才发现什么都没有——而且假数据长得就像真的，
   * 点页面根本看不出来。
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  /*
   * **块级**失败：整版快照拿到了，但其中某几块报错。
   *
   * 与 loadError（整版读不到，整页不渲染）不是一回事，必须分开：块失败时页面照常画出来，
   * 只是那一块对应的页面 `avail.xxx` 留在 false、于是整页变**只读**——
   * 而在这条提示之前，那是**完全静默**的：按钮灰着、没有任何错误，
   * 运营只会以为是权限问题或者自己哪里没点对。
   *
   * 真实案例（2026-09-16）：本地库少跑了几个迁移，`cfg_main_common` 缺列 →
   * common 块整个报错 → 初始化配置页的单选点不动，界面上一个字的线索都没有。
   */
  const [blockErrors, setBlockErrors] = useState<Array<{ block: string; error: string }>>([]);
  /** 手动重试计数：演示横幅的「重试」按钮触发重新拉取 */
  const [reloadKey, setReloadKey] = useState(0);
  /** 各版本的写 CAS 修订号，随快照一起更新 */
  const [revs, setRevs] = useState<Record<string, number>>({});
  /**
   * 服务端写操作进行中。双击「保存」会建出两个草稿、双击「发布」会连发两次——
   * 入口处互斥 + 按钮禁用双保险；用 state 是为了让按钮态跟着变。
   */
  const [busy, setBusy] = useState(false);
  // setState 是异步的，同一轮事件里的第二次点击仍读到 busy=false——同步互斥必须走 ref
  const busyRef = useRef(false);
  /** 竞品逐格网格（ref_dataset，只读）：关卡 id → 竞品列 id → 网格 */
  /** 本地新增、还没同步到服务端的关卡 id（保存时按位插入后清空） */
  const [pendingAdds, setPendingAdds] = useState<number[]>([]);
  /** 读回回来那一刻的待插清单（闭包里的是发起读回时的） */
  const pendingAddsRef = useRef(pendingAdds);
  pendingAddsRef.current = pendingAdds;
  /**
   * 各业务块接口的可用性。关卡链路先行，其余块的读写接口
   * 后端逐个交付——没就绪的块在界面上只读并提示，绝不给「可编辑但不落库」。
   */
  const [avail, setAvail] = useState({ common: false, rewards: false, cards: false, auctions: false, tiles: false, spins: false, puzzles: false, daily: false, socialChannels: false, socialEventTypes: false, frames: false, pets: false, adoptPools: false, petStars: false, pettownStars: false, pettownSlots: false, matchModes: false, battleStages: false, abilityCards: false, talentNodes: false, boardCells: false, lampDrops: false, intimacy: false, idolPhotos: false, idolVipPhotos: false, idolVipVideos: false, stages: false, rivals: false });
  /** 当前各业务块 state 拼成一份快照（与 store 里的版本快照同构） */
  const takeSnap = (): Snapshot => ({ levels, dice, fight, rewards, itemPacks, idolPacks, auctions, tiles, spins, puzzles, puzzlePrices, dailyRewards, socialChannels, socialEventTypes, frames, pets, adoptPools, petStars, petStar, selectors, pettown, pettownStars, pettownSlots, matchModes, battleStages, abilityCards, talentNodes, boardCells, lampDrops, lamp, intimacyLevels, intimacySources, intimacy, idolPhotos, idolVipPhotos, idolVipVideos, stages, stageImages, rivals, pettownRivalPrices });
  /** 最新一帧的工作副本。实时保存结束时要拿「此刻」的本地状态做合并，闭包里的是发起那一刻的 */
  const liveSnapRef = useRef<Snapshot | null>(null);
  liveSnapRef.current = takeSnap();
  const applySnap = (s: Snapshot) => {
    setLevels(s.levels);
    setDice(s.dice);
    setFight(s.fight);
    setRewards(s.rewards);
    setItemPacks(s.itemPacks);
    setIdolPacks(s.idolPacks);
    setAuctions(s.auctions);
    setTiles(s.tiles);
    setSpins(s.spins);
    setPuzzles(s.puzzles);
    setPuzzlePrices(s.puzzlePrices);
    setDailyRewards(s.dailyRewards);
    setSocialChannels(s.socialChannels);
    setPettown(s.pettown);
    setPettownStars(s.pettownStars);
    setPettownRivalPrices(s.pettownRivalPrices ?? {});
    setRivals(s.rivals ?? []);
    setPettownSlots(s.pettownSlots);
    setMatchModes(s.matchModes);
    setBattleStages(s.battleStages);
    setAbilityCards(s.abilityCards);
    setTalentNodes(s.talentNodes ?? []);
    setSocialEventTypes(s.socialEventTypes);
    setFrames(s.frames);
    setBoardCells(s.boardCells ?? []);
    setLampDrops(s.lampDrops ?? []);
    // 不用 DEFAULT_LAMP 兜底：编出来的 10 显示出来跟真配置一模一样（同 intimacy）
    setLamp(s.lamp ?? { pityThreshold: null });
    setIntimacyLevels(s.intimacyLevels ?? []);
    setIntimacySources(s.intimacySources ?? []);
    // 不用 DEFAULT_INTIMACY 兜底：那是编出来的 100，显示出来跟真配置一模一样（见 emptyBlocks 上面的注释）
    setIntimacy(s.intimacy ?? { idolDailyCap: null });
    setIdolPhotos(s.idolPhotos ?? []);
    setIdolVipPhotos(s.idolVipPhotos ?? []);
    setIdolVipVideos(s.idolVipVideos ?? []);
    setStages(s.stages ?? []);
    setStageImages(s.stageImages ?? []);
    setPets(s.pets);
    setAdoptPools(s.adoptPools);
    setPetStars(s.petStars);
    setPetStar(s.petStar);
    setSelectors({ ...EMPTY_SELECTORS, ...s.selectors });
  };
  /*
   * 某个块的接口没答上来时用什么占位。
   *
   * 全部为空/null，**不能用 DEFAULT_DICE 这类默认值**：那些是编出来的数
   * （E=3.1、ρ=0.9…），显示出来跟真配置一模一样，接口挂了也看不出来。
   * 空输入框至少是诚实的——它说的是「这里没有值」。
   * 键从 DEFAULT_* 推导，新增参数时不会漏。
   */
  const emptyParams = <T,>(shape: T): T =>
    Object.fromEntries(Object.keys(shape as object).map((k) => [k, null])) as T;
  const emptyBlocks = () => ({
    dice: emptyParams(DEFAULT_DICE), fight: emptyParams(DEFAULT_FIGHT),
    rewards: [] as Reward[], itemPacks: [] as CardPack[], idolPacks: [] as CardPack[], auctions: {} as AuctionMap,
    tiles: [] as Tile[], spins: [] as SpinCell[],
    puzzles: [] as Puzzle[], puzzlePrices: [] as PuzzlePrice[],
    dailyRewards: EMPTY_DAILY as DailyRewards,
    socialChannels: [] as SocialChannel[], socialEventTypes: [] as SocialEventType[],
    frames: [] as AvatarFrame[],
    // ⚠️ 键顺序必须与 takeSnap 一致：dirty 用 JSON.stringify 比快照，顺序不同 = 永远「有未保存改动」
    pets: [] as Pet[],
    adoptPools: [] as AdoptPool[],
    petStars: [] as PetStarStep[],
    petStar: { ...DEFAULT_PET_STAR },
    selectors: EMPTY_SELECTORS,
    pettown: emptyParams(DEFAULT_PETTOWN),
    pettownStars: [] as PettownStar[], pettownSlots: [] as PettownSlot[],
    matchModes: [] as MatchMode[], battleStages: [] as BattleStage[], abilityCards: [] as AbilityCard[],
    talentNodes: [] as TalentNode[],
    boardCells: [] as BoardCell[],
    lampDrops: [] as LampDrop[],
    lamp: emptyParams(DEFAULT_LAMP),
    intimacyLevels: [] as IntimacyLevel[],
    intimacySources: [] as IntimacySource[],
    intimacy: emptyParams(DEFAULT_INTIMACY),
    idolPhotos: [] as IdolPhoto[],
    idolVipPhotos: [] as IdolVipPhoto[],
    idolVipVideos: [] as IdolVipVideo[],
    stages: [] as Stage[],
    stageImages: [] as StageImage[],
    rivals: [] as RivalDef[],
    pettownRivalPrices: {} as Record<string, Record<number, Money>>,
  });
  const mkSnap = (ls: Level[]): Snapshot => ({ levels: ls, ...emptyBlocks() });
  /**
   * 拉一个版本的完整快照。关卡是必得项（拉不到即失败）；
   * 四个业务块用 allSettled 各自尽力——哪个接口挂了哪个块标不可用并只读，
   * 不拖垮整版加载。
   */
  const rawSnapshots = useRef<Record<string, import("./lib/api").SnapshotPayload>>({});
  const snapshotFailures = useRef<Record<string, string[]>>({});
  const loadSnapshot = async (version: number, readOnly = false) => {
    // 发请求时的代理（拉回来之前可能已经切走了；底座要记在发请求的那个代理名下）
    const tilesKey = `${agentRef.current ?? ""}|${version}`;
    let rawTiles: Record<string, import("./lib/server-tiles").ServerTile> | null = null;
    /** 玩法格块自己的修订号（服务端读前读后对过，和这份数据对得上）；不用关卡块的——各块分开读，可能跨了别人的提交 */
    let rawTilesRev = 0;
    // 关卡 + 9 个块一个请求拿齐（服务端贴着库并发拉，见 /config/versions/:v/snapshot）。
    // 各块 ok=false 与原来 allSettled 的 rejected 同义：标不可用、只读，不拖垮整版
    const snapshot = await fetchSnapshotOf(version);
    if (!snapshot) return null;
    rawSnapshots.current[tilesKey] = snapshot;
    const pl = snapshot.levels;
    const settle = <T,>(b: SnapshotBlock<T>): PromiseSettledResult<T> =>
      b.ok ? { status: "fulfilled", value: b.value } : { status: "rejected", reason: new Error(b.error) };
    const sb = snapshot.blocks;
    snapshotFailures.current[tilesKey] = Object.entries(sb).filter(([, b]) => b && !b.ok).map(([k]) => k);
    const [cm, rw, pk, au, tl, sp, pz, pp, dr, sc, se, pt, ap, ps, pts, pto, mm, bs, ac, tn, fr, bc, ld, rv, im] = [
      settle(sb.common), settle(sb.rewards), settle(sb.packs), settle(sb.auctions),
      settle(sb.tiles), settle(sb.spin), settle(sb.puzzles), settle(sb.puzzlePrices), settle(sb.dailyRewards),
      settle(sb.socialChannels), settle(sb.socialEventTypes), settle(sb.pets), settle(sb.adoptPools),
      // 升星档块是新加的：老服务端没带它时当「块不可用」，页面显示为只读空表
      settle(sb.petStars ?? { ok: false, error: "petStars block missing" }),
      settle(sb.pettownStars), settle(sb.pettownSlots),
      settle(sb.matchModes), settle(sb.battleStages), settle(sb.abilityCards),
      // 天赋节点块是新加的：服务端还没带上它时当「块不可用」
      settle(sb.talentNodes ?? { ok: false, error: "talentNodes block missing" }),
      // 头像框块是新加的：老服务端没带它时当「块不可用」
      settle(sb.frames ?? { ok: false, error: "frames block missing" }),
      // 棋盘格位表块是新加的：老服务端没带它时当「块不可用」
      settle(sb.boardCells ?? { ok: false, error: "boardCells block missing" }),
      // 神灯掉落表块是新加的：老服务端没带它时当「块不可用」，页面只读空表
      settle(sb.lampDrops ?? { ok: false, error: "lampDrops block missing" }),
      // 竞品列块（随版本存）：老服务端没带它时当「块不可用」，竞品列只读、不显示
      settle(sb.rivals ?? { ok: false, error: "rivals block missing" }),
      // idol 亲密度块是新加的：老服务端没带它时当「块不可用」
      settle(sb.intimacy ?? { ok: false, error: "intimacy block missing" }),
    ];
    // idol 相册三块随快照下发（#722）；老服务端没带时当「块不可用」，同 intimacy
    const missing = (name: string) => ({ ok: false as const, error: `${name} block missing` });
    const [iph, ivp, ivv] = [
      settle(sb.idolPhotos ?? missing("idolPhotos")), settle(sb.idolVipPhotos ?? missing("idolVipPhotos")), settle(sb.idolVipVideos ?? missing("idolVipVideos")),
    ];
    // 关卡多图两块：老服务端没带时当「块不可用」（页面只读）。两块互相引用（图挂在关卡下），一块挂了整组只读
    const [stg, sti] = [settle(sb.stages ?? missing("stages")), settle(sb.stageImages ?? missing("stageImages"))];
    /*
     * 先把失败的块记下来再往下走。判据是 `ok === false`（服务端 block() 的形状），
     * 不是「值为空」——空数组是合法内容，和「这块没取到」是两回事。
     */
    if (!readOnly) setBlockErrors(
      Object.entries(sb)
        .filter(([, b]) => b != null && (b as { ok?: boolean }).ok === false)
        .map(([block, b]) => ({ block, error: String((b as { error?: unknown }).error ?? "") })),
    );
    const blocks = emptyBlocks();
    const a = { common: false, rewards: false, cards: false, auctions: false, tiles: false, spins: false, puzzles: false, daily: false, socialChannels: false, socialEventTypes: false, frames: false, pets: false, adoptPools: false, petStars: false, pettownStars: false, pettownSlots: false, matchModes: false, battleStages: false, abilityCards: false, talentNodes: false, boardCells: false, lampDrops: false, intimacy: false, idolPhotos: false, idolVipPhotos: false, idolVipVideos: false, stages: false, rivals: false };
    if (cm.status === "fulfilled") {
      blocks.dice = toDice(cm.value.common);
      blocks.fight = toFight(cm.value.common);
      // 萌宠小镇四参也在 cfg_main_common 这一行里，跟着 common 块一起来
      blocks.pettown = toPettown(cm.value.common);
      blocks.petStar = toPetStar(cm.value.common);
      blocks.intimacy = toIntimacy(cm.value.common);
      blocks.lamp = toLamp(cm.value.common);
      // 三个选择器跟数值参数同表同块，但各走各的 PATCH 字段
      blocks.selectors = {
        gameMode: cm.value.gameMode ?? "",
        buildingSet: cm.value.buildingSet || "default",
        buildingSystem: cm.value.buildingSystem ?? "",
        buildingPresentation: cm.value.buildingPresentation ?? "buildings",
        costSource: cm.value.costSource || "formula",
        costCurve: cm.value.costCurve || "",
        topBonusImg: { ...EMPTY_TOP_BONUS_IMG, ...cm.value.topBonusImg },
      };
      a.common = true;
    }
    if (rw.status === "fulfilled") { blocks.rewards = rw.value.rewards.map(toReward); a.rewards = true; }
    if (pk.status === "fulfilled") {
      blocks.itemPacks = pk.value.packs.filter((p) => p.kind === "item").map(toPack);
      blocks.idolPacks = pk.value.packs.filter((p) => p.kind === "idol").map(toPack);
      a.cards = true;
    }
    if (au.status === "fulfilled") { blocks.auctions = toAuctions(au.value.byLevel); a.auctions = true; }
    if (tl.status === "fulfilled") {
      blocks.tiles = toTiles(tl.value.tiles);
      rawTiles = Object.fromEntries(tl.value.tiles.map((x) => [x.tileTypeId, x]));
      if (!readOnly) serverTilesRef.current = rawTiles;
      rawTilesRev = tl.value.revision;
      a.tiles = true;
    }
    if (sp.status === "fulfilled") { blocks.spins = sp.value.cells.map(toSpin); a.spins = true; }
    // 拼图行与价目表是同一个块：只有两个接口都答上来才算可写，
    // 否则会出现「能编拼图但价目表没加载」——保存时把整张价目表清空
    if (pz.status === "fulfilled" && pp.status === "fulfilled") {
      blocks.puzzles = pz.value.puzzles.map(toPuzzle);
      blocks.puzzlePrices = pp.value.prices;
      a.puzzles = true;
    }
    if (dr.status === "fulfilled") { blocks.dailyRewards = { days: dr.value.days, milestones: dr.value.milestones }; a.daily = true; }
    // 两张社交表各自独立：一张挂了另一张照常可编（它们之间没有引用关系）
    if (sc.status === "fulfilled") { blocks.socialChannels = sc.value.channels; a.socialChannels = true; }
    if (se.status === "fulfilled") { blocks.socialEventTypes = se.value.types; a.socialEventTypes = true; }
    if (fr.status === "fulfilled") { blocks.frames = fr.value.frames; a.frames = true; }
    if (bc.status === "fulfilled") { blocks.boardCells = bc.value.cells; a.boardCells = true; }
    if (ld.status === "fulfilled") { blocks.lampDrops = withTemplate(ld.value.drops); a.lampDrops = true; }
    if (im.status === "fulfilled") {
      blocks.intimacyLevels = im.value.levels;
      blocks.intimacySources = im.value.sources;
      if (!readOnly) setIntimacySourceDefs(im.value.sourceDefs);
      a.intimacy = true;
    }
    // 相册三块各自独立：一块挂了另两块照常可编（它们之间没有引用关系；相册对拼图的引用由发布闸查）
    if (iph.status === "fulfilled") { blocks.idolPhotos = iph.value.rows.map(toIdolPhoto); a.idolPhotos = true; }
    if (ivp.status === "fulfilled") { blocks.idolVipPhotos = ivp.value.rows.map(toIdolVipPhoto); a.idolVipPhotos = true; }
    if (ivv.status === "fulfilled") { blocks.idolVipVideos = ivv.value.rows.map(toIdolVipVideo); a.idolVipVideos = true; }
    if (stg.status === "fulfilled" && sti.status === "fulfilled") {
      blocks.stages = stg.value.rows.map(toStage);
      blocks.stageImages = sti.value.rows.map(toStageImage);
      a.stages = true;
    }
    if (pt.status === "fulfilled") { blocks.pets = pt.value.pets.map(toPet); a.pets = true; }
    if (ps.status === "fulfilled") { blocks.petStars = ps.value.stars; a.petStars = true; }
    if (ap.status === "fulfilled") {
      blocks.adoptPools = ap.value.pools;
      // 写接口也回了一份名册（ap.value.pets），刻意不用：成员表的行源由
      // pets 快照块派生（见 lib/adoptPool.ts 的 AdoptRosterPet），
      // 单独存一份会让池和名册来自两个不同的版本
      a.adoptPools = true;
    }
    // 萌宠小镇两张表各自独立：星级表挂了不影响槽位页照常可编（它们之间只有解锁星数一个弱引用）
    // 星级表是稀疏覆盖：规范化（排序、丢空行）后当基线，读回与本地编辑才比得齐；bValue 原样带着
    if (pts.status === "fulfilled") { blocks.pettownStars = normalizeOverrides(pts.value.stars); a.pettownStars = true; }
    if (pto.status === "fulfilled") { blocks.pettownSlots = pto.value.slots; a.pettownSlots = true; }
    // 多人对战三张表各自独立。solo 代理这三块服务端直接拒（60238），块不可用、页面只读并提示
    // 读接口每行都带 configVersion（元数据）；进了快照，跨版本对比就永远说匹配参数变了。剥掉，PUT 时服务端自己盖版本号
    if (mm.status === "fulfilled") { blocks.matchModes = mm.value.map((r) => { const { configVersion: _v, ...m } = r as MatchMode & { configVersion?: number }; return m; }); a.matchModes = true; }
    if (bs.status === "fulfilled") { blocks.battleStages = bs.value; a.battleStages = true; }
    if (ac.status === "fulfilled") { blocks.abilityCards = ac.value; a.abilityCards = true; }
    if (tn.status === "fulfilled") { blocks.talentNodes = tn.value.nodes; a.talentNodes = true; }
    // 竞品列：定义 + 萌宠逐星价进块；关卡制逐格价挂进各关的 levels[].rivals
    let rivalCellRows: Array<{ rivalId: string; level: number; buildingIdx: number; starLevel: number; cost: Money }> = [];
    if (rv.status === "fulfilled") {
      blocks.rivals = rv.value.rivals.map((r, i) => ({ id: r.rivalId, name: r.name, color: rivalColor(i) }));
      const prices: Record<string, Record<number, Money>> = {};
      for (const r of rv.value.pettownPrices) { const p = money(r.price); if (p != null) (prices[r.rivalId] ??= {})[r.star] = p; }
      blocks.pettownRivalPrices = prices;
      rivalCellRows = rv.value.cells;
      a.rivals = true;
    }
    const snap: Snapshot = { levels: attachRivalGrids(toLevels(pl.levels), rivalCellRows), ...blocks };
    // 底座只认更新的：作废了的旧请求（A→B→A 时第一次的回包最后才到）不能把同一版的底座换回旧的，
    // 否则界面和修订号都是新的、保存却带着旧的隐藏字段，CAS 拦不住
    if (rawTiles) {
      const had = serverTilesByVerRef.current[tilesKey];
      if (!had || rawTilesRev >= had.revision) serverTilesByVerRef.current[tilesKey] = { revision: rawTilesRev, tiles: rawTiles };
    }
    return { snap, revision: pl.revision, avail: a };
  };
  useEffect(() => {
    if (!session.token || isExpired(liveSession(session))) return;
    /*
     * 代理还没选中就不拉。agent 初始是 null、要等 /agents 回来才有值，
     * 而本 effect 一有会话就跑——首拉带不上 X-A0，服务端必然回 401（10068 缺 a0）。
     * 后端慢的时候这个 401 回来得晚，effect 已被下一次（agent 有值）清理掉，
     * 没人看见；后端快了以后它先到，页面就闪一下「配置读取失败」，
     * 而且 api.ts 把 401 当票失效顺手清掉自有票，下一个请求还得再换一张。
     * 没有租户上下文的请求本来就不该发。
     */
    if (!agent) return;
    let alive = true;
    /*
     * 租户一变必须清空重拉。之前用 levels.length 当「只拉一次」守卫，
     * 结果首拉发生在 agent 还没选中时（打的是默认库），选中后守卫挡住重拉——
     * 界面顶着 A 代理的名字显示默认库的数据，配置串租户。
     * effect 依赖 agent：换代理先清干净，旧租户的数据一格不留。
     */
    setApiA0(agent);
    applySnap(mkSnap([]));
    setVersions([]);
    setStore({});
    setCurVid("");
    setBaseline("");
    setUnconfigured(false);
    setSnapLoaded(false);
    setSnapAgent(null);
    // 上一个代理还在路上的切版本一律作废：A→B→A 时版本号相同、代理也对得上，单靠代理比对挡不住
    switchSeqRef.current++;
    setVerUi(null);
    setPendingAdds([]);
    setAvail({ common: false, rewards: false, cards: false, auctions: false, tiles: false, spins: false, puzzles: false, daily: false, socialChannels: false, socialEventTypes: false, frames: false, pets: false, adoptPools: false, petStars: false, pettownStars: false, pettownSlots: false, matchModes: false, battleStages: false, abilityCards: false, talentNodes: false, boardCells: false, lampDrops: false, intimacy: false, idolPhotos: false, idolVipPhotos: false, idolVipVideos: false, stages: false, rivals: false });
    setLoadError(null);
    (async () => {
      try {
        // 竞品列 2026-09-16 起随版本存，跟快照一起来（loadSnapshot 的 rivals 块），不再单独拉参照数据集
        /*
         * 初始化那两个单选的锁：代理级事实，跟版本无关，所以跟版本列表一起拉、
         * 换代理时重拉。**读失败按不锁处理**（catch 成 null）——锁不上最多是让人
         * 改一次被发布闸拦下；一次读失败就把界面锁死，人会以为配置台坏了。
         */
        const locksP = fetchInitLocks().catch(() => null);
        const rows = await fetchVersions();
        if (!alive) return;
        void locksP.then((l) => { if (alive) setInitLocks(l); });
        // 后端答了、但这个代理一版都没有：空状态 + 引导建第一版，不退演示数据
        if (!rows.length) { setUnconfigured(true); setSnapLoaded(true); setSnapAgent(agent); return; }
        const active = rows.find((r) => r.active);
        const open = initialVersion(rows)!;
        const opened = await loadSnapshot(open.configVersion);
        if (!alive) return;
        if (!opened) { setLoadError(t("initcfg.app.loadVersionFail")); setSnapLoaded(true); setSnapAgent(agent); return; }
        const nextStore: VersionStore = { [vid(open.configVersion)]: opened.snap };
        const nextRevs: Record<string, number> = { [vid(open.configVersion)]: opened.revision };
        if (active && active.configVersion !== open.configVersion) {
          const act = await loadSnapshot(active.configVersion);
          if (!alive) return;
          if (act) { nextStore[vid(active.configVersion)] = act.snap; nextRevs[vid(active.configVersion)] = act.revision; }
        }
        setAvail(opened.avail);
        setRevs(nextRevs);
        setVersions(rows.map(toVersion));
        setStore(nextStore);
        setCurVid(vid(open.configVersion));
        applySnap(cloneSnap(opened.snap));
        setBaseline(JSON.stringify(opened.snap));
        setSnapLoaded(true); setSnapAgent(agent);
      } catch (e) {
        // 读不到就说读不到。**不造演示数据顶上**——顶上去之后页面看着一切正常，
        // 策划会对着一份不存在的配置改半天，保存时才发现什么都没有。
        if (alive) { setLoadError((e as Error).message || t("initcfg.app.backendDown")); setSnapLoaded(true); setSnapAgent(agent); }
      }
    })();
    return () => { alive = false; };
    // levels/store 等在本 effect 里被重置，不能进依赖——只有会话与租户变化才触发重拉
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, agent, reloadKey]);
  /**
   * 完全不读配置的页面。它们既不该等配置载入，**也不该被配置载入失败连坐**——
   * 配置读不出来时玩家面板照样应该能用（它查的是玩家数据，跟配置版本无关）。
   * 只有真的不碰 levels / spins / tiles 的页面才能进这个名单。
   */
  /*
   * 版本漂移守卫：定时 + 窗口重新聚焦时复查一次版本列表，发现线上生效版本
   * 变了就横幅提醒。**只读不写**——不动 curVid、不动快照、不碰未保存的编辑。
   * 30 秒一次：配置台是低频写场景，再密只是白打接口。
   */
  useEffect(() => {
    if (!agent || unconfigured || loadError) return;
    let alive = true, checking = false;
    const check = async () => {
      if (document.visibilityState !== "visible" || checking) return;
      checking = true;
      try {
        const rows = await fetchVersions();
        if (!alive || !rows.length) return;
        const next = rows.map(toVersion);
        const nowOnline = next.find((v) => v.status === "active")?.id ?? null;
        setVersions((prev) => {
          const wasOnline = prev.find((v) => v.status === "active")?.id ?? null;
          // 只有真的换了生效版本才提醒；版本条数变化（别人建了草稿）不打扰
          if (prev.length && wasOnline !== nowOnline) setDrift({ was: wasOnline, now: nowOnline });
          return next;
        });
      } catch { /* 网络抖动不打扰，下一轮再看 */ }
      finally { checking = false; }
    };
    const timer = setInterval(check, 30_000);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", check); };
  }, [agent, unconfigured, loadError]);

  const CONFIG_FREE_PAGES = ["report-players", "report-client-state", "match-monitor", "debug-trace", "dev-token", "dev-panel", "gm", "accounts", "agents", "shop", "shop-sections", "mail", "reports", "push-events", "hot-update", "share-links", "piggy", "pay-chain", "econ-switch"];
  const isLearningPage = LEARNING_PAGES.includes(page);
  // 学习进度写透到服务端（进度排名用）：播放器在任何配置页都能打开，所以挂在登录态上而不是课程页上
  const operator = session?.operator ?? null;
  useEffect(() => startLearningSync(operator, pushLearningProgress), [operator]);
  const configFree = isLearningPage || CONFIG_FREE_PAGES.includes(page);
  useEffect(() => {
    if (!session) { setIsSuperAdmin(false); setIsRoot(false); return; }
    let alive = true;
    void fetchTrace()
      .then((d) => { if (alive) { setIsSuperAdmin(!!d.is_super_admin); setIsRoot(!!d.is_root); } })
      .catch(() => { if (alive) { setIsSuperAdmin(false); setIsRoot(false); } });
    return () => { alive = false; };
  }, [session]);
  // 本版本的 E / ρ。所有算目标造价 / 公式价的地方都要拿它——落到 DEFAULT_GLOBALS 的话，
  // 1201 第一关会显示 837（3.1 × 0.9）而客户端实际是 295
  // E / ρ 没配时（新版本是 -1 占位）照实传下去，算法总造价显示「—」并说明缺什么；
  // 不拿默认常量顶上（a0=leo 2026-10-09：没配 E / ρ 却显示了 41,850）
  /*
   * 按「几倍单掷金币」填、按 B 数存的格子用来换算的 E：model 2 版本用迁移时定下的旧 E（刻度就是 1000 / 它），
   * 不看页面上的 E——model 2 里 E 不参与计算、也不显示
   */
  const unitE = curEcon.model === 2 ? (curEcon.legacyEMilli != null ? curEcon.legacyEMilli / 1000 : null) : dice.E;
  const globals = useMemo<Globals>(() => ({ unifiedB: dice.unifiedB === 1, algorithmAnchorB: levels.find(lv => lv.id === 1)?.B, costCurve: selectors.costCurve, E: dice.E ?? Number.NaN, rho: dice.rho ?? Number.NaN, incomeGrowthBps: Math.round((dice.incomeK ?? 0) * 10000), incomeAnchorB: dice.incomeAnchor ?? 0, targetRollsBase: dice.targetBase ?? 0, targetRollsGrowthBps: Math.round((dice.targetGrowth ?? 0) * 10000), economyModel: curEcon.model }), [curEcon.model, dice.unifiedB, levels, dice.E, dice.rho, dice.incomeK, dice.incomeAnchor, dice.targetBase, dice.targetGrowth, selectors.costCurve]);
  const starCurveSeries = useMemo(() => page === "pettown-star" ? pettownSeries(pettown.maxStar??0,pettownStars,globals,rivals,pettownRivalPrices,dice.init) : [], [page,pettown.maxStar,pettownStars,globals,rivals,pettownRivalPrices,dice.init]);
  const results = useMemo(() => levels.map((l) => validate(l, globals)), [levels, globals]);
  // 以 B 计的数折金币用的尺：每关 / 每颗星运行时实际的 B（components/data/BCoins.tsx）
  const bScale = useMemo<BScale>(() => {
    /*
     * model 2（单掷金币）版本：按几倍单掷金币填的数，实际金币 = 数 × 刻度 × g（服务端 money-unit.ts coins）。
     * 刻度是迁移时定的 1000 / 旧 E，所以折算用的「每份」= g × 刻度，「几掷」= 数 ÷ 旧 E——与旧口径显示的数一致，只是来源换成 g
     */
    if (curEcon.model === 2) {
      const num = toBig(curEcon.scale?.num ?? null), den = toBig(curEcon.scale?.den ?? null);
      // 不在这里除：先取整成每个 q 几枚，再乘 q 会放大误差（禅道 #9362：11.09 取成 11，上限少算 0.83%）
      const ok = num != null && den != null && den !== 0n;
      const per = (gMilli: string | null | undefined) => {
        const g = toBig(gMilli ?? null);
        return g == null || !ok ? null : g * num!;
      };
      const exact = ok ? 1000n * den! : undefined;
      const e = curEcon.legacyEMilli != null ? curEcon.legacyEMilli / 1000 : null;
      if (selectors.buildingSystem === "pettown") return { unit: "star", rows: pettownStarsView.map((s) => ({ n: s.star, b: per(pettownStars.find((x) => x.star === s.star)?.gMilli) })), e, model: 2, den: exact };
      return { unit: "level", rows: levels.map((lv) => ({ n: lv.id, name: lv.name, b: per(lv.gMilli) })), e, model: 2, den: exact };
    }
    if (selectors.buildingSystem === "pettown") return { unit: "star", rows: pettownStarsView.map((s) => ({ n: s.star, b: s.runtimeB })), e: dice.E };
    const bs = levelRuntimeB(levels, { ...globals, incomeGrowthBps: Math.round((dice.incomeK ?? 0) * 10000), incomeAnchorB: dice.incomeAnchor ?? 0, targetRollsBase: dice.targetBase ?? 0, targetRollsGrowthBps: Math.round((dice.targetGrowth ?? 0) * 10000) }, selectors.costSource);
    return { unit: "level", rows: levels.map((lv, i) => ({ n: lv.id, name: lv.name, b: bs[i] })), e: dice.E };
  }, [selectors.buildingSystem, selectors.costSource, pettownStarsView, pettownStars, levels, globals, dice.incomeK, dice.incomeAnchor, dice.E, curEcon]);
  const defaultIncomeB = useMemo(() => {
    if (selectors.buildingSystem === "pettown") return deriveStars(1, pettownStars, dice.E, dice.rho,
      { costSource: selectors.costSource, rivalPrices: pettownRivalPrices, economyModel: curEcon.model })[0]?.runtimeB ?? null;
    const first = levels.find((lv) => lv.id === 1);
    if (dice.unifiedB === 1) return toBig(first?.B);
    return first ? levelRuntimeB([first], { ...globals, incomeGrowthBps: 0 }, selectors.costSource)[0] : null;
  }, [selectors.buildingSystem, selectors.costSource, pettownStars, pettownRivalPrices, dice.E, dice.rho, dice.unifiedB, levels, globals, curEcon.model]);
  const incomeAnchorPlaceholder = defaultIncomeB == null ? t("common.pending") : t("p.incomeAnchor.default", { b: fmt(defaultIncomeB) });
  const curVer = versions.find((v) => v.id === curVid);
  const online = versions.find((v) => v.status === "active");
  /** 只有草稿可编辑；查看历史/生效版本时全页只读 */
  const editable = curVer?.status === "draft";
  // 脏检测覆盖整份快照：关卡之外，全局参数/奖励/卡牌/竞拍的改动同样算未保存。
  // 空基线（还没打开版本：门禁页 / 首屏 / 刚切代理）不算脏，见 lib/dirty.ts
  const snapJson = useMemo(
    () => JSON.stringify(takeSnap()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // 依赖必须与 takeSnap 的字段一一对应。漏一个（tiles 就漏过）＝ 改了那个块
    // 不重算，顶栏永远显示「无改动」，发布前的 dirty 拦截也跟着失效。
    [levels, dice, fight, rewards, itemPacks, idolPacks, auctions, tiles, spins, puzzles, puzzlePrices, dailyRewards,
      socialChannels, socialEventTypes, frames, pets, adoptPools, petStars, petStar, selectors, pettown, pettownStars, pettownSlots, matchModes, battleStages, abilityCards, talentNodes, boardCells, lampDrops, lamp, intimacyLevels, intimacySources, intimacy, idolPhotos, idolVipPhotos, idolVipVideos, stages, stageImages, rivals, pettownRivalPrices],
  );
  const dirty = useMemo(() => isDirty(baseline, snapJson), [baseline, snapJson]);
  /*
   * 草稿实时保存（leo 2026-10-09：刷新不能丢东西）。改完停 1.5 秒自动存，不再有「保存草稿」按钮。
   *
   * 状态机：idle（本会话还没存过，显示服务端记的最后写入人）→ saving → saved / error / conflict。
   * - error 分两种：网络类（没拿到信封）按 2s 起翻倍自动重试；服务端拒了的（校验不过等）
   *   不重试，等人再改一笔再试——同一份内容重试只会再被拒一遍。
   * - conflict：别人先写了这一版（修订号对不上）或这一版已经不是草稿。不自动覆盖，
   *   等人点「载入最新」：没动过的块换成服务端的，动过的块留着继续存。
   */
  const [saveState, setSaveState] = useState<SaveState>({ state: "idle" });
  const savingRef = useRef(false);
  /**
   * 保存失败后、还没对上账时留下的合并基线（发起保存时的服务端 + 已经写成功的部分）：之后人点「载入最新」还要用它，
   * 不能退回 store 里那份旧的——旧的里没有我这一轮已经写进去的块
   */
  const pendingBaseRef = useRef<Snapshot | null>(null);
  /**
   * 还没对上账的那几轮各自发出去的内容（按先后）：回包丢失时，读回里和它们一样的地方算我写进去的（refineBase）。
   * 重试对账也失败时会再加一轮，不能只留最后一轮——前面那轮的写入同样可能已经落库
   */
  const pendingSentRef = useRef<Snapshot[]>([]);
  /** 已确认基线 + 逐轮按读回修正：哪些「可能写进去了」的确实写进去了 */
  // 发出去的新建行先按读回精确认领身份（拼图 uid、萌宠编号）：没 id 的那份认不出是同一行，
  // 「建成了、我又在本地删了」会被当成别人新加的行而复活
  /** 建行请求发出去了、还没确认落库的萌宠编号：回包丢失认领只认这些（lib/autosave.ts reconcileLostCreates） */
  const sentPetIds = () => new Set(pendingSentRef.current.flatMap((s) => s.pets.filter((p) => !p.persisted).map((p) => p.petId)));
  const refinedBase = (server: Snapshot) => pendingSentRef.current.reduce((b, s) => refineBase(b, reconcileLostCreates(s, server), server) as Snapshot, pendingBaseRef.current!);
  /** 存完后广播给协同房间；协同 hook 在下面才建，这里先放个 ref */
  const collabAnnounceRef = useRef<(revision: number, summary: string) => void>(() => {});
  /** 最新一帧的修订号：读回的快照比它旧就不收（两个读回乱序回来时修订号不能倒退） */
  const revsRef = useRef<Record<string, number>>({});
  revsRef.current = revs;
  /**
   * 眼前在编的是哪个代理的哪一版。保存在路上时人可能切了版本或代理（切换确认里点了放弃），
   * 回来的结果只能落进缓存，不能盖到新打开的那一版上——两个代理的版本号还可能撞
   */
  const editingKeyRef = useRef("");
  /** 最新一帧的代理：异步拉回来的东西据此判断「拉的时候和现在是不是同一个代理」 */
  const agentRef = useRef(agent);
  agentRef.current = agent;
  const curVidRef = useRef(curVid);
  curVidRef.current = curVid;
  editingKeyRef.current = `${agent ?? ""}|${curVid}`;
  // 表单里边改边存的草稿也按这个作用域隔开（useLiveDraft）：切走之后旧版本没交的那笔不会交进新版本
  setLiveDraftScope(editingKeyRef.current);
  useEffect(() => { setSaveState({ state: "idle" }); pendingBaseRef.current = null; pendingSentRef.current = []; }, [curVid]);
  const autoSaveRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!editable || !dirty || saveState.state === "saving" || saveState.state === "conflict") return;
    if (saveState.state === "error" && !saveState.retry && saveState.at === snapJson) return;
    const wait = saveState.state === "error" && saveState.retry ? retryDelay(saveState.attempt) : AUTOSAVE_DEBOUNCE_MS;
    const timer = setTimeout(() => autoSaveRef.current(), wait);
    return () => clearTimeout(timer);
  }, [snapJson, baseline, editable, dirty, saveState]);
  // 还有没存上的改动时拦一下关页——防抖的 1.5 秒里、或者存失败了，刷新仍会丢
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  /** 有未保存改动时先确认再切——否则草稿被静默覆盖，改动无从找回 */
  const requestSwitch = (vid: string, back?: "history") => {
    if (vid === curVid) return;
    // 表单里还在防抖的那一笔先交掉：交出东西了就算有未存改动，按同一个确认走
    const flushed = flushLiveDrafts();
    if (dirty || flushed) setVerUi({ kind: "confirmSwitch", vid, back });
    else switchVersion(vid);
  };
  /**
   * 切版本的序号：拉快照是异步的，回来之前又切了别的版本、或者跳到别人那里落在了当前版本，
   * 这份迟到的快照就不能再套到界面上（跟随时对方连着换版本，乱序回来会把我拽回旧版本）。缓存照收
   */
  const switchSeqRef = useRef(0);
  const switchVersion = (target: string) => {
    const seq = ++switchSeqRef.current;
    const apply = (snap: Snapshot) => {
      setCurVid(target);
      applySnap(cloneSnap(snap));
      setBaseline(JSON.stringify(snap));
      setPendingAdds([]);
      setVerUi(null);
    };
    // 草稿会被其他人继续编辑。切回来必须重读，也要带回这一版的块可用性；
    // 沿用上一版的 avail 会让新版本可编辑却不发保存请求，或把读失败的块当作空表写回。
    const forAgent = agent;
    const fromKey = editingKeyRef.current;
    const fromSnap = JSON.stringify(liveSnapRef.current);
    void loadSnapshot(vnum(target))
      .then((r) => {
        if (agentRef.current !== forAgent || seq !== switchSeqRef.current || editingKeyRef.current !== fromKey) return;
        if (!r) throw new Error(t("ver.snapshotLoadFail", { id: target }));
        // 加载期间旧页面仍能输入；先交掉表单防抖中的一笔，不能用目标快照覆盖刚输入的内容。
        const flushed = flushLiveDrafts();
        if (flushed || JSON.stringify(liveSnapRef.current) !== fromSnap) {
          setVerUi(null);
          setToast(t("ver.saveFirst"));
          return;
        }
        setStore((st) => ({ ...st, [target]: r.snap }));
        setRevs((rv) => ({ ...rv, [target]: r.revision }));
        setAvail(r.avail);
        apply(r.snap);
      })
      .catch((e) => {
        if (agentRef.current === forAgent && seq === switchSeqRef.current && editingKeyRef.current === fromKey) {
          setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
        }
      });
  };

  /** 版本快照按需拉取并缓存；对比/回滚不能拿「未加载」冒充空配置。 */
  const ensureSnapshot = async (id: string): Promise<Snapshot> => {
    const forAgent = agentRef.current;
    if (forAgent !== agent) throw new Error(t("auditChange.contextChanged"));
    const assertReadable = () => {
      const missing = snapshotFailures.current[`${forAgent ?? ""}|${vnum(id)}`];
      if (missing?.length) throw new Error(t("auditChange.incomplete", { id, blocks: missing.join(", ") }));
    };
    const cached = store[id];
    const version = versions.find((v) => v.id === id);
    if (cached && !snapshotFailures.current[`${forAgent ?? ""}|${vnum(id)}`]?.length && version && version.status !== "draft") return cached;
    const loaded = await loadSnapshot(vnum(id), true);
    if (agentRef.current !== forAgent) throw new Error(t("auditChange.contextChanged"));
    if (!loaded) throw new Error(t("ver.snapshotLoadFail", { id }));
    assertReadable();
    setStore((st) => ({ ...st, [id]: loaded.snap }));
    setRevs((rv) => ({ ...rv, [id]: loaded.revision }));
    return loaded.snap;
  };

  /*
   * 发布确认里列「这次改了什么」：打开时拉线上生效版本（拉过的在 store 里），和眼前工作副本比。
   * 工作副本在弹窗打开那一刻取一次，弹窗开着时不会再变（编辑区被遮住）
   */
  const [pubDiff, setPubDiff] = useState<PublishDiff>({ state: "loading" });
  const pubOpen = verUi?.kind === "publish";
  useEffect(() => {
    if (!pubOpen || !online) return;
    let alive = true;
    setPubDiff({ state: "loading" });
    ensureSnapshot(online.id)
      .then((left) => { if (alive) setPubDiff({ state: "ready", left, right: cloneSnap(takeSnap()) }); })
      .catch(() => { if (alive) setPubDiff({ state: "error" }); });
    return () => { alive = false; };
    // 只在弹窗打开 / 换了生效版本时拉；ensureSnapshot / takeSnap 每次渲染都是新函数，放进依赖会反复重拉
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubOpen, online?.id]);

  /** 页头对比：右侧必须是眼前工作副本，才能把尚未保存的变化也显示出来。 */
  const compareCurrentToOnline = async () => {
    if (!online) return;
    if (!dirty) { await comparePair(online.id, curVid); return; }
    try {
      const left = await ensureSnapshot(online.id);
      setVerUi({
        kind: "diffPair", left, right: cloneSnap(takeSnap()),
        la: t("ver.online", { id: online.id }), lb: curVid,
      });
    } catch (e) {
      setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
    }
  };

  const loadComparisonPayload = async (id: string) => {
    const forAgent = agentRef.current;
    if (forAgent !== agent) throw new Error(t("auditChange.contextChanged"));
    const key = `${forAgent ?? ""}|${vnum(id)}`;
    const draft = versions.some((v) => v.id === id && v.status === "draft");
    const cached = rawSnapshots.current[key];
    const complete = cached && COMPARISON_BLOCKS.every((k) => cached.blocks[k]?.ok);
    const raw = (!draft && complete && cached) || await fetchSnapshotOf(vnum(id));
    if (!raw || agentRef.current !== forAgent) throw new Error(t("auditChange.contextChanged"));
    rawSnapshots.current[key] = raw;
    return raw;
  };
  const comparisonSeq = useRef(0);
  const comparePair = async (leftId: string, rightId: string) => {
    const seq = ++comparisonSeq.current;
    const forAgent = agentRef.current;
    try {
      const [left, right, leftRaw, rightRaw] = await Promise.all([ensureSnapshot(leftId), ensureSnapshot(rightId), loadComparisonPayload(leftId), loadComparisonPayload(rightId)]);
      const changes = [...rawSnapshotChanges(leftRaw, rightRaw), ...currencyChanges(versions.find((v) => v.id === leftId) ?? {}, versions.find((v) => v.id === rightId) ?? {})];
      if (agentRef.current !== forAgent || seq !== comparisonSeq.current) return;
      setVerUi({ kind: "diffPair", left, right, la: leftId, lb: rightId, leftId, rightId, changes, ...publishedComparisonProof(versions.find((v) => v.id === leftId), versions.find((v) => v.id === rightId)) });
    } catch (e) { setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true); }
  };
  const compareVersionToSource = (v: Version) => {
    const source = [v.base, v.from].find((id) => id && id !== v.id && versions.some((row) => row.id === id)) ?? online?.id;
    if (source) void comparePair(source, v.id);
  };

  /** 历史对比先把两边完整快照拉齐，不能只比较 levels 或把 cache miss 当空数组。 */
  const compareVersionToOnline = async (v: Version) => {
    if (online) await comparePair(online.id, v.id);
  };

  /**
   * 群通知卡片「查看完整改动」的直达：`#diff/<代理>/<旧>..<新>`（admin/config-notify.ts 的 diffUrl）。
   * 代理写在 hash 里而不只靠 `?a0=`：密码登录后整页重进会丢掉查询串、只留 hash（lib/landing.ts），
   * 没登录的人点进来会落到上次选的代理上，拿同样的版本号比出别的代理的内容。
   * 不是这个代理就先切过去；等这个代理的快照载完（snapAgent）再开对比。同一个 hash 只开一次
   */
  const diffLinkOpened = useRef("");
  useEffect(() => {
    const open = () => {
      const m = /^#diff\/(\w+)\/(\d+)\.\.(\d+)$/.exec(location.hash);
      if (!m || diffLinkOpened.current === location.hash) return;
      if (agent !== m[1]) { switchAgent(m[1]); return; }
      if (snapAgent !== m[1]) return;
      diffLinkOpened.current = location.hash;
      const [a, b] = [vid(Number(m[2])), vid(Number(m[3]))];
      void comparePair(a, b);
    };
    open();
    window.addEventListener("hashchange", open);
    return () => window.removeEventListener("hashchange", open);
    // 代理切过去、快照载完时各重试一次；ensureSnapshot / switchAgent 每次渲染都是新函数，放进依赖会反复注册
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, snapAgent]);

  const openRollback = async (v: Version) => {
    const forAgent = agentRef.current;
    try {
      const right = await ensureSnapshot(v.id);
      const left = online ? await ensureSnapshot(online.id) : right;
      const [leftRaw, rightRaw] = await Promise.all([loadComparisonPayload(online?.id ?? v.id), loadComparisonPayload(v.id)]);
      if (agentRef.current !== forAgent) return;
      setVerUi({ kind: "rollback", v, left, right, changes: [...rawSnapshotChanges(leftRaw, rightRaw), ...currencyChanges(online ?? {}, v)] });
    } catch (e) {
      setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
    }
  };

  /**
   * 实时保存的一轮：对每个改过的块下发补丁（revision CAS 串行推进）→ 读回服务端事实
   * 作为新基线，不信本地拼装 → 和保存期间的新输入按块合并（lib/autosave.ts）。
   */
  const doSaveServer = async () => {
    // 失败归属：每发一块之前记一下这块属于哪一页，catch 里据此在那一页标红。
    // 声明在 try 之外——放里面的话 catch 根本看不见它
    let failPage = "init-config";
    // 这次发出去的工作副本与它的基线：实时保存结束后据此合并保存期间的新输入
    const sent = takeSnap();
    /*
     * 差异基线：上一轮失败、还没对上账时，服务端「至少」已经是 pendingBaseRef 那样（含已写成功的部分），
     * 拿它比才对——拿 store 里的旧服务端比，保存期间改回原值的输入会被判成「没改」，永远存不上去
     */
    const baseSnap = pendingBaseRef.current ?? store[curVid] ?? mkSnap([]);
    /*
     * 哪几关是新加的：按 uid 认（服务端的基线里没有这个 uid），不按待插清单的位置号——
     * 协同时别人插了关，位置号就对不上了；回包丢了的关读回后 uid 已在基线里，也不会再建一遍
     */
    const baseUids = new Set(baseSnap.levels.map((l) => l.uid));
    const adds = levels.filter((l) => !baseUids.has(l.uid)).map((l) => l.id).sort((a, b) => a - b);
    const key = editingKeyRef.current;
    /** 这一轮服务端确认建出来的行。不论成败都要套回本地，否则下一轮会再建一遍 */
    const receipts = emptyReceipts();
    /*
     * 已确认写进服务端的样子：从发起保存时的服务端出发，**每一个请求成功后**把它写的那一块 / 那一行改成我发出去的。
     * 失败后对账拿它当合并基线：写成功的部分服务端就是我发的，不能当成「别人改的」——
     * 否则保存期间我把某个值又改回原样，会被读回里我自己刚写进去的旧值盖掉。按行记，一块写到一半失败也对得上
     */
    const ab = { ...baseSnap } as unknown as Record<string, unknown>;
    const sentA = sent as unknown as Record<string, unknown>;
    const ackBlock = (...ks: Array<keyof Snapshot>) => { for (const k of ks) ab[k] = sentA[k]; };
    const ackRow = <R,>(k: keyof Snapshot, idOf: (r: R) => unknown, row: R) => {
      const list = (ab[k] as R[] | undefined) ?? [];
      const i = list.findIndex((x) => idOf(x) === idOf(row));
      ab[k] = i >= 0 ? list.map((x, j) => (j === i ? row : x)) : [...list, row];
    };
    const ackDel = <R,>(k: keyof Snapshot, idOf: (r: R) => unknown, id: unknown) => { ab[k] = ((ab[k] as R[] | undefined) ?? []).filter((x) => idOf(x) !== id); };
    try {
      const base = baseSnap.levels;
      /*
       * 萌宠小镇星级覆盖：上限调小时**不删**超出的行，原样存着、不生效；上限调回去它们就回来。
       * 删的话「12」改成「15」中途在「1」停一下（参数边改边存 + 自动保存），1 星以上的价、皮肤全没了
       */
      const starsOut = pettownStars;
      const commonPatch = avail.common
        ? diffCommon(
          { dice: baseSnap.dice, fight: baseSnap.fight, pettown: baseSnap.pettown, petStar: baseSnap.petStar, intimacy: baseSnap.intimacy, lamp: baseSnap.lamp },
          { dice, fight, pettown, petStar, intimacy, lamp },
        )
        : null;
      const blockChanged =
        commonPatch != null ||
        (avail.pettownStars && JSON.stringify(starsOut) !== JSON.stringify(baseSnap.pettownStars)) ||
        (avail.rivals && JSON.stringify(rivals.map((r) => [r.id, r.name])) !== JSON.stringify((baseSnap.rivals ?? []).map((r) => [r.id, r.name]))) ||
        (avail.rivals && JSON.stringify(pettownRivalPrices) !== JSON.stringify(baseSnap.pettownRivalPrices ?? {})) ||
        (avail.rivals && rivals.some((r) => changedRivalLevels(base, levels, r.id).length > 0)) ||
        (avail.adoptPools && JSON.stringify(adoptPools) !== JSON.stringify(baseSnap.adoptPools)) ||
      (avail.petStars && JSON.stringify(petStars) !== JSON.stringify(baseSnap.petStars)) ||
        (avail.pettownSlots && JSON.stringify(pettownSlots) !== JSON.stringify(baseSnap.pettownSlots)) ||
        (avail.matchModes && JSON.stringify(matchModes) !== JSON.stringify(baseSnap.matchModes)) ||
        (avail.battleStages && JSON.stringify(battleStages) !== JSON.stringify(baseSnap.battleStages)) ||
        (avail.abilityCards && JSON.stringify(abilityCards) !== JSON.stringify(baseSnap.abilityCards)) ||
        (avail.talentNodes && JSON.stringify(talentNodes) !== JSON.stringify(baseSnap.talentNodes ?? [])) ||
        (avail.rewards && JSON.stringify(rewards) !== JSON.stringify(baseSnap.rewards)) ||
        (avail.cards && (JSON.stringify(itemPacks) !== JSON.stringify(baseSnap.itemPacks) || JSON.stringify(idolPacks) !== JSON.stringify(baseSnap.idolPacks))) ||
        (avail.auctions && JSON.stringify(auctions) !== JSON.stringify(baseSnap.auctions)) ||
        // tiles 漏在这里 = 只改玩法格时被判成「无改动」，直接返回，下面的 PUT 一次都不发。
        // 界面上更难受：顶栏明明亮着「有未保存改动」（dirty 走 takeSnap，它认 tiles），
        // 点保存却弹「无改动」——两个判断必须看同一份东西。
        (avail.tiles && JSON.stringify(tiles) !== JSON.stringify(baseSnap.tiles)) ||
        (avail.spins && JSON.stringify(spins) !== JSON.stringify(baseSnap.spins)) ||
        (avail.puzzles && (JSON.stringify(puzzles) !== JSON.stringify(baseSnap.puzzles) || JSON.stringify(puzzlePrices) !== JSON.stringify(baseSnap.puzzlePrices))) ||
        (avail.daily && JSON.stringify(dailyRewards) !== JSON.stringify(baseSnap.dailyRewards)) ||
        (avail.socialChannels && JSON.stringify(socialChannels) !== JSON.stringify(baseSnap.socialChannels)) ||
        (avail.socialEventTypes && JSON.stringify(socialEventTypes) !== JSON.stringify(baseSnap.socialEventTypes)) ||
        (avail.frames && JSON.stringify(frames) !== JSON.stringify(baseSnap.frames)) ||
        (avail.boardCells && JSON.stringify(boardCells) !== JSON.stringify(baseSnap.boardCells)) ||
        (avail.lampDrops && JSON.stringify(lampDrops) !== JSON.stringify(baseSnap.lampDrops ?? [])) ||
        (avail.intimacy && (JSON.stringify(intimacyLevels) !== JSON.stringify(baseSnap.intimacyLevels ?? [])
          || JSON.stringify(intimacySources) !== JSON.stringify(baseSnap.intimacySources ?? []))) ||
        (avail.idolPhotos && JSON.stringify(idolPhotos) !== JSON.stringify(baseSnap.idolPhotos ?? [])) ||
        (avail.idolVipPhotos && JSON.stringify(idolVipPhotos) !== JSON.stringify(baseSnap.idolVipPhotos ?? [])) ||
        (avail.idolVipVideos && JSON.stringify(idolVipVideos) !== JSON.stringify(baseSnap.idolVipVideos ?? [])) ||
        (avail.stages && (JSON.stringify(stages) !== JSON.stringify(baseSnap.stages ?? [])
          || JSON.stringify(stageImages) !== JSON.stringify(baseSnap.stageImages ?? []))) ||
        // 比**规范化后**的形状，与下面逐行提交的判据同源。比原始 Pet 的话，
        // 一个只被规范化吃掉的编辑（颜色末尾多打个空格、语言抽屉里加了条只有空白的）
        // 会让这里为真、而逐行循环每一行都 continue —— 结果是分叉出一个空版本、
        // 一个请求都不发，然后跟运营说「已保存」。正是上面这道 hasChange 闸要防的事
        (avail.pets && JSON.stringify(pets.map(toServerPet)) !== JSON.stringify(baseSnap.pets.map(toServerPet))) ||
        (avail.common && JSON.stringify(selectors) !== JSON.stringify(baseSnap.selectors));
      const hasChange = adds.length > 0 || blockChanged || levels.some((lv) => {
        const b = base.find((x) => x.uid === lv.uid);
        return b ? !!diffToPatch(b, lv) : false;
      });
      if (!hasChange) {
        // 上一轮还没对上账（回包丢了、读回也失败）：相对已知的样子没改动，不等于服务端也是这样——先对账
        if (pendingBaseRef.current) {
          const r = await reloadRef.current({ silent: true });
          if (!r) throw new Error(t("ver.autoReconcileFail"));
          return;
        }
        // 工作副本和服务端只差在规范化掉的东西上（比如末尾空格）：把基线挪到这份上，
        // 否则会一直判「有改动」、一直触发保存
        setBaseline(JSON.stringify(sent)); setSaveState({ state: "idle" }); return;
      }
      /*
       * 只写当前这份草稿，带着手上的修订号；不建、不复用别的草稿。
       * 别人先写了会撞修订号，进 conflict，不会悄悄落到另一份上。
       * （以前「保存」会先 createDraft：一个代理只有一个草稿，有就复用。现在编辑的
       * 只能是草稿本身，那一步没有意义了。）
       */
      const configVersion = vnum(curVid);
      const curRev = revs[curVid];
      if (curRev == null) throw new Error(t("ver.snapshotLoadFail", { id: curVid }));
      let rev = curRev;
      /**
       * 服务端分配 ID 的逐行表（idol 相册 / VIP 照片 / VIP 视频）：与下面拼图那段同一套，三块共用这一份。
       * **先删后建再改**；只发改过的行（整表推一遍会让审计看不出谁改了什么）；
       * 新建按 `id == null` 走 POST，回来的 ID 记进回执、不回写本地 state（保存结束后读回再套回执）。
       * 每一笔成功都立刻接力到外层的 `rev`：中途某一行被拒时，前面已写成功的修订号不能丢
       */
      const saveAssignedRows = async <R extends { localKey: string },>(o: {
        block: "idolPhotos" | "idolVipPhotos" | "idolVipVideos" | "stages" | "stageImages";
        rows: R[]; base: R[];
        idOf: (r: R) => string | null;
        withId: (r: R, id: string) => R;
        create: (rev: number, r: R) => Promise<{ revision: number; id: string }>;
        put: (rev: number, id: string, r: R) => Promise<{ revision: number }>;
        del: (rev: number, id: string) => Promise<{ revision: number }>;
      }): Promise<void> => {
        for (const b of o.base) {
          const id = o.idOf(b);
          if (id && !o.rows.some((x) => o.idOf(x) === id)) {
            rev = (await o.del(rev, id)).revision;
            ackDel<R>(o.block, o.idOf, id);
          }
        }
        for (const row of o.rows) {
          const id = o.idOf(row);
          const b = id ? o.base.find((x) => o.idOf(x) === id) : null;
          if (b && JSON.stringify(b) === JSON.stringify(row)) continue;
          if (id == null) {
            const made = await o.create(rev, row);
            rev = made.revision;
            ackRow<R>(o.block, (x) => x.localKey, o.withId(row, made.id));
            receipts[o.block].set(row.localKey, made.id);
          } else {
            rev = (await o.put(rev, id, row)).revision;
            ackRow<R>(o.block, (x) => x.localKey, row);
          }
        }
      };
      // 先按位插入本地新增的关（升序：后插的位置号已含前面插入的顺延）
      failPage = "level";
      for (const at of adds) {
        const lv = levels.find((x) => x.id === at);
        if (lv) { rev = (await createLevelApi(configVersion, rev, at, toNewLevel(lv))).revision; receipts.levels.push(at); }
      }
      // 建关后读回一次：服务端编号已与本地一致，再对每关做逐字段差异
      const mid = adds.length ? await fetchLevelsOf(configVersion) : null;
      if (adds.length && !mid) throw new Error("draft read-back failed");
      const diffBase = mid ? toLevels(mid.levels) : base;
      /*
       * 建关之后读回的修订号必须正好是我最后一笔建关回来的那个：多了说明这中间别人存过，
       * 读回里带着他的改动。拿它当差异基线的话，下面的逐关补丁会把他的改动改回我手上的旧值，
       * 而且修订号对得上、服务端拦不住。所以当成修订号冲突，走自动合并（merge3）后再存
       */
      if (mid && mid.revision !== rev) throw new ApiError(t("ver.autoConflict"), "60302");
      if (mid) {
        rev = mid.revision;
        // 新建的关以服务端读回的为准记进基线（建关只带了一部分字段，其余是服务端的默认值）
        const created = new Set(levels.filter((l) => !baseUids.has(l.uid)).map((l) => l.uid));
        for (const row of toLevels(mid.levels)) if (created.has(row.uid)) ackRow<Level>("levels", (l) => l.uid, row);
      }
      for (const lv of levels) {
        // 按 uid 对上服务端的那一关，补丁打到它在服务端的位置上
        const b = diffBase.find((x) => x.uid === lv.uid);
        if (!b) continue;
        const patch = diffToPatch(b, lv);
        if (patch) {
          rev = (await patchLevelApi(configVersion, b.id, rev, patch)).revision;
          /*
           * 只记这条补丁真写了的字段：竞品格在后面的竞品那一段单独写、逐格素材由素材弹窗直接写，
           * 留空的数值（B / 目标骰次 / 浮动区间）补丁不带——这些都沿用基线里原来的值
           */
          const prev = ((ab.levels as Level[]) ?? []).find((x) => x.uid === lv.uid) ?? b;
          ackRow<Level>("levels", (l) => l.uid, {
            ...lv, id: prev.id, rivals: prev.rivals, assets: prev.assets,
            B: lv.B ?? prev.B, targetRolls: lv.targetRolls ?? prev.targetRolls, rollsRange: lv.rollsRange ?? prev.rollsRange,
          });
        }
      }
      /*
       * 下面每一块各自成败：某一块被服务端按参数拒了（code 100，比如那一页有行还没填完），修订号没动，
       * 记下来接着存别的块——不然一页没填完，同一轮排在它后面的所有页都存不上，别人也看不到。
       * 撞修订号、版本状态不对、网络错一律立刻停（修订号已经不可信）。全部跑完再把被拒的那块报出来
       */
      const rejected: Array<{ page: string; e: unknown }> = [];
      const blk = async (fn: () => Promise<void>) => {
        try { await fn(); } catch (e) {
          if (e instanceof ApiError && e.code === "100") { rejected.push({ page: failPage, e }); return; }
          throw e;
        }
      };
      await blk(async () => {
        // ── 四个业务块：以 store 里的服务端事实为基准做差异（CAS 被拒时 revision 不 bump，以响应为准）
        if (commonPatch) {
          // 数值仍共用 common；失败时按变化的字段送回真正的编辑入口。
          failPage = JSON.stringify(lamp) !== JSON.stringify(baseSnap.lamp) ? "lamp-drops"
            : JSON.stringify(pettown) !== JSON.stringify(baseSnap.pettown) ? "pettown-params"
            : JSON.stringify(fight) !== JSON.stringify(baseSnap.fight) ? "global-fight"
            : ["unifiedB", "eTargetMilliB", "costRatioBps", "incomeGrowthBps", "incomeAnchorB", "targetRollsBase", "targetRollsGrowthBps"].some((key) => key in commonPatch)
              ? (selectors.buildingSystem === "pettown" ? "pettown-star" : "cost") : "global-dice";
          rev = (await patchCommonApi(configVersion, rev, commonPatch)).revision;
          ackBlock("dice", "fight", "pettown", "petStar", "intimacy", "lamp");
        }
      });
      await blk(async () => {
        if (avail.common && JSON.stringify(selectors) !== JSON.stringify(baseSnap.selectors)) {
          // 最高奖励的图只在改过时才带上：没改就不发，免得别的选择器保存时被这一项连坐
          const imgChanged = JSON.stringify(selectors.topBonusImg) !== JSON.stringify(baseSnap.selectors.topBonusImg ?? EMPTY_TOP_BONUS_IMG);
          failPage = selectors.costCurve !== baseSnap.selectors.costCurve ? "cost" : imgChanged ? "match-mode" : "init-config";
          rev = (await patchSelectorsApi(configVersion, rev, { ...selectors, topBonusImg: imgChanged ? selectors.topBonusImg : undefined })).revision;
          ackBlock("selectors");
        }
      });
      await blk(async () => {
        // 竞品列（随版本存）：先发列定义（删掉的列服务端连价一起删），再发各列改过的关卡逐格价、萌宠逐星价。
        // 列定义整表替换要在选择器之后：客户端在用的列不许删，选择器先切走服务端才放行
        if (avail.rivals) {
          const defsChanged = JSON.stringify(rivals.map((r) => [r.id, r.name])) !== JSON.stringify((baseSnap.rivals ?? []).map((r) => [r.id, r.name]));
          if (defsChanged) {
            failPage = "cost";
            rev = (await putRivalsApi(configVersion, rev, rivals.map((r) => ({ rivalId: r.id, name: r.name })))).revision;
            ackBlock("rivals");
          }
          for (const r of rivals) {
            const changed = changedRivalLevels(base, levels, r.id);
            if (changed.length) {
              failPage = "building";
              const cells = levels.filter((lv) => changed.includes(lv.id)).flatMap((lv) => rivalCellsOf(lv, r.id));
              rev = (await putRivalCellsApi(configVersion, rev, r.id, changed, cells)).revision;
              // 这一家竞品在这几关的格子已经写进去了
              ab.levels = ((ab.levels as Level[]) ?? []).map((l) => {
                const mine = levels.find((x) => x.uid === l.uid);
                return mine && changed.includes(mine.id) ? { ...l, rivals: { ...l.rivals, [r.id]: mine.rivals[r.id] } } : l;
              });
            }
            const prices = pettownRivalPrices[r.id] ?? {};
            if (JSON.stringify(prices) !== JSON.stringify((baseSnap.pettownRivalPrices ?? {})[r.id] ?? {})) {
              failPage = "pettown-star";
              rev = (await putPettownRivalPricesApi(configVersion, rev, r.id,
                Object.entries(prices).map(([star, price]) => ({ star: Number(star), price })).filter((x) => (toBig(x.price) ?? 0n) > 0n))).revision;
              ab.pettownRivalPrices = { ...((ab.pettownRivalPrices as Record<string, unknown>) ?? {}), [r.id]: prices };
            }
          }
        }
      });
      await blk(async () => {
        // 萌宠小镇两张表：整表替换，改了才发（同 spin 盘面）。星级表按星存，总星数看 common 的累计星数上限
        failPage = "pettown-star";
        if (avail.pettownStars && JSON.stringify(starsOut) !== JSON.stringify(baseSnap.pettownStars)) {
          rev = (await putPettownStarsApi(configVersion, rev, starsForSave(starsOut, curEcon.model))).revision;
          ab.pettownStars = starsOut;
        }
      });
      await blk(async () => {
        failPage = "pettown-slot";
        if (avail.pettownSlots && JSON.stringify(pettownSlots) !== JSON.stringify(baseSnap.pettownSlots)) {
          rev = (await putPettownSlotsApi(configVersion, rev, pettownSlots)).revision; ackBlock("pettownSlots");
        }
      });
      await blk(async () => {
        // 天赋节点：整表替换。写接口只核节点自身；「第一级要比体力树 / 盾牌 / 好友的起点更好」在发布闸对整版核
        failPage = selectors.buildingSystem === "levels" ? "level-talent" : "pettown-talent";
        if (avail.talentNodes && JSON.stringify(talentNodes) !== JSON.stringify(baseSnap.talentNodes ?? [])) {
          rev = (await putTalentNodesApi(configVersion, rev, talentNodes)).revision; ackBlock("talentNodes");
        }
      });
      await blk(async () => {
        // 多人对战三张表：整表替换、改了才发。
        failPage = "match-mode";
        if (avail.matchModes && JSON.stringify(matchModes) !== JSON.stringify(baseSnap.matchModes)) {
          rev = (await putMatchModesApi(configVersion, rev, matchModes)).revision; ackBlock("matchModes");
        }
      });
      // 战阶与道具卡互相引用，服务端两个 PUT 各自拿**已落库**的另一张表校验：
      // 卡表指向不存在的阶会拒、删掉还有卡指着的阶也会拒。所以顺序看阶数增减——
      // 加阶：先发战阶（卡才指得到新阶）；删阶：先发卡（把指着末阶的卡改掉），再删阶。
      // 阶号连号，一次保存里不会又加又删。
      const stagesChanged = avail.battleStages && JSON.stringify(battleStages) !== JSON.stringify(baseSnap.battleStages);
      const cardsChanged = avail.abilityCards && JSON.stringify(abilityCards) !== JSON.stringify(baseSnap.abilityCards);
      const putStages = async () => { failPage = "battle-stage"; rev = (await putBattleStagesApi(configVersion, rev, battleStages)).revision; ackBlock("battleStages"); };
      const putCards = async () => { failPage = "ability-card"; rev = (await putAbilityCardsApi(configVersion, rev, abilityCards)).revision; ackBlock("abilityCards"); };
      const stagesShrank = battleStages.length < baseSnap.battleStages.length;
      if (stagesShrank) { if (cardsChanged) await blk(putCards); if (stagesChanged) await blk(putStages); }
      else { if (stagesChanged) await blk(putStages); if (cardsChanged) await blk(putCards); }
      await blk(async () => {
        failPage = "reward";
        if (avail.rewards) {
          for (const r of rewards) {
            const b = baseSnap.rewards.find((x) => x.id === r.id);
            if (!b || JSON.stringify(b) !== JSON.stringify(r)) { rev = (await putRewardApi(configVersion, rev, toServerReward(r))).revision; ackRow<Reward>("rewards", (x) => x.id, r); }
          }
          for (const b of baseSnap.rewards) {
            if (!rewards.some((r) => r.id === b.id)) { rev = (await deleteRewardApi(configVersion, rev, b.id)).revision; ackDel<Reward>("rewards", (x) => x.id, b.id); }
          }
        }
      });
      await blk(async () => {
        failPage = "card-item";
        if (avail.cards) {
          const syncPacks = async (nextP: CardPack[], baseP: CardPack[], kind: "item" | "idol") => {
            for (const p of nextP) {
              const b = baseP.find((x) => x.id === p.id);
              if (!b || JSON.stringify(b) !== JSON.stringify(p)) { rev = (await putPackApi(configVersion, rev, toServerPack(p, kind))).revision; ackRow<CardPack>(kind === "item" ? "itemPacks" : "idolPacks", (x) => x.id, p); }
            }
            // 被 idol 引用的物品卡包服务端会拒删（60301）——错误直接抛给 toast，别静默吞
            for (const b of baseP) {
              if (!nextP.some((p) => p.id === b.id)) { rev = (await deletePackApi(configVersion, rev, b.id)).revision; ackDel<CardPack>(kind === "item" ? "itemPacks" : "idolPacks", (x) => x.id, b.id); }
            }
          };
          await syncPacks(itemPacks, baseSnap.itemPacks, "item");
          await syncPacks(idolPacks, baseSnap.idolPacks, "idol");
        }
      });
      await blk(async () => {
        failPage = "auction";
        if (avail.auctions) {
          // 「全部关卡开新一期」是运营主操作：识别出来就走一次性的 new-period，别打 50 个 PUT
          const bulk = detectBulkAppend(baseSnap.auctions, auctions, levels);
          if (bulk) { rev = (await newAuctionPeriodApi(configVersion, rev, bulk)).revision; ackBlock("auctions"); }
          else {
            for (const l of levels) {
              const bp = baseSnap.auctions[l.id] ?? [], np = auctions[l.id] ?? [];
              if (JSON.stringify(bp) !== JSON.stringify(np)) {
                rev = (await putAuctionLevelApi(configVersion, rev, l.id, toServerPeriods(np))).revision;
                ab.auctions = { ...((ab.auctions as AuctionMap) ?? {}), [l.id]: np };
              }
            }
          }
        }
      });
      await blk(async () => {
        // 副玩法格逐格 PUT。只发改过的：整表推一遍会把没动过的格子也算成一次修改，
        // 审计日志里看不出谁真的改了什么。
        failPage = "spin";
        if (avail.tiles) {
          for (const tile of tiles) {
            const before = baseSnap.tiles.find((x) => x.id === tile.id);
            if (before && JSON.stringify(before) === JSON.stringify(tile)) continue;
            const tileBase = serverTilesByVerRef.current[`${agent ?? ""}|${configVersion}`]?.tiles ?? serverTilesRef.current;
            const body = toServerTile(tile, tileBase[tile.id] ?? null);
            rev = (await putTileApi(configVersion, rev, tile.id, body)).revision;
            ackRow<Tile>("tiles", (x) => x.id, tile);
          }
        }
      });
      await blk(async () => {
        // spin 整盘替换：改了才发，没改不发（避免每次保存都在审计里留一条无意义的整盘覆盖）
        failPage = "spin";
        if (avail.spins && JSON.stringify(spins) !== JSON.stringify(baseSnap.spins)) {
          rev = (await putSpinApi(configVersion, rev, spins.map(toServerSpin))).revision; ackBlock("spins");
        }
      });
      await blk(async () => {
        failPage = "puzzle-list";
        if (avail.puzzles) {
          // 价目表先发：拼图行的发布闸要求每个 seq 在价目表里有对应行，
          // 反过来发的话中间态是「新拼图指向还不存在的档位」
          if (JSON.stringify(puzzlePrices) !== JSON.stringify(baseSnap.puzzlePrices)) {
            rev = (await putPuzzlePricesApi(configVersion, rev, puzzlePrices)).revision;
            ackBlock("puzzlePrices");
          }
          // **先删后加。** 反过来的话，「删掉旧 ID、用新 ID 接管同一个 idol/seq」
          // 这种很正常的操作会在 PUT 时先撞 uk_idol_seq —— 而那一步的报错
          // 说的是「序号重复」，看不出真正的原因是旧行还没删
          for (const b of baseSnap.puzzles) {
            // 只比已持久化的行；新建行没有服务端 ID，本来就不可能出现在 baseline 里
            if (b.puzzleId && !puzzles.some((p) => p.puzzleId === b.puzzleId)) {
              rev = (await deletePuzzleApi(configVersion, rev, b.puzzleId)).revision;
              ackDel<Puzzle>("puzzles", (x) => x.puzzleId, b.puzzleId);
            }
          }
          /*
           * 逐行提交，只发改过的——整表推一遍会让审计日志看不出谁真的改了什么。
           *
           * **新建走 POST、已有走 PUT**，按 `puzzleId == null` 分流。ID 由服务端分配：
           * 它要跨所有版本取最大、还要算上 user_puzzle（发布过或兑过的号永不复用），
           * 前端看不到这两样，算不对。上一版这里对新行也发 PUT，把前端的本地占位 ID
           * 直接写进了库，而 PUT 当时是 upsert、照单全收。
           */
          for (const p of puzzles) {
            const b = p.puzzleId ? baseSnap.puzzles.find((x) => x.puzzleId === p.puzzleId) : null;
            if (b && JSON.stringify(b) === JSON.stringify(p)) continue;
            if (p.puzzleId == null) {
              /*
               * 返回的 puzzleId **刻意不回写本地 state**：保存结束后读回服务端快照，
               * 建成的 id 记进回执，逐行套回本地（lib/autosave.ts applyReceipts）。
               */
              const made = await createPuzzleApi(configVersion, rev, toServerPuzzleBody(p));
              rev = made.revision;
              ackRow<Puzzle>("puzzles", (x) => x.localKey, { ...p, puzzleId: made.puzzleId, seq: made.seq });
              receipts.puzzles.set(p.localKey, { puzzleId: made.puzzleId, seq: made.seq });
            } else {
              rev = (await putPuzzleApi(configVersion, rev, p.puzzleId, toServerPuzzleBody(p))).revision;
              ackRow<Puzzle>("puzzles", (x) => x.localKey, p);
            }
          }
        }
      });
      await blk(async () => {
        // idol 关卡相册：排在拼图之后——照片只能挂在已落库的拼图下（页面只列有 ID 的拼图）
        failPage = "idol-photos";
        if (avail.idolPhotos && JSON.stringify(idolPhotos) !== JSON.stringify(baseSnap.idolPhotos ?? [])) {
          await saveAssignedRows<IdolPhoto>({
            block: "idolPhotos", rows: idolPhotos, base: baseSnap.idolPhotos ?? [],
            idOf: (x) => x.photoId, withId: (x, id) => ({ ...x, photoId: id }),
            create: async (r, x) => { const m = await createIdolPhotoApi(configVersion, r, toServerIdolPhotoBody(x)); return { revision: m.revision, id: m.photoId }; },
            put: (r, id, x) => putIdolPhotoApi(configVersion, r, id, toServerIdolPhotoBody(x)),
            del: (r, id) => deleteIdolPhotoApi(configVersion, r, id),
          });
        }
      });
      await blk(async () => {
        failPage = "idol-vip-photos";
        if (avail.idolVipPhotos && JSON.stringify(idolVipPhotos) !== JSON.stringify(baseSnap.idolVipPhotos ?? [])) {
          await saveAssignedRows<IdolVipPhoto>({
            block: "idolVipPhotos", rows: idolVipPhotos, base: baseSnap.idolVipPhotos ?? [],
            idOf: (x) => x.vipPhotoId, withId: (x, id) => ({ ...x, vipPhotoId: id }),
            create: async (r, x) => { const m = await createIdolVipPhotoApi(configVersion, r, toServerIdolVipPhotoBody(x)); return { revision: m.revision, id: m.vipPhotoId }; },
            put: (r, id, x) => putIdolVipPhotoApi(configVersion, r, id, toServerIdolVipPhotoBody(x)),
            del: (r, id) => deleteIdolVipPhotoApi(configVersion, r, id),
          });
        }
      });
      await blk(async () => {
        failPage = "idol-vip-videos";
        if (avail.idolVipVideos && JSON.stringify(idolVipVideos) !== JSON.stringify(baseSnap.idolVipVideos ?? [])) {
          await saveAssignedRows<IdolVipVideo>({
            block: "idolVipVideos", rows: idolVipVideos, base: baseSnap.idolVipVideos ?? [],
            idOf: (x) => x.vipVideoId, withId: (x, id) => ({ ...x, vipVideoId: id }),
            create: async (r, x) => { const m = await createIdolVipVideoApi(configVersion, r, toServerIdolVipVideoBody(x)); return { revision: m.revision, id: m.vipVideoId }; },
            put: (r, id, x) => putIdolVipVideoApi(configVersion, r, id, toServerIdolVipVideoBody(x)),
            del: (r, id) => deleteIdolVipVideoApi(configVersion, r, id),
          });
        }
      });
      await blk(async () => {
        /*
         * 关卡多图（改版）：先关卡、后图片。
         * - 删关：服务端同事务连带删掉它的图，回包给 deletedImageIds——这些图从图片基线里摘掉、ackDel，不再逐张 DELETE（会 404）；
         * - 本地新建的关建成后拿到 st_ 号（回执），它名下本地新建的图此时还写着关的 localKey，建图前用 resolveStageId 换成真号；
         *   换不出来（那一关这一轮没建成）的图本轮跳过，留在本地等下一轮。
         */
        failPage = "stage-images";
        if (!avail.stages) return;
        const baseStages = baseSnap.stages ?? [];
        const baseImages = baseSnap.stageImages ?? [];
        if (JSON.stringify(stages) === JSON.stringify(baseStages) && JSON.stringify(stageImages) === JSON.stringify(baseImages)) return;
        const gone = new Set<string>();
        for (const b of baseStages) {
          if (b.stageId && !stages.some((x) => x.stageId === b.stageId)) {
            const res = await deleteStageApi(configVersion, rev, b.stageId);
            rev = res.revision;
            ackDel<Stage>("stages", (x) => x.stageId, b.stageId);
            for (const id of res.deletedImageIds ?? []) { gone.add(id); ackDel<StageImage>("stageImages", (x) => x.imageId, id); }
          }
        }
        await saveAssignedRows<Stage>({
          block: "stages", rows: stages, base: baseStages.filter((b) => b.stageId == null || stages.some((x) => x.stageId === b.stageId)),
          idOf: (x) => x.stageId, withId: (x, id) => ({ ...x, stageId: id }),
          create: async (r, x) => { const m = await createStageApi(configVersion, r, toServerStageBody(x)); return { revision: m.revision, id: m.stageId }; },
          put: (r, id, x) => putStageApi(configVersion, r, id, toServerStageBody(x)),
          del: (r, id) => deleteStageApi(configVersion, r, id),
        });
        const known = stages.map((x) => (x.stageId == null && receipts.stages.has(x.localKey) ? { ...x, stageId: receipts.stages.get(x.localKey)! } : x));
        const sid = (x: StageImage) => resolveStageId(x, known);
        await saveAssignedRows<StageImage>({
          block: "stageImages",
          rows: stageImages.filter((x) => sid(x) != null || x.imageId != null),
          base: baseImages.filter((x) => !x.imageId || !gone.has(x.imageId)),
          idOf: (x) => x.imageId, withId: (x, id) => ({ ...x, imageId: id }),
          create: async (r, x) => { const m = await createStageImageApi(configVersion, r, toServerStageImageBody(x, sid(x)!)); return { revision: m.revision, id: m.imageId }; },
          put: (r, id, x) => putStageImageApi(configVersion, r, id, toServerStageImageBody(x, sid(x) ?? x.stageId)),
          del: (r, id) => deleteStageImageApi(configVersion, r, id),
        });
      });
      await blk(async () => {
        // 每日奖励整表替换：改了才发（同 spin 的理由——别在审计里留无意义的整表覆盖）
        failPage = "daily-reward";
        if (avail.daily && JSON.stringify(dailyRewards) !== JSON.stringify(baseSnap.dailyRewards)) {
          rev = (await putDailyRewardsApi(configVersion, rev, dailyRewards)).revision; ackBlock("dailyRewards");
        }
      });
      await blk(async () => {
        // 两张社交表同样整表替换、同样改了才发。分两次 PUT 而不是合成一次：
        // 它们在服务端就是两个接口、两次 CAS，合起来只会让审计日志分不清改的是哪张
        failPage = "social-channel";
        if (avail.socialChannels && JSON.stringify(socialChannels) !== JSON.stringify(baseSnap.socialChannels)) {
          rev = (await putSocialChannelsApi(configVersion, rev, socialChannels)).revision; ackBlock("socialChannels");
        }
      });
      await blk(async () => {
        failPage = "social-event-type";
        if (avail.socialEventTypes && JSON.stringify(socialEventTypes) !== JSON.stringify(baseSnap.socialEventTypes)) {
          rev = (await putSocialEventTypesApi(configVersion, rev, socialEventTypes)).revision; ackBlock("socialEventTypes");
        }
      });
      await blk(async () => {
        failPage = "frame";
        if (avail.frames && JSON.stringify(frames) !== JSON.stringify(baseSnap.frames)) {
          rev = (await putFramesApi(configVersion, rev, frames)).revision; ackBlock("frames");
        }
      });
      await blk(async () => {
        failPage = "board-cells";
        if (avail.boardCells && JSON.stringify(boardCells) !== JSON.stringify(baseSnap.boardCells)) {
          rev = (await putBoardCellsApi(configVersion, rev, boardCells)).revision; ackBlock("boardCells");
        }
      });
      await blk(async () => {
        failPage = "lamp-drops";
        if (avail.lampDrops && JSON.stringify(lampDrops) !== JSON.stringify(baseSnap.lampDrops ?? [])) {
          rev = (await putLampDropsApi(configVersion, rev, lampDrops)).revision; ackBlock("lampDrops");
        }
      });
      await blk(async () => {
        failPage = "idol-intimacy";
        if (avail.intimacy && (JSON.stringify(intimacyLevels) !== JSON.stringify(baseSnap.intimacyLevels ?? [])
          || JSON.stringify(intimacySources) !== JSON.stringify(baseSnap.intimacySources ?? []))) {
          rev = (await putIntimacyApi(configVersion, rev, intimacyLevels, intimacySources)).revision; ackBlock("intimacyLevels", "intimacySources");
        }
      });
      await blk(async () => {
        failPage = "pet-list";
        if (avail.pets) {
          // **先删后加**，同拼图：反过来的话「删掉旧编号、用新编号接管」会在 POST 时先撞主键，
          // 而那一步的报错说的是「编号已被占用」，看不出真正的原因是旧行还没删
          for (const b of baseSnap.pets) {
            if (b.persisted && !pets.some((p) => p.persisted && p.petId === b.petId)) {
              rev = (await deletePetApi(configVersion, rev, b.petId)).revision;
              ackDel<Pet>("pets", (x) => x.petId, b.petId);
            }
          }
          /*
           * 逐行提交，只发改过的——整表推一遍会让审计日志看不出谁真的改了什么。
           *
           * **新建走 POST、已有走 PUT**，按 persisted 分流（编号是运营手填的，所以
           * 新建也带编号，撞号由服务端报错而不是静默覆盖）。返回的东西刻意不回写本地
           * state：保存结束后读回服务端快照，建成的编号记进回执，逐行标成已落库。
           */
          for (const p of pets) {
            const body = toServerPet(p);
            const b = p.persisted ? baseSnap.pets.find((x) => x.petId === p.petId) : null;
            if (b && JSON.stringify(toServerPet(b)) === JSON.stringify(body)) continue;
            if (p.persisted) rev = (await putPetApi(configVersion, rev, body.petId, body)).revision;
            else { rev = (await createPetApi(configVersion, rev, body)).revision; receipts.pets.add(body.petId); }
            ackRow<Pet>("pets", (x) => x.petId, { ...p, persisted: true });
          }
        }
      });
      await blk(async () => {
        /*
         * 领养池排在角色之后：成员引用的角色在不在名册、是不是启用，现在由发布闸查（草稿照存），
         * 顺序不再影响能不能存；留着这个顺序，读回时名册与池子是同一轮写完的样子
         */
        if (avail.adoptPools && JSON.stringify(adoptPools) !== JSON.stringify(baseSnap.adoptPools)) {
          /*
           * 领养池配置在两个玩法格页里，没有「领养池页」可跳了。
           * 所以按**哪个点的那一行真的变了**算落点；两个点都变了就取第一个
           * （改动多于一处时任选其一都对，重要的是别送去一个不存在的页——
           * setPage 到一个不存在的 id 会渲染成空白，比不跳更糟）。
           */
          // 落点 = 哪个点的那一行真的变了（判据与用例都在 lib/adoptPool.ts）
          failPage = `subgame-adopt_${adoptFailPoint(adoptPools, baseSnap.adoptPools as typeof adoptPools)}`;
          rev = (await putAdoptPoolsApi(configVersion, rev, adoptPools)).revision; ackBlock("adoptPools");
        }
      });
      await blk(async () => {
        if (avail.petStars && JSON.stringify(petStars) !== JSON.stringify(baseSnap.petStars)) {
          failPage = "pet-star";
          rev = (await putPetStarsApi(configVersion, rev, petStars)).revision; ackBlock("petStars");
        }
      });
      if (rejected.length) {
        // 别的块已经写进去了：照常记修改记录、告诉同一版上的人，再走失败对账（读回 + 合并），被拒那页标红
        if (rev !== curRev) {
          const done = ab as unknown as Snapshot;
          collabAnnounceRef.current(rev, (Object.keys(done) as Array<keyof Snapshot>).filter((k) => JSON.stringify(done[k]) !== JSON.stringify(baseSnap[k])).join(","));
        }
        failPage = rejected[0].page;
        throw rejected.length > 1 ? new ApiError(rejected.map((r) => (r.e instanceof Error ? r.e.message : String(r.e))).join("；"), "100") : rejected[0].e;
      }
      const [readBack, rows] = await Promise.all([loadSnapshot(configVersion), fetchVersions()]);
      if (!readBack) throw new Error("saved but read-back failed");
      const id = vid(configVersion);
      // 切了代理：这些都是上一个代理的东西，一样都不能进现在的缓存
      if (!editingKeyRef.current.startsWith(`${agent ?? ""}|`)) return;
      setVersions(rows.map(toVersion));
      // 缺块的读回不进缓存：下一轮拿缓存当差异基线，占位的空数组会让删除一条都发不出去
      /*
       * 读回缺块：那几块是占位的空值，不能当成「已保存的事实」进缓存（下一轮拿缓存当差异基线，
       * 占位的空数组会让删除一条都发不出去），也不能把它们标成不可写。
       * 写全部成功了，所以服务端此刻就是这次发出去的那份（套上建行回执）——拿它当缓存和基线。
       * 关卡块读回总在（缺了整个快照就是 null），用服务端的。
       */
      const lost = missingBlocks(readBack.avail);
      /*
       * 读回缺块、而且修订号比我最后一笔写回来的新：这中间别人也写过，缺的那几块就不能拿「我发出去的」顶——
       * 那不再是服务端的样子。这次什么都不收（基线和修订号留旧的），等下一轮：写的时候会撞修订号，
       * 走完整读回 + 合并；读回一直缺块就停在冲突上等人处理
       */
      if (lost.length && readBack.revision !== rev) {
        if (editingKeyRef.current !== key) return;
        // 写都成功了，只是对不上账：留住已写成功的部分（下一轮与撞修订号后的合并都用它），建成的行先拿回 id
        pendingBaseRef.current = applyReceipts(ab as unknown as Snapshot, receipts);
        setBaseline(JSON.stringify(pendingBaseRef.current));
        applySnap(cloneSnap(applyReceipts(liveSnapRef.current ?? sent, receipts)));
        setSaveState((prev) => ({ state: "error", msg: t("ver.autoReadBackPartial", { blocks: lost.join(", ") }), retry: true, at: "", attempt: prev.state === "error" ? prev.attempt + 1 : 1 }));
        return;
      }
      /*
       * 只有没取到的那几块用「这次发出去的」顶上（写都成功了，服务端那几块就是它）；
       * 取到了的块一律用读回的——里面可能有别人在我最后一笔之后新存的行，丢了的话下一轮整表写回会把它删掉
       */
      const sentR = applyReceipts(sent, receipts);
      const server: Snapshot = { ...readBack.snap };
      for (const flag of lost) for (const k of BLOCK_KEYS[flag]) (server as unknown as Record<string, unknown>)[k] = (sentR as unknown as Record<string, unknown>)[k];
      setStore((s) => ({ ...s, [id]: server }));
      setRevs((r) => ({ ...r, [id]: readBack.revision }));
      // 同一代理切了版本：缓存照更新（切回来时修订号是对的），界面不动
      if (editingKeyRef.current !== key) return;
      if (lost.length) setToast(t("ver.autoReadBackPartial", { blocks: lost.join(", ") }), true);
      // 没取到的块不标成不可写（它刚刚还写成功了），其余照读回的
      setAvail((a) => ({ ...readBack.avail, ...Object.fromEntries(lost.map((k) => [k, a[k]])) }));
      {
        /*
         * 字段级三方合并：base = 这次发出去的（套上建行回执），mine = 此刻手上的，theirs = 读回的。
         * 保存期间我又改的字段留着；读回里别人在我最后一笔之后存进去的字段也收下——
         * 按块整块留本地的话，同一块里别人的改动会被下一轮自动保存改回去
         */
        const m0 = merge3(applyReceipts(sent, receipts), applyReceipts(liveSnapRef.current ?? sent, receipts), server) as Snapshot;
        const merged: Snapshot = { ...m0, levels: renumber(m0.levels) };
        followLevels(merged.levels); applySnap(cloneSnap(merged));
        setBaseline(JSON.stringify(server)); pendingBaseRef.current = null; pendingSentRef.current = [];
        settleAdds(receipts);
        const row = rows.find((r) => r.configVersion === configVersion);
        setSaveState({ state: "saved", atMs: row?.updatedAtMs ?? Date.now(), by: row?.updatedBy ?? "" });
        setSaveError(null);
        // 修改记录由服务端随各块的配置事务提交，浏览器不再重复上报。
        // 告诉同一版上的其他人：我存到了第几号、改了哪几块
        collabAnnounceRef.current(readBack.revision, (Object.keys(sent) as Array<keyof Snapshot>).filter((k) => JSON.stringify(sent[k]) !== JSON.stringify(baseSnap[k])).join(","));
      }
    } catch (e) {
      if (editingKeyRef.current !== key) return;
      await autoSaveFailed(e, e instanceof Error ? e.message : String(e), failPage, receipts, key, ab as unknown as Snapshot, sent);
    }
  };
  /**
   * 发布 + 激活。**两段必须分开报错。**
   *
   * 激活现在也会按此刻的版本外事实（idol 名册、授权批次）重验一次，
   * 所以它同样可能返回结构化 422。用一个 catch 兜着的话，
   * 「发布成功、激活被拒」会被显示成「发布被拒 · 请修改后重新发布」——
   * 而那一版已经是不可变的 published 了，改不动，人会一直卡在那句提示上。
   */
  /** 最近一次发布 / 设为生效选的生效方式与版本：激活被拒后「重试」沿用它，从横幅重开「设为生效」也默认选它 */
  const lastRefreshModeRef = useRef<RefreshMode>("normal");
  const lastRefreshVidRef = useRef<string | null>(null);
  /** 发布弹窗里填的备注；undefined = 不动草稿原有的 */
  const publishNoteRef = useRef<string | undefined>(undefined);
  /** keepDraftOnto：冲突弹窗里选了「保留草稿的值」，带上那份冲突是和线上哪一版比的 */
  const doPublishServer = async (mode: RefreshMode, keepDraftOnto?: number) => {
    const v = vnum(curVid);
    const oldOnline = online?.id ?? "-";
    const vidNow = curVid;
    // ── 第一段：发布 ──
    let merged: PublishMerge | null = null;
    let activeAtPublish: number | null | undefined;
    try {
      const out = await publishVersionApi(v, revs[vidNow] ?? 0, keepDraftOnto, publishNoteRef.current);
      merged = out.merged ?? null;
      activeAtPublish = out.activeAtPublish;
    } catch (e) {
      // 发布校验失败（60303/422）带结构化 errors：渲染成可定位的清单，别只吐一句 message
      const rows = validationErrorsOf(e);
      if (rows) setVerUi({ kind: "pubErrors", rows });
      else if (mergeConflictsOf(e)) setVerUi({ kind: "mergeConflicts", message: e instanceof Error ? e.message : String(e), mode, onto: mergeConflictsOf(e)!.onto });
      else setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
      /*
       * 服务端发布是「先冻结（revision + 1）再校验」，失败只解冻不回退 revision
       * （config.ts publishVersion 3a：写方重读即可）。这里不重读的话，手里的 revision
       * 从此落后一号，改完再点发布必撞「修订号冲突」，只有整页刷新才能救——
       * 2026-09-09 在 12491249 上就是这么卡住的。合并冲突、口径不同这类非校验失败同样冻结过。
       *
       * 而且校验没过时服务端可能已经把线上的改动合进了草稿（合并结果留在草稿里），所以按「载入最新」重读：
       * 内容与修订号一起从服务端来，手上没存的改动按三方合并留着。不能只把修订号推上去——那样工作副本还是
       * 合并之前的，接着改再自动保存，会把刚合进来的线上改动写回去。重读失败就让修订号停在旧值：
       * 下一次保存撞冲突，走正常的「载入最新」
       */
      try {
        await reloadDraft({ silent: true });
      } catch { /* 重读失败：下次发布会报冲突，刷新页面即可，不额外打扰 */ }
      return;
    }
    // 合并过的话，眼前和缓存里这一版都是合并前的内容，重读成真正发布出去的那份
    if (merged) {
      await reloadDraft({ silent: true }).catch(() => null);
      setToast(t("ver.mergedOnPublish", { online: vid(merged.onto), n: merged.rows, id: vidNow, base: vid(merged.from) }));
    }
    // ── 第二段：激活。到这里版本已经 published、不可变，失败不能再说「去改内容」 ──
    // 带上发布那一刻的线上版本：这中间别人换了一版上线，就不自动生效（那一版的改动没合进来）
    await activateAfterPublish(v, curVid, oldOnline, mode, "publish", activeAtPublish);
  };

  /**
   * 激活 + 收尾刷新。**三段各自归因，不能共用一个 catch：**
   *
   * - 激活失败 → 版本停在 published，界面要如实显示「已发布未生效」；
   * - 激活成功但刷新失败 → 服务端**已经生效了**，不能报成「激活失败」，
   *   否则人会去重试一个已经成功的动作，而界面还停在 draft 上。
   *
   * 刷新本身也要 catch：它扔出去的话会经 runExclusive 的裸 finally 变成
   * 未处理拒绝，界面继续按草稿渲染 —— 与服务端状态正好相反。
   */
  const activateAfterPublish = async (v: number, vid: string, oldOnline: string, mode: RefreshMode, via: "publish" | "activate", expectedActive?: number | null) => {
    lastRefreshModeRef.current = mode;
    lastRefreshVidRef.current = vid;
    let activated = false;
    try {
      await activateVersionApi(v, mode, expectedActive);
      activated = true;
    } catch (e) {
      const rows = validationErrorsOf(e);
      if (rows) setVerUi({ kind: "actErrors", rows, v: vid });
      else { setVerUi(null); setToast(t("ver.activateFail", { id: vid, msg: e instanceof Error ? e.message : String(e) }), true); }
    }
    // 无论激活成没成，都要把真实状态拉回来——这是界面与服务端唯一的对齐点
    try {
      const rows = await fetchVersions();
      setVersions(rows.map(toVersion));
    } catch {
      setToast(t("ver.refreshFail"), true);
    }
    if (!activated) return;
    setVerUi(null);
    logAction("config.verPublish", vid, { replaced: oldOnline, refresh_mode: mode }, agent ?? undefined);
    setToast(t(via === "publish" ? "ver.publishedAs" : "ver.activatedAs", { id: vid, old: oldOnline, mode: t(refreshModeLabel(mode)) }));
  };

  /** 只激活（版本已发布，不需要也不可能再发布一次）。「设为生效」弹窗与激活失败抽屉的重试都用它 */
  const doActivateOnly = (vid: string, mode: RefreshMode) =>
    runExclusive(() => activateAfterPublish(vnum(vid), vid, online?.id ?? "-", mode, "activate"));

  /*
   * 演示模式（后端不可达/该租户无版本）里**没有任何假装成功的写操作**。
   * 之前保存/发布在本地内存里走完并弹「已发布并设为生效」——运营会当真。
   * 现在演示模式下保存/发布/回滚一律拒绝并提示，页面顶部有常驻横幅说明。
   */
  /** 服务端写操作统一入口：进行中一律拒绝再次进入 */
  const runExclusive = (op: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    void op().finally(() => { busyRef.current = false; setBusy(false); });
  };
  /**
   * 「配置检查」：只跑一遍发布校验，不发布。
   *
   * 走的是发布那条路上的同一份规则（服务端 checkVersion），不是前端另写的近似版——
   * 前端那套 validate.ts 只管关卡造价，两边口径不一致已经咬过一次。
   * 结果复用发布被拒那张表：位置 → 页面 · 字段、点行跳转。
   */
  /*
   * 「还差 N 项才能发布」（设计稿 nav.jsx PendingChip）：保存不再拦「还没填完」的内容（草稿照存、发布闸拦），
   * 所以每次修订号变了（自己存成了、合进了别人存的）停 3 秒后自动跑一遍配置检查；同一版最多 15 秒一次，
   * 页面在后台不跑。结果记在「代理|版本」名下，切走了就不显示旧的
   */
  const [pending, setPending] = useState<{ key: string; rows: ValidationErrorRow[] } | null>(null);
  const pendingAtRef = useRef<{ key: string; at: number }>({ key: "", at: 0 });
  const curRev = revs[curVid];
  const pendingOn = !!agent && !!curVer && curVer.status === "draft" && editable && snapLoaded;
  useEffect(() => {
    if (!pendingOn || curRev == null) return;
    const key = editingKeyRef.current;
    const last = pendingAtRef.current.key === key ? pendingAtRef.current.at : 0;
    const wait = Math.max(3_000, last + 15_000 - Date.now());
    const timer = setTimeout(() => {
      if (document.visibilityState === "hidden" || editingKeyRef.current !== key) return;
      pendingAtRef.current = { key, at: Date.now() };
      checkVersionApi(vnum(curVid)).then((r) => {
        if (editingKeyRef.current !== key) return;
        setPending({ key, rows: (r.issues ?? []).map((x) => ({
          path: String(x.path), rule: String(x.rule), actual: x.actual == null ? "" : String(x.actual), expected: String(x.expected),
          ...(x.severity === "warn" ? { severity: "warn" as const } : {}),
        })) });
      }).catch(() => { /* 检查失败不打扰：下一次修订号变了再试，手动「配置检查」照常可用 */ });
    }, wait);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOn, curVid, curRev, agent]);

  const doCheckServer = () =>
    runExclusive(async () => {
      try {
        const key = editingKeyRef.current;
        const r = await checkVersionApi(vnum(curVid));
        const rows = (r.issues ?? []).map((x) => ({
          path: String(x.path), rule: String(x.rule),
          actual: x.actual == null ? "" : String(x.actual), expected: String(x.expected),
        }));
        if (editingKeyRef.current === key) setPending({ key, rows });
        if (!rows.length) { setToast(t("ver.checkOk", { id: curVid })); return; }
        setVerUi({ kind: "pubErrors", rows, from: "check" });
      } catch (e) {
        setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
      }
    });

  /**
   * 实时保存失败。
   *
   * 写到一半失败时服务端已经落了前面几块，本地基线却还是旧的——不重新对齐的话，
   * 下一轮会拿过期的修订号去写（撞冲突），或者把已经建好的新行再建一遍。
   * 所以先读回服务端，再按块合并：本地没存上的改动一律留着，只有「会建新行、且服务端
   * 已经多出东西」的块换成服务端的。
   */
  /** ackBase：发起保存时的服务端 + 这一轮每个成功请求写进去的块 / 行（doSaveServer 里逐请求记的） */
  const autoSaveFailed = async (e: unknown, msg: string, failPage: string, receipts: CreateReceipts, key: string, ackBase: Snapshot, sent: Snapshot) => {
    // 先留住「已写成功的部分」：读回失败、读回缺块、撞修订号都得靠它对账，直到一次完整读回合并成功才清掉。
    // 「有没有没存的」也按它判：改回原值的输入相对它是改动，得接着存、关页要拦
    pendingBaseRef.current = applyReceipts(ackBase, receipts);
    pendingSentRef.current = [...pendingSentRef.current, applyReceipts(sent, receipts)];
    setBaseline(JSON.stringify(pendingBaseRef.current));
    let reconciled = false;
    // 写到一半失败，前面建成的行已经在服务端了：先把回执套回本地，冲突也一样——
    // 否则「载入最新」后本地那几行还是没 id，下一轮再建一遍
    const withReceipts = applyReceipts(liveSnapRef.current ?? takeSnap(), receipts);
    applySnap(cloneSnap(withReceipts));
    settleAdds(receipts);
    const code = e instanceof ApiError ? e.code : "";
    // 修订号对不上 = 别人先存了：自动把他的块合进来、跟上修订号，接着存（自己的块留着）。
    // 合不进来（读不到）才停下来等人点「载入最新」
    if (code === "60302") {
      const r = await reloadRef.current({ silent: true });
      if (r) return;
    }
    if (code === "60301" || code === "60302") {
      // 修订号对不上 / 不再是草稿：刷新版本列表，右上角显示是谁什么时候改的
      void fetchVersions().then((rows) => setVersions(rows.map(toVersion))).catch(() => {});
      setSaveState({ state: "conflict", msg });
      return;
    }
    // 本地改动一律留着（mergeAfterSave 失败分支就是原样），只把基线和修订号换成服务端此刻的：
    // 已经写进去的部分下一轮自然变成「无差异」
    let at = JSON.stringify(withReceipts);
    // 拿到了信封 = 服务端看过并拒了，原样重试没有意义；没拿到（断网、网关 5xx 页）才重试
    const retry = !(e instanceof ApiError);
    // 没拿到信封时，还没回执的插关可能已经提交了：记下来，读回对账（这里或之后的「载入最新」）时按回包丢失处理
    try {
      const readBack = await loadSnapshot(vnum(curVid));
      if (editingKeyRef.current !== key) return;
      // 读回缺块就什么都不收：基线和修订号都留旧的。下一轮写到真动过的地方会撞修订号进冲突，
      // 由「载入最新」重新对齐；缺块的占位值不能当基线
      if (readBack && !missingBlocks(readBack.avail).length && readBack.revision >= (revsRef.current[curVid] ?? 0)) {
        /*
         * 基线要换成读回的，本地就必须先和读回合一次（merge3：base = 发起保存时的服务端，mine = 此刻手上的，
         * theirs = 读回的）。只换基线不合的话，读回里别人这期间新存的行在我本地没有，
         * 下一轮自动保存会把它当成「我删了」而真的删掉。
         * 没拿到信封时，那一笔可能已经提交、只是回包丢了：先按读回精确认领（萌宠编号、拼图 uid；关卡 uid 由合并认）。
         * 用读回之后这一刻的工作副本：等读回的这段时间里人可能又改了
         */
        const now = applyReceipts(liveSnapRef.current ?? withReceipts, receipts);
        const claimed = retry ? reconcileLostCreates(now, readBack.snap, sentPetIds()) : now;
        // 回包丢了的那一笔可能已经提交：读回里和我发的一样的地方算我写的，再合并
        const m0 = merge3(refinedBase(readBack.snap), claimed, readBack.snap) as Snapshot;
        reconciled = true;
        const snap: Snapshot = { ...m0, levels: renumber(m0.levels) };
        followLevels(snap.levels); applySnap(cloneSnap(snap));
        at = JSON.stringify(snap);
        setStore((s) => ({ ...s, [curVid]: readBack.snap }));
        setRevs((r) => ({ ...r, [curVid]: readBack.revision }));
        setBaseline(JSON.stringify(readBack.snap)); pendingBaseRef.current = null; pendingSentRef.current = [];
        }
    } catch { /* 读回也失败：多半是网络，下一轮重试时再对齐 */ }
    /*
     * 没拿到信封、也没对上账：那一笔到底写没写进去不知道。保持「有没存上的」（自动保存接着重试对账、关页要拦），
     * 哪怕此刻本地和已知的样子一样——服务端可能是我发出去的那个中间值
     */
    if (retry && !reconciled) setBaseline(UNRECONCILED);
    // 不跳页：人正在别的页上改东西。出错那页打开时会看到标红
    setSaveError({ page: failPage, msg });
    setSaveState((prev) => ({ state: "error", msg, retry, at, attempt: prev.state === "error" ? prev.attempt + 1 : 1 }));
  };
  /** 已经插进服务端的新关从待插清单里去掉，剩下的下一轮接着插 */
  /** 保存前可写、读回时却没取到的块。这些块读回的是占位值 */
  /*
   * 合并进别人的改动后关卡会重新编号（别人在前面插了关，后面全体 +1）。眼前选中的关、开着的关卡名称弹窗
   * 都是按编号记的——不跟着 uid 挪，下一笔输入就落到了另一关上。按 uid 找回它现在的编号；那一关没了就不动
   */
  const followLevels = (next: Level[]) => {
    const prev = liveSnapRef.current?.levels ?? levels;
    const move = (id: number) => { const u = prev.find((l) => l.id === id)?.uid; const to = u ? next.find((l) => l.uid === u) : undefined; return to ? to.id : id; };
    setSelId((id) => move(id));
    setNamesFor((id) => (id == null ? id : move(id)));
  };
  const missingBlocks = (after: typeof avail) => (Object.keys(avail) as Array<keyof typeof avail>).filter((k) => avail[k] && !after[k]);
  const settleAdds = (r: CreateReceipts) => {
    if (r.levels.length) setPendingAdds((p) => p.filter((x) => !r.levels.includes(x)));
  };
  autoSaveRef.current = () => {
    if (savingRef.current) return;
    // 发布、删草稿这类操作在跑，或者正在把别人存的合进来：等它完再存，别和它抢修订号与基线
    if (busyRef.current || remoteReload.current.running) { setTimeout(() => autoSaveRef.current(), 1000); return; }
    savingRef.current = true;
    busyRef.current = true;
    setSaveState({ state: "saving" });
    void doSaveServer().finally(() => { savingRef.current = false; busyRef.current = false; });
  };
  /**
   * 冲突后「载入最新」：服务端的这一版为准，自己动过的块留着，接着实时保存。
   * 两个人动了同一块时，后存的那个人的块覆盖前一个——修改记录里两笔都在，可以对着找回。
   */
  /**
   * 「载入最新」，也是协同编辑时别人存了之后的自动跟上：服务端这一版为准，自己动过的块留着。
   * 返回双方都改了的块（overlap）——那几块留的是我的，下一轮自动保存会覆盖对方的，调用方据此提示
   */
  const reloadDraft = async (opts: { silent?: boolean } = {}): Promise<{ overlap: string[]; changed: string[] } | null> => {
    const key = editingKeyRef.current;
    try {
      const [readBack, rows] = await Promise.all([loadSnapshot(vnum(curVid)), fetchVersions()]);
      if (editingKeyRef.current !== key) return null;
      setVersions(rows.map(toVersion));
      if (!readBack) throw new Error(t("ver.snapshotLoadFail", { id: curVid }));
      // 比手上的还旧（两次读回乱序到达）：不收，修订号不能倒退
      if (readBack.revision < (revsRef.current[curVid] ?? 0)) return null;
      // 上一轮撞修订号时留下的基线（含那一轮已写成功的块）优先；没有就是缓存里的服务端
      const base = pendingBaseRef.current ? refinedBase(readBack.snap) : store[curVid] ?? mkSnap([]);
      const live = liveSnapRef.current ?? base;
      if (missingBlocks(readBack.avail).length) throw new Error(t("ver.autoReadBackPartial", { blocks: missingBlocks(readBack.avail).join(", ") }));
      // 冲突前可能有建行请求提交了却没拿到回包：先认领，认不出的新关以服务端为准
      // 只有「发出去了不知道成没成」的插关才按回包丢失对账；还没发的新关不是丢了，是别人也插了关
      const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
      // 萌宠 / 拼图：建行请求可能已经提交、回包丢了——先按读回认领。关卡不用：它有 uid，下面的合并自己认得出
      const claimed = reconcileLostCreates(live, readBack.snap, sentPetIds());
      /*
       * 字段级三方合并（lib/autosave.ts merge3）：base = 上一次的服务端，mine = 我手上的，theirs = 服务端现在的。
       * 两个人改不同的关、同一关的不同字段都留下；同一个字段两边改成不同值以我的为准，记进 conflicts 提示。
       * 关卡按 uid 合，合完重新编号（id 是位置）；待插清单按「服务端没有的 uid」重算
       */
      const conflicts: string[] = [];
      // 竞拍按关号做键：别人插关 / 拖动改编号后先换到服务端现在的编号再合，否则我的改动会落到同号的另一关上
      const onTheirs = (s: Snapshot): Snapshot => ({ ...s, auctions: rekeyByLevelUid(s.auctions, s.levels, readBack.snap.levels) });
      const merged0 = merge3(onTheirs(base), onTheirs(claimed), readBack.snap, "", conflicts) as Snapshot;
      const merged: Snapshot = { ...merged0, levels: renumber(merged0.levels) };
      const serverUids = new Set(readBack.snap.levels.map((l) => l.uid));
      setPendingAdds(merged.levels.filter((l) => !serverUids.has(l.uid)).map((l) => l.id));
      const keys = Object.keys(readBack.snap) as Array<keyof Snapshot>;
      const changed = keys.filter((k) => !same(readBack.snap[k], base[k]));
      setStore((s) => ({ ...s, [curVid]: readBack.snap }));
      setRevs((r) => ({ ...r, [curVid]: readBack.revision }));
      followLevels(merged.levels); applySnap(cloneSnap(merged));
      setBaseline(JSON.stringify(readBack.snap)); pendingBaseRef.current = null; pendingSentRef.current = [];
      setSaveState({ state: "idle" });
      // 冲突位置里的关卡 uid 换回「第几关」，提示给人看
      const uidPos = new Map(merged.levels.map((l) => [l.uid, l.id]));
      const readable = conflicts.map((c) => c.replace(/^levels\[uid=([^\]]+)\]/, (m, u: string) => (uidPos.has(u) ? `levels[id=${uidPos.get(u)}]` : m)));
      return { overlap: readable, changed: changed as string[] };
    } catch (e) {
      if (!opts.silent) setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
      return null;
    }
  };
  const reloadRef = useRef(reloadDraft);
  reloadRef.current = reloadDraft;

  /*
   * 协同编辑（设计稿 nav.jsx 协同编辑一节）。房间只传在线状态，版本号只由服务端 CAS 推进：
   * 别人存到了第几号，我比他旧就先把他改的块合进来（自己正在改的块不动），手上的修订号跟上，
   * 下一次自动保存就不会拿旧号去撞。
   */
  const [merged, setMerged] = useState<{ by: string; summary: string; conflicts: string[] } | null>(null);
  /*
   * 别人存了之后的跟上：同一时刻只跑一个，跑的时候又来了就跑完再补一次（合并成一次）；
   * 两次之间至少隔 1.5 秒——连续收到很多条（包括有人乱发）也只会每 1.5 秒读一次。
   * 补跑前等一帧：让上一次合并的结果先渲染，reloadRef 拿到的是新基线，不会拿旧基线把别人刚合进来的当成我改的
   */
  const remoteReload = useRef<{ running: boolean; again: SavedEvent | null; last: number }>({ running: false, again: null, last: 0 });
  const scheduleRemoteReload = (e: SavedEvent) => {
    const st = remoteReload.current;
    if (st.running) { st.again = e; return; }
    st.running = true;
    setTimeout(async () => {
      st.last = Date.now();
      try {
        // 我正在存：先不读，存完再看一次（存的时候读回已经带上了他的，那时修订号够新就跳过）
        if (savingRef.current) { st.again = st.again ?? e; return; }
        if (e.revision > (revsRef.current[curVidRef.current] ?? 0)) {
          const r = await reloadRef.current({ silent: true });
          if (r) setMerged({ by: e.by, summary: blockLabels(e.summary ? e.summary.split(",") : r.changed), conflicts: r.overlap });
        }
      } finally {
        st.running = false;
        const next = st.again;
        st.again = null;
        if (next) setTimeout(() => scheduleRemoteReload(next), 30);
      }
    }, Math.max(30, st.last + 1500 - Date.now()));
  };
  const clearMerged = useCallback(() => setMerged(null), []);
  const blockLabels = (keys: string[]) => keys.map((k) => (SECTION_KEYS[k] ? t(SECTION_KEYS[k]) : k)).filter((x, i, a) => a.indexOf(x) === i).join("、");
  const doPublish = (mode: RefreshMode, note?: string) => {
    // 发布的是服务端存的那一版；本地有未保存改动时先保存，否则发出去的不是眼前看到的
    if (dirty) { setToast(t("ver.saveFirst")); return; }
    // 合并冲突后「保留草稿的值并发布」是第二次请求，备注要跟着带上
    publishNoteRef.current = note;
    runExclusive(() => doPublishServer(mode));
  };
  /** 回滚 = 以历史版本内容开新草稿；历史与线上都不动（后端 createDraft 的既定语义） */
  /** 从 0 建这个代理的第一版。建完直接进编辑态，策划开始往空版本里填 */
  /*
   * 还没有任何版本时，初始化页的两个选项直接可点：点第一项就先在后台建出第一版，建好再把这次的选择存进去
   * （以前要先点「从 0 新建第一版」才有东西可选——实时保存之后那一步是多余的，10-10 从零建代理实测）
   */
  const pendingInitRef = useRef<{ agent: string | null; v: Partial<typeof selectors> } | null>(null);
  /** 第一版正在建（createFirstVersion 在路上）：期间不许切代理 */
  const firstBuildingRef = useRef(false);
  useEffect(() => {
    const p = pendingInitRef.current;
    if (!p || p.agent !== agent || !snapLoaded || snapAgent !== agent || !editable || !avail.common) return;
    pendingInitRef.current = null;
    setSelectors((s) => ({ ...s, ...p.v }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, snapLoaded, snapAgent, editable, avail.common]);
  /** fromA0：以那个代理的生效版本为底稿（初始化页「以已有代理为底稿」）；不给就从空白建 */
  const createFirstVersion = async (fromA0?: string) => {
    // 绑定发起时的代理：建的过程中切代理已被 switchAgent 拦住，这里再兜一层——每次 await 回来都核对，对不上就什么都不套
    const forAgent = agent;
    const stale = () => agentRef.current !== forAgent;
    /** 建好之后全局参数块读到了没：没读到的话初始化页的选择存不进去（见 finally） */
    let commonOk = false;
    firstBuildingRef.current = true;
    setBusy(true);
    try {
      const made = fromA0 ? await createFirstFromAgentApi(fromA0) : await createFirstDraftApi(t("ver.firstNote"));
      const { configVersion } = made;
      if (stale()) return;
      const rows = await fetchVersions();
      if (stale()) return;
      const opened = await loadSnapshot(configVersion);
      if (stale()) return;
      commonOk = !!opened?.avail.common;
      const id = vid(configVersion);
      setVersions(rows.map(toVersion));
      setUnconfigured(false);
      if (opened) {
        setStore({ [id]: opened.snap });
        setRevs({ [id]: opened.revision });
        setAvail(opened.avail);
        applySnap(cloneSnap(opened.snap));
        setBaseline(JSON.stringify(opened.snap));
      }
      setCurVid(id);
      setSnapLoaded(true); setSnapAgent(agent);
      logAction("config.verCreateFirst", id, fromA0 ? { fromA0 } : {}, agent ?? undefined);
      const from = (made as { from?: { a0: string; configVersion: number } }).from;
      const dropped = (made as { dropped?: { puzzles: number; packIdols: number; petOshi: number } }).dropped;
      const lost = dropped ? dropped.puzzles + dropped.packIdols + dropped.petOshi : 0;
      setToast(from
        ? t(lost ? "initcfg.copiedDropped" : "initcfg.copied", { from: from.a0, v: from.configVersion, puzzles: dropped?.puzzles ?? 0, packs: dropped?.packIdols ?? 0, pets: dropped?.petOshi ?? 0 })
        : t("ver.firstCreated", { id }));
      // 空版本建出来之后第一件事是选玩法体系与建筑体系——它俩决定左边显示哪一组页面，
      // 不先选就去填造价/关卡，很可能填的是这个代理根本不跑的那一套
      setPage("init-config");
    } catch (e) {
      // 建失败：初始化页上等着落地的选择作废，让人能再点一次（只清这个代理的）
      if (pendingInitRef.current?.agent === forAgent) pendingInitRef.current = null;
      if (!stale()) setToast((e as Error).message, true);
    } finally {
      firstBuildingRef.current = false;
      /*
       * 等着落地的初始化选择落不了的两种情况，都要清掉：中途作废（代理对不上）；版本建好了但全局参数块没读回来
       *（读回部分失败不进 catch）——后者告诉人刷新后再选一次（版本已经在了，不会再建一遍）
       */
      if (pendingInitRef.current?.agent === forAgent && (stale() || !commonOk)) {
        pendingInitRef.current = null;
        if (!stale()) setToast(t("ver.firstPartial"), true);
      }
      setBusy(false);
    }
  };

  const newDraftFromServer = async (v: Version, isRollback: boolean) => {
    try {
      /*
       * 一个代理只有一个草稿：已经有草稿时服务端把它交回来（reused），不新建。
       * 这一步必须**说出来**——不说的话，人点了「基于 v51 新建草稿」却落在
       * v59 上，会以为按错了；而 v59 里还躺着别人的改动。
       *
       * ⚠️ 回滚这条路也一样会被复用兜住，这是对的：手上有在制品时不该
       * 悄悄再开一份历史内容的分叉，那正是「回滚覆盖掉别人的活」的老路。
       */
      const draft = await createDraftApi(vnum(v.id), t(isRollback ? "ver.rollbackFrom" : "ver.newDraftFrom", { id: v.id }), isRollback);
      const configVersion = draft.configVersion;
      const [readBack, rows] = await Promise.all([loadSnapshot(configVersion), fetchVersions()]);
      if (!readBack) throw new Error("draft created but read-back failed");
      const id = vid(configVersion);
      setVersions(rows.map(toVersion));
      setStore((s) => ({ ...s, [id]: readBack.snap }));
      setRevs((r) => ({ ...r, [id]: readBack.revision }));
      setAvail(readBack.avail);
      setCurVid(id);
      applySnap(cloneSnap(readBack.snap));
      setBaseline(JSON.stringify(readBack.snap)); pendingBaseRef.current = null; pendingSentRef.current = [];
      logAction(isRollback ? "config.verRollback" : "config.verDraft", id, { from: v.id }, agent ?? undefined);
      if (draft.reused) {
        setVerUi(null);
        setToast(t("ver.draftReused", {
          id, who: draft.author || "?", at: draft.createdAtMs ? new Date(draft.createdAtMs).toLocaleString() : "?",
        }), true);
      } else if (isRollback) setVerUi({ kind: "rollbackResult", id, src: v.id });
      else { setVerUi(null); setToast(t("ver.draftCreated", { id })); }
    } catch (e) {
      setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
    }
  };
  /**
   * 删草稿。三道闸（只删 draft / 不删生效中 / revision CAS）都在服务端，
   * 这里不重复判断，只负责删完把界面切到一个仍然存在的版本上——
   * 留在已删版本上会让后续每个请求都 404，而人看不出发生了什么。
   */
  const deleteDraftServer = async (id: string) => {
    try {
      // 下拉里就地删的草稿多半从没打开过，本地没有它的修订号——现从版本列表取。
      // CAS 语义不变：取号到删除之间要是有人写了它，服务端照样拒。
      let expected = revs[id];
      if (expected === undefined) {
        const listed = await fetchVersions();
        const row = listed.find((r) => vid(r.configVersion) === id);
        if (!row) { setVersions(listed.map(toVersion)); setVerUi(null); return; } // 已被别人删了
        expected = row.revision;
      }
      await deleteVersionApi(vnum(id), expected);
      const rows = await fetchVersions();
      const next = rows.map(toVersion);
      setVersions(next);
      setStore((st) => { const c = { ...st }; delete c[id]; return c; });
      setRevs((r) => { const c = { ...r }; delete c[id]; return c; });
      setVerUi(null);
      setToast(t("ver.deleted", { id }));
      // 删的是别的草稿（下拉里就地删）：当前视图不动，刷新完列表就结束
      if (id !== curVid) return;
      // 删的是当前版本：切到生效版本；没有生效版本就退到列表第一个，实在没有则回空状态
      const fallback = next.find((v) => v.status === "active") ?? next[0];
      if (!fallback) { setUnconfigured(true); return; }
      const snap = await loadSnapshot(vnum(fallback.id));
      if (!snap) { setLoadError(t("initcfg.app.loadVersionFail")); return; }
      setStore((st) => ({ ...st, [fallback.id]: snap.snap }));
      setRevs((r) => ({ ...r, [fallback.id]: snap.revision }));
      setAvail(snap.avail);
      setCurVid(fallback.id);
      applySnap(cloneSnap(snap.snap));
      setBaseline(JSON.stringify(snap.snap));
    } catch (e) {
      setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
    }
  };
  const deleteDraft = (id: string) => runExclusive(() => deleteDraftServer(id));

  /**
   * 清掉多余草稿，只留最新那个。
   *
   * 删哪些由服务端判（「除最新草稿以外的全部草稿」），前端不传清单——
   * 两边各算一份判据，分叉时就是误删。
   * 结果如实报：清掉几个、哪几个没清掉、为什么。半路失败不算成功。
   */
  const pruneDraftsServer = async () => {
    try {
      const r = await pruneDraftsApi();
      const rows = await fetchVersions();
      setVersions(rows.map(toVersion));
      setVerUi(null);
      if (!r.deleted.length && !r.skipped.length) { setToast(t("ver.pruneNone")); return; }
      if (r.skipped.length) {
        setToast(t("ver.prunePartial", {
          n: r.deleted.length, m: r.skipped.length,
          why: r.skipped.map((x) => `v${x.version} ${x.reason}`).join("；"),
        }), true);
      } else {
        setToast(t("ver.pruneDone", { n: r.deleted.length, id: r.kept == null ? "-" : vid(r.kept) }));
      }
      // 清掉的可能正是当前视图停着的那一版：本地缓存跟着删，别留着一个不存在的版本
      setStore((st) => { const c = { ...st }; for (const v of r.deleted) delete c[vid(v)]; return c; });
      setRevs((rv) => { const c = { ...rv }; for (const v of r.deleted) delete c[vid(v)]; return c; });
      if (r.deleted.some((v) => vid(v) === curVid)) setReloadKey((k) => k + 1);
    } catch (e) {
      setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true);
    }
  };
  const pruneDrafts = () => runExclusive(pruneDraftsServer);

  const newDraftFrom = (v: Version, isRollback: boolean) => {
    /*
     * 有未保存改动时不许直接建草稿：建完会用服务端读回的快照 applySnap，
     * 本地没保存的东西**无提示地整个覆盖掉**。曾经配合「已发布版本上输入框
     * 被硬编码成可编辑」，正好组成一条静默丢数据的路。
     */
    if (dirty) { setVerUi(null); setToast(t("ver.saveFirst")); return; }
    /*
     * 基线比生效版本旧 → 先说清后果再建。
     *
     * createDraft 是整版拷贝 from 的内容，**服务端不比较 from 与生效版本**——
     * 这是刻意的，回滚就是靠「from 一个历史版本」实现的，不能一律拦。
     * 但它同时是一条静默毁配置的路：页面停在 v10（旧标签页、从历史点进来、
     * 或别人刚发了新版本自己不知道），点「基于此新建」拿到的是 v10 的内容，
     * 编辑完发布激活，v11~v14 的改动就被整个覆盖掉，全程零提示
     *（leo 2026-08-27 实际遇到）。
     * 回滚路径不弹这个——它本来就是要拿旧内容覆盖，后果由回滚对话框自己讲。
     */
    if (!isRollback && online && vnum(v.id) < vnum(online.id)) {
      setVerUi({ kind: "staleBase", v, isRollback });
      return;
    }
    runExclusive(() => newDraftFromServer(v, isRollback));
  };
  const [addingAt, setAddingAt] = useState<number | null>(null);
  const [batching, setBatching] = useState(false);
  const [renamingRival, setRenamingRival] = useState<RivalDef | null>(null);
  // 点了表头的 × 之后、确认之前的那一列（确认框里要写清删多少个价）
  const [removingRival, setRemovingRival] = useState<RivalDef | null>(null);
  const [assetCell, setAssetCell] = useState<AssetCell | null>(null);
  const [selId, setSelId] = useState(1);
  /*
   * 协同编辑（全部代理一个房间，按权限过滤）：顶栏看得见我有权限的所有代理里在线的人；鼠标、描格、「存了」只对同代理同版本生效。
   * 别人存了同一版：比我新就把他的改动合进来（scheduleRemoteReload）
   */
  const myVersion = curVer ? vnum(curVid) : null;
  /** 当前页跟配置版本有关（顶上有版本条）。不是的话协同时不按版本分人 */
  const versionedPage = VERSIONED_PAGES.includes(page) || isTilePage(page) || page === "act-item";
  /** 协同按版本分人：凡是读配置的页都要（版本不同，同一格里的东西就不是一回事），不只是有版本条的那几页 */
  const collabView = { a0: agent ?? "", page, version: myVersion, versioned: !configFree };
  const collab = useCollab({
    enabled: !!agent,
    a0: agent,
    version: myVersion,
    versioned: !configFree,
    page,
    selUid: levels.find((l) => l.id === selId)?.uid ?? null,
    onSaved: (e) => {
      if (e.a0 !== (agent ?? "") || e.version !== myVersion) return;
      if (e.revision <= (revsRef.current[curVid] ?? 0)) return;
      scheduleRemoteReload(e);
    },
  });
  collabAnnounceRef.current = collab.announceSaved;
  /*
   * 点顶栏头像跳到他那里：切到他的版本（我还有没存上的改动时先走切换确认）→ 他的页面 → 他选中的关 →
   * 滚到他看的位置，他正在填的那一格闪一下。要切版本的话等版本切过来再接着做（pendingJump）
   */
  /**
   * 切代理（顶栏下拉、点别的代理里的人的头像都走这里）。草稿还没存上（防抖中 / 正在存）时不许切：
   * 存的请求按请求各自取当前代理，切到一半后面几笔会带着新代理的头写进新代理的同号版本。切成了返回 true
   */
  const switchAgent = (id: string | null): boolean => {
    if (id === agent) return true;
    if (savingRef.current || (editable && dirty) || flushLiveDrafts()) { setToast(t("ver.autoPending"), true); return false; }
    // 第一版还在建（或建好要存的初始化选择还没落地）：切走的话回来的结果会套到新代理上
    if (firstBuildingRef.current) { setToast(t("ver.firstBuilding"), true); return false; }
    if (id) logAction("agent.switch", id, { from: agent }, id);
    setAgent(id);
    rememberA0(id);
    return true;
  };
  /** 顶栏悬浮卡里写他在哪个代理：有名字用名字 */
  const agentLabel = (a0: string) => { const o = agentOptions.find((x) => x.a0 === a0); return o?.name ? `${a0} - ${o.name}` : a0; };
  const pendingJump = useRef<{ p: CollabPeer; at: number; switched?: boolean } | null>(null);
  const applyJump = (p: CollabPeer) => {
    if (p.page && p.page !== page) setPage(p.page);
    const lv = p.selUid ? levels.find((l) => l.uid === p.selUid) : undefined;
    if (lv) setSelId(lv.id);
    setTimeout(() => {
      if (p.scroll != null) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo({ top: p.scroll * Math.max(0, max), behavior: "smooth" });
      }
      const el = p.field ? findField(p.field) : null;
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        el.animate([{ boxShadow: `0 0 0 3px ${colorOf(p.name)}` }, { boxShadow: "0 0 0 0 transparent" }], { duration: 1600 });
      }
    }, 200);
  };
  const jumpTo = (p: CollabPeer) => {
    // 他在别的代理：先切代理（有没存上的改动时切不过去，switchAgent 会提示），切过去、数据到了再接着跳（下面的 effect）
    if (p.a0 && p.a0 !== agent) {
      if (switchAgent(p.a0)) pendingJump.current = { p, at: Date.now() };
      return;
    }
    // 代理对了但这个代理的快照还没到（刚切过来、正在加载）：排队，用最新的目标覆盖——跟随时他可能连着换了几次
    if (!snapLoaded || snapAgent !== agent || !curVid) { pendingJump.current = { p, at: Date.now() }; return; }
    if (p.version != null && vid(p.version) !== curVid && versions.some((v) => v.id === vid(p.version!))) {
      pendingJump.current = { p, at: Date.now(), switched: true };
      requestSwitch(vid(p.version));
      return;
    }
    // 就落在眼前这一版：之前排着的跳转、还在路上的切版本都作废，以这次为准
    pendingJump.current = null;
    switchSeqRef.current++;
    applyJump(p);
  };
  useEffect(() => {
    const j = pendingJump.current;
    if (!j) return;
    // 切换确认被取消、或者半天没切过来：作废，免得以后手动切到那一版时突然跳页
    if (Date.now() - j.at > 30_000) { pendingJump.current = null; return; }
    // 跨代理：等代理切过来、版本列表和快照都到了（换代理会先清空，curVid 为空、snapLoaded 为假）
    if (j.p.a0 && j.p.a0 !== agent) return;
    if (!snapLoaded || snapAgent !== agent || !curVid) return;
    if (j.p.version != null && vid(j.p.version) !== curVid) {
      // 新代理默认打开的不是他那一版：切一次（只切一次，取消了就作废）
      if (!j.switched && versions.some((v) => v.id === vid(j.p.version!))) { j.switched = true; requestSwitch(vid(j.p.version)); }
      else if (j.switched) return;
      else { pendingJump.current = null; applyJump(j.p); }
      return;
    }
    pendingJump.current = null; applyJump(j.p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, versions, snapLoaded, snapAgent, curVid, levels]);
  /*
   * 跟随：他换版本、换页、换选中的关、滚动，我跟着跳。按 Esc、自己动手（键盘输入、在内容区点按）或他离开就停
   */
  const [following, setFollowing] = useState<string | null>(null);
  /** 看不看别人的鼠标：每人自己的偏好，存本机（读写失败就当开着） */
  const [showCursors, setShowCursorsState] = useState(() => { try { return localStorage.getItem("collab.cursors") !== "0"; } catch { return true; } });
  const setShowCursors = (v: boolean) => { setShowCursorsState(v); try { localStorage.setItem("collab.cursors", v ? "1" : "0"); } catch { /* 无痕窗口等：只在本次生效 */ } };
  const followed = following ? collab.peers.find((p) => p.id === following) ?? null : null;
  /*
   * 换了代理：是为了跳到 / 跟着某人过去的，照常；是自己在顶栏手动切走的，跟随与待跳作废——
   * 不然他下一次动，又把我拽回他那个代理
   */
  useEffect(() => {
    if (pendingJump.current && pendingJump.current.p.a0 !== agent) pendingJump.current = null;
    if (followed && followed.a0 !== agent && !pendingJump.current) setFollowing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);
  const followSig = followed ? JSON.stringify([followed.a0, followed.version, followed.page, followed.selUid, followed.scroll]) : "";
  useEffect(() => { if (followed) jumpTo(followed); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [followSig]);
  useEffect(() => { if (following && !followed) setFollowing(null); }, [following, followed]);
  useEffect(() => {
    if (!following) return;
    const stop = (e: Event) => {
      if (e instanceof KeyboardEvent && ["Shift", "Control", "Alt", "Meta"].includes(e.key)) return;
      if (e.type === "pointerdown" && !(e.target as Element)?.closest?.("[data-collab-main]")) return;
      setFollowing(null);
    };
    window.addEventListener("keydown", stop, true);
    window.addEventListener("pointerdown", stop, true);
    return () => { window.removeEventListener("keydown", stop, true); window.removeEventListener("pointerdown", stop, true); };
  }, [following]);
  const [namesFor, setNamesFor] = useState<number | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  /*
   * 竞拍是**按关卡分期**的，不是一组全局参数。默认值只是开新一期时的表单预填，
   * 不属于版本快照，不参与脏检测。
   */
  const [aucDefaults, setAucDefaults] = useState<AuctionDefaults>(() => defaultAuction(Date.now()));
  // 奖励里可选的卡牌候选直接来自卡牌配置，避免两处各维护一份名单。
  // 下拉的 value 是基准语言名（服务端只认它和编号），显示按界面语言查卡牌名词表（lib/reward 的 setCardCatalog）。
  // 2026-09-11 ~ 10-21 曾直接拿界面语言名当 value，中文界面存进去的引用全被发布闸拒掉。
  const cardLocale = uiLang === "en" ? "en" : "zh-CN";
  // 奖励项的显示名（活动道具 / 称号 / 头像框）按界面语言取，见 lib/reward 的 setRewardLocale
  setRewardLocale(cardLocale);
  // 头像框名词表来自本版本的「头像框」页：奖励弹窗里 kind=frame 的下拉与摘要按它显示
  setFrameCatalog(frames.map((f) => ({ id: f.frameId, name: f.nameI18n, enabled: f.enabled })));
  setCardCatalog([...itemPacks, ...idolPacks]);
  const itemCards = itemPacks.flatMap((p) => p.cards.map((c) => cardRef(p, c)));
  const idolCards = idolPacks.flatMap((p) => p.cards.map((c) => cardRef(p, c)));
  // 任务 / 成就的指标字典与租户、版本无关（服务端常量），登录后拉一次即可
  useEffect(() => {
    if (!session.token || isExpired(liveSession(session))) return;
    let alive = true;
    fetchAchievementMetrics().then((d) => { if (alive) setAchDict(d); }).catch(() => { /* 拉不到就只显示 key */ });
    return () => { alive = false; };
  }, [session.token]);

  /** 只改当前选中关；成本格与竞品格都走这里，保证撤销/脏标记有单一入口 */
  const patchLevel = (id: number, patch: Partial<Level>) =>
    setLevels((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  /**
   * 竞品列：新增 / 删除。列 id 在前端就按服务端的格式发号（rv_ + 36 进制时间戳），保存时原样带上，
   * 省掉「服务端发号后回来改网格键」那一步。删列连三处数值一起删；客户端正在用它就改回「手填」。
   */
  const addRival = () => {
    setRivals((xs) => {
      const id = `rv_${Date.now().toString(36)}${xs.length.toString(36)}`;
      return [...xs, { id, name: t("rivalDefault", { n: xs.length + 1 }), color: rivalColor(xs.length) }];
    });
  };
  const removeRival = (r: RivalDef) => {
    setRivals((xs) => xs.filter((x) => x.id !== r.id));
    setLevels((ls) => ls.map((l) => { const { [r.id]: _gone, ...rest } = l.rivals; return { ...l, rivals: rest }; }));
    setPettownRivalPrices((m) => { const { [r.id]: _gone, ...rest } = m; return rest; });
    // 客户端正在用这一列：改回默认的公式（leo 2026-09-17 定默认公式）
    if (selectors.costSource === `rival:${r.id}`) {
      setSelectors((sel) => ({ ...sel, costSource: "formula" }));
      setToast(t("cost.src.removedInUse", { name: r.name }), true);
    }
  };
  /** 这一列已经填了几个价：关卡制逐格 + 萌宠小镇逐星 */
  const rivalFilled = (id: string): number =>
    levels.reduce((a, l) => a + Object.values(l.rivals[id] ?? {}).filter((v) => v != null).length, 0)
    + Object.keys(pettownRivalPrices[id] ?? {}).length;
  const setCostSource = (v: string) => {
    setSelectors((sel) => ({ ...sel, costSource: v }));
  };

  const checkHealth = useCallback(() => {
    setHealth({ state: "checking" });
    probeHealth().then(setHealth);
  }, []);
  useEffect(checkHealth, [checkHealth]);

  // 门禁必须在渲染配置界面**之前**返回：
  // 放在后面用条件隐藏没用——假数据仍会被构造、结构仍会进 DOM。
  if (!session.token || isExpired(liveSession(session))) {
    return <Gate lost={session.token ? { kind: "expired" } : null} uiLang={uiLang} onUiLang={onUiLang} />;
  }

  const item = findItem(page);
  /**
   * 不依赖 `levels` 的版本化页面。空版本时它们照常可编辑 ——
   * 其余页在 levels 为空时 `lv` 是 undefined，渲染会当场炸，所以必须被上面那条闸拦住。
   */
  // 喵汪角色与关卡完全无关：不放行的话，一关都没配的新代理进不了这一页。
  // 任务成就同理（且它不走版本，只借当前版本的关卡数 / 卡包名做下拉提示）
  /*
   * 不依赖关卡表的页。**没关卡就整页换成「新建第一关」**这条只对关卡制的内容成立；
   * 全局参数、萌宠小镇那三页跟关卡毫无关系，挡在这道门后面就成了死结：
   * 发布闸要求填全局参数 → 点进骰子参数 → 页面说「这一版还没有关卡」→
   * 而这个代理跑的是萌宠小镇 / 多人匹配，本来就不该有关卡。
   */
  /**
   * 副玩法页（subgame-*）也不依赖关卡：它们编辑的是 cfg_tile 一行 + 挂在 cfg_main_common 上的对抗参数
   * （FIGHT_TILE_FIELDS）。萌宠小镇 / 多人代理没有关卡概念，2026-09-09 之前这些页被「这一版还没有关卡」
   * 挡住，那 11 个对抗参数无处可填、COMMON_UNFILLED 永远发布不了。
   */
  const levelFree = (p: string) => LEVEL_FREE_PAGES.includes(p) || p.startsWith("subgame-");
  const LEVEL_FREE_PAGES = [
    "puzzle-list", "puzzle-price", "pet-list", "pet-star", "achievement-tasks", "achievement-achievements", "achievement-rewards", "newbie-activity",
    "erhe-daily", "erhe-chest", "erhe-weekly", "erhe-achievement", "erhe-tabs",
    "init-config", "global-dice", "global-fight", "cost",
    "pettown-params", "pettown-star", "pettown-slot", "pettown-talent", "level-talent",
    "social-channel", "social-event-type", "frame", "daily-reward", "shop", "shop-sections",
    "match-mode", "battle-stage", "ability-card", "board-cells", "lamp-drops",
    "idol-intimacy", "idol-photos", "idol-vip-photos", "idol-vip-videos", "stage-images",
    // 活动道具页编辑的是 spin 格的四行 + 一张不进版本的排期表，两者都与关卡无关
    "act-item",
  ];

  /** 玩法格编辑页。玩法格各页与「活动道具」页顶上的活动道具格共用（2026-10-06 并页） */
  const tilePageFor = (tile: Tile) => (
          <TilePage
            // 按版本 + 格子重挂：领养池面板里的公示文案控件只在挂载时从 value 初始化语言列表；
            // 格子表单边改边存，切到另一个格子时旧格子没交的那一笔要在卸载时交掉，
            // 不重挂的话新格子会被当成「外面变了」、留着旧格子的草稿
            key={`${curVid}:${tile.key}`}
            tile={tile}
            editable={editable && avail.tiles}
            eB={unitE}
            fight={fight}
            fightEditable={editable && avail.common}
            adoptPools={adoptPools}
            /*
             * 只取填了编号的：新建行的 petId 还是空串，进名册会让成员权重全撞在 "" 上。
             *
             * 传 `nameI18n` 而**不是**扁平 `name`：管端改名只写 nameI18n，扁平那份要等
             * toServerPet 由 en 派生、再从服务端读回才更新（lib/pet.ts）。传 name 的话，
             * 同一草稿里新建 / 改过名的角色在成员表里显示的是旧名字或空白。
             * 取哪个语言是面板的事（它按当前 UI 语言 pickText），这里原样交出去。
             */
            adoptRoster={pets.filter((p) => p.petId).map((p) => ({
              petId: p.petId, nameI18n: p.nameI18n, quality: p.quality, enabled: p.enabled,
            }))}
            /*
             * 绑 avail.adoptPools 而不是 avail.tiles：它是另一个数据块，
             * 绑错块会出现「能编但不落库」的静默丢改动。
             * **块可用性单独传**（见 TilePage 的 adoptReady 注释）：揉进 editable 的话，
             * 读失败时占位 `[]` 会被渲染成「确实没配」，只读版本上还没有任何提示。
             */
            adoptEditable={editable}
            adoptReady={avail.adoptPools}
            rosterReady={avail.pets}
            onSaveAdopt={setAdoptPools}
            // 边改边存：每次停手都会调到这里，不弹提示、不单独记日志（版本修改记录里有）
            onSaveFight={(patch) => setFight((f) => ({ ...f, ...patch } as FightParams))}
            // 这一版还没有这一格（页面直接给了默认值的空格子）：第一次改动时把它加进来，之后照常替换
            onSave={(next) => setTiles((xs) => (xs.some((x) => x.key === next.key) ? xs.map((x) => (x.key === next.key ? next : x)) : [...xs, next]))}
          />
  );

  // 选中的 A0 决定后续所有请求打哪个库
  setApiA0(agent);
  const tz = tzOfAgent(agent);

  /*
   * ⌘K 搜索里的「内容」：当前版本快照里的关卡、建筑、卡包…。只在打开搜索时现算（ref 拿最新状态、回调本身不变），
   * 不在每次渲染建索引。建筑跳过去前先选中所在那一关（建筑页按 selId 显示）
   */
  const searchSnapRef = useRef<Parameters<typeof snapshotDocs>[0]>({});
  searchSnapRef.current = {
    levels, rewards, itemPacks, idolPacks, spins, pets, frames, battleStages, abilityCards, socialChannels, talentNodes, pettownSlots,
    buildingSystem: selectors.buildingSystem,
  };
  const getSearchContent = useCallback(
    (path: (page: string) => string, tags: Parameters<typeof snapshotDocs>[1]) => snapshotDocs(searchSnapRef.current, tags, path, setSelId),
    [],
  );
  return (
    <TzProvider tz={tz}>
    <BScaleContext.Provider value={bScale}>
    <CollabContext.Provider value={{ peers: collab.peers, connected: collab.connected, agent: agent ?? "", agentLabel, jumpTo, following, setFollowing, showCursors, setShowCursors }}>
    <AppShell
      page={page} onPage={setPage}
      getSearchContent={getSearchContent}
      tiles={tiles}
      selectors={selectors}
      isSuperAdmin={isSuperAdmin}
      isRoot={isRoot}
      agent={agent}
      agents={agentOptions}
      onAgent={(id) => { switchAgent(id); }}
      uiLang={uiLang} onUiLang={onUiLang}
      user={session.operator ?? t("top.notLoggedIn")}
      onOpenLog={() => setShowLog(true)}
      onLogout={() => setConfirmLogout(true)}
      env={health.state === "ok" ? health.env : undefined}
    >
      {/* 接口视角：顶栏开关打开后才渲染；值按配置台当前打开的版本现场生成 */}
      <ApiLensPanel version={curVer ? vnum(curVer.id) : null} activeVersion={online ? vnum(online.id) : null} revision={curVer ? revs[curVer.id] ?? null : null} />
      {/*
        后端不可达要说出来。否则表现是「页面正常、只是没数据」，
        策划会当成配置被清空，而不是服务出问题。
      */}
      {health.state === "down" && (
        <div className="mb-3 flex items-center gap-3 rounded-base border border-danger-200 bg-danger-50 px-4 py-2 text-sm text-danger-700">
          <span>{t("health.down", { reason: t(health.reason.key, health.reason.vars) })}</span>
          <button type="button" onClick={checkHealth} className="rounded-base border border-danger-200 px-2 py-0.5 text-xs">
            {t("common.retry")}
          </button>
        </div>
      )}

      <ErrorBoundary>
        <Suspense fallback={<Panel><div className="py-10 text-center text-sm text-ink-placeholder">{t("app.loadingConfig")}</div></Panel>}>
        {/*
          ── 非版本页先分流 ──
          idol 名册不受配置版本管辖，必须绕开下面那三层闸门
          （loadError → !snapLoaded → levels.length === 0）。
          那三层排在所有页面分支之前：新代理一版配置都没有时，整个界面只显示
          「去建第一版」，根本进不去 idol 页——而 idol 恰恰是不受版本管辖、
          应该能先录的东西。不绕开的话新环境上录不了 idol，也就配不了拼图。
        */}
        {page === "idol-list" ? (
          /*
            key={agent}：换代理时整页重挂，而不是只重拉列表。
            不重挂的话，旧租户的详情抽屉会留在屏幕上，它保存时打的是**新** A0
            （api.ts 的 currentA0 是模块级变量）——等于把 A 代理的 idol 写进 B 代理。
          */
          <IdolListPage key={agent ?? "-"} a0={agent} onJump={setPage} />
        ) : (
        <>
        {/*
          该代理还没有任何配置版本。这是合法状态而不是错误——每份配置都属于
          某个真实代理，没有全局那份可继承，新代理天然是空的。
          所以这里给的是「从 0 建第一版」，不是重试。
        */}
        {/*
          上一次保存就是卡在这一页。横幅留着不自动消失：运营改完再点保存，
          成功了才清掉——自动消失的提示等于没提示，人一抬头就不知道刚才发生过什么。
        */}
        {saveError && saveError.page === page && (
          <div className="mb-3">
            <Notice tone="danger">
              <span className="font-semibold">{t("ver.saveFailedHere")}</span>
              <span className="ml-1">{saveError.msg}</span>
              <button type="button" className="ml-3 underline" onClick={() => setSaveError(null)}>
                {t("common.dismiss")}
              </button>
            </Notice>
          </div>
        )}
        {/* 读不到就说读不到，整页不渲染任何配置——绝不拿演示数据顶上 */}
        {loadError && !configFree && (
          <Panel>
            <div className="flex flex-col items-start gap-3 px-4 py-10">
              <div className="text-base font-medium text-danger">{t("app.loadFailedTitle")}</div>
              <div className="max-w-2xl text-sm text-ink-regular">{t("app.loadFailedBody", { err: loadError })}</div>
              <Button tone="primary" onClick={() => setReloadKey((k) => k + 1)}>{t("common.retry")}</Button>
            </div>
          </Panel>
        )}
        {/*
          * 块级失败的提示。**放在页面内容之前**，因为它解释的正是「为什么这一页点不动」——
          * 放底下或者做成 toast 的话，运营滚不到、或者早就消失了。
          */}
        {!loadError && blockErrors.length > 0 && (
          <div className="mb-4">
            <Notice tone="danger">
              <div className="font-medium">{t("app.blockFailedTitle")}</div>
              <ul className="mt-1.5 list-disc pl-5 text-sm">
                {blockErrors.map((b) => (
                  <li key={b.block}><code>{b.block}</code>：{b.error || t("app.blockFailedNoMsg")}</li>
                ))}
              </ul>
              <div className="mt-1.5 text-sm">{t("app.blockFailedHint")}</div>
            </Notice>
          </div>
        )}
        {/* 初始化页本身就是起点：在那一页直接选，不用先点这个按钮 */}
        {!loadError && unconfigured && !configFree && page !== "init-config" && (
          <Panel>
            <div className="flex flex-col items-start gap-3 px-4 py-8">
              <div className="text-base font-medium text-ink">{t("ver.unconfiguredTitle")}</div>
              <div className="max-w-2xl text-sm text-ink-regular">{t("ver.unconfiguredBody")}</div>
              <Button tone="primary" disabled={busy} onClick={() => { void createFirstVersion(); }}>
                {t("ver.createFirst")}
              </Button>
            </div>
          </Panel>
        )}
        {/* 版本条只出现在版本化的四页；操作集中在总造价页，其余页只展示 */}
        {/* online 可为空：租户首个草稿阶段还没有生效版本，保存/发布入口必须照常在 */}
        {/* 玩法格页含只在服务端存在的格子（不在静态表里），用 isTilePage 补上 */}
        {/* 这一版还没有关卡时也要有：关卡页上「新建第一关」之前，人也得看到正在编辑哪一版、存没存上、还差几项（10-10 从零建代理时这一条整个不见了） */}
        {!loadError && !unconfigured && curVer && versionedPage && (
          <>
            {drift && (
              <div className="mb-3 flex flex-wrap items-center gap-3 rounded-base border border-danger-200 bg-danger-50 px-4 py-2">
                <span className="text-sm text-danger-700">
                  {drift.now
                    ? t("ver.driftBody", { was: drift.was ?? "—", now: drift.now })
                    : t("ver.driftBodyNone", { was: drift.was ?? "—" })}
                </span>
                {drift.now && curVid !== drift.now && (
                  <Button size="sm" onClick={() => { const to = drift.now!; setDrift(null); requestSwitch(to); }}>
                    {t("ver.driftGo", { id: drift.now })}
                  </Button>
                )}
                <Button size="sm" onClick={() => setDrift(null)}>{t("ver.driftDismiss")}</Button>
              </div>
            )}
            {!editable && (
              <div className="mb-3 flex flex-wrap items-center gap-3 rounded-base border border-warning-200 bg-warning-50 px-4 py-2">
                {/*
                  「已发布但不是生效版本」必须有一个**常驻**的激活入口。
                  只把它放在激活失败的抽屉里是不够的：抽屉一关、页面一刷新、
                  或者激活时只是网络抖了一下，那一版就再也激活不了了 ——
                  它已经只读（发布按钮禁用），版本历史里也只有查看/对比/回滚。
                */}
                <span className="text-sm text-warning-700">
                  {curVer.status === "published" ? t("ver.publishedNotLive", { id: curVid }) : t("ver.publishedBanner")}
                </span>
                {curVer.status === "published" && (
                  <Button size="sm" tone="danger" disabled={busy} onClick={() => setVerUi({ kind: "activate", v: curVer })}>
                    {t("ver.activateThis")}
                  </Button>
                )}
                <Button size="sm" tone="primary" disabled={busy} onClick={() => newDraftFrom(curVer, false)}>{t("ver.newDraftFrom")}</Button>
              </div>
            )}
            <VersionStrip
              ver={curVer}
              versions={versions}
              onlineId={online?.id ?? null}
              dirty={dirty}
              editable={editable && !busy}
              busy={busy}
              onDelete={() => setVerUi({ kind: "delete", id: curVid })}
              onDeleteDraft={(id) => setVerUi({ kind: "delete", id })}
              onSwitch={requestSwitch}
              save={saveState}
              onReload={() => { void reloadDraft(); }}
              onCheck={doCheckServer}
              onPublish={() => setVerUi({ kind: "publish" })}
              onHistory={() => setVerUi({ kind: "history" })}
              onDiff={() => { void compareCurrentToOnline(); }}
              onVersionLog={() => setLogView({ vid: curVid, scope: "one" })}
              onVersionLogOf={(vid) => setLogView({ vid, scope: "one" })}
              onPerson={openPersonLog}
              me={session.operator ?? undefined}
              onPruneDrafts={() => setVerUi({ kind: "prune" })}
              onTrash={() => setVerUi({ kind: "trash" })}
              // 只数阻断项：提示（severity=warn）不挡发布，单独显示条数，免得「还差 N 项」与「能发布」自相矛盾
              pending={pending && pending.key === editingKeyRef.current
                ? countPending(pending.rows)
                : null}
              onPending={() => { if (pending?.rows.length) setVerUi({ kind: "pubErrors", rows: pending.rows, from: "check" }); }}
            />
            {(page === "level-talent" || page === "pettown-talent") && (
              <TalentPageActions key={`${agent}|${curVid}`} draftId={curVid} onlineId={online?.id ?? null}
                revision={revs[curVid]} editable={editable && avail.talentNodes} blocked={busy || dirty || saveState.state === "conflict" || saveState.state === "error"}
                snapshot={takeSnap()}
                onLog={() => setLogView({ vid: curVid, scope: "one", blocks: TALENT_BLOCKS })}
                onRestore={(s) => { setTalentNodes(s.talentNodes ?? []); }} />
            )}
            {(() => {
              // 该页对应的后端业务块还没就绪：只读展示 + 明说，不给「可编辑但不落库」
              const block = ({ "global-dice": "common", "global-fight": "common", cost: "common", "pettown-star": "common", reward: "rewards", "card-item": "cards", "card-drop": "cards", "card-idol": "cards", auction: "auctions", "puzzle-list": "puzzles", "puzzle-price": "puzzles", "stage-images": "stages", "daily-reward": "daily", "social-channel": "socialChannels", "social-event-type": "socialEventTypes", frame: "frames", "pet-list": "pets", "pet-star": "petStars", "match-mode": "matchModes", "battle-stage": "battleStages", "ability-card": "abilityCards", "pettown-talent": "talentNodes", "level-talent": "talentNodes", "board-cells": "boardCells", "lamp-drops": "lampDrops", "idol-intimacy": "intimacy", "idol-photos": "idolPhotos", "idol-vip-photos": "idolVipPhotos", "idol-vip-videos": "idolVipVideos" } as const)[
                page as "global-dice"
              ];
              return block && !avail[block] ? (
                <div className="mb-3"><Notice tone="info">{t("ver.blockPending")}</Notice></div>
              ) : null;
            })()}
          </>
        )}
        {/*
          配置还没到手时不要往下渲染任何一页。
          之前直接渲染：建筑配置在 levels 为空时 lv 是 undefined，formulaCells 当场炸，
          ErrorBoundary 接住后整页停在红色堆栈上——刷新时正好停在这一页就永远打不开。
        */}
        {/*
          不吃配置的页先放行——它们卡在「正在载入配置…」后面纯属连坐。
          载入要拉十几张配置表加竞品数据，好几秒；而玩家面板是 <PlayerPage />
          零参数、客户端自留数据页只要一个 toast 回调，两者都跟配置无关
          （leo 2026-08-26：「每次都展示这个 好慢」）。
          新增页面时注意：只有真的不读 levels / spins / tiles 的才放这里。
        */}
        {page === "report-players" ? (
          <PlayerPage />
        ) : page === "recharge-records" ? (
          // 不读配置：只查 pay_order
          <RechargePage />
        ) : page === "match-monitor" ? (
          // 不读配置：它看的是队列 / 房间 / 已结算的实况，与当前编辑的配置版本无关
          <MatchMonitorPage a0={agent} />
        ) : page === "report-client-state" ? (
          <ClientStatePage onToast={setToast} />
        ) : page === "shop" ? (
          <ShopItemsPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "asset-info" ? (
          <AssetInfoPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "shop-sections" ? (
          <ShopSectionsPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "iap" ? (
          <IapPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "mail" ? (
          <MailPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "reports" ? (
          <ReportsPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "hot-update" ? (
          <HotUpdatePage key={agent ?? "-"} a0={agent} isSuper={!!isSuperAdmin} onToast={setToast} />
        ) : page === "push-events" ? (
          <PushEventsPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "share-links" ? (
          <ShareLinksPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "piggy" ? (
          <PiggyPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "pay-chain" ? (
          <PayChainPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : page === "agents" ? (
          <AgentsPage onToast={setToast} isSuper={!!isSuperAdmin} />
        ) : page === "accounts" ? (
          <AccountsPage onToast={setToast} self={session.operator} />
        ) : isLearningPage ? (
          // 与代理和配置无关：视频随前端打包，排名只读管端自己的进度表
          <HelpVideosPage key={session.operator ?? "anonymous"} view={page} operator={session.operator} />
        ) : page === "discord-bind" ? (
          // 不读配置：绑定与当前选中的代理无关
          <DiscordBindPage onToast={setToast} self={session.operator} />
        ) : page === "debug-trace" ? (
          // 与玩家面板同组：它不读配置，不该被「正在载入配置」挡住
          <DebugTracePage onToast={setToast} />
        ) : page === "gm" ? (
          // 不读配置：白名单是全局调试配置，与当前选中的代理无关
          <GmPage onToast={setToast} />
        ) : page === "dev-panel" ? (
          <DevPanelPage a0={agent} onToast={setToast} />
        ) : page === "dev-token" ? (
          <DevTokenPage a0={agent} onToast={setToast} />
        ) : page === "econ-switch" ? (
          // 不读配置（状态与版本列表自己拉），但按代理：换代理整页重挂
          <EconSwitchPage key={agent ?? "-"} a0={agent} onToast={setToast} />
        ) : !agent ? (
          /* 代理没选中时载入 effect 不会跑（见上），这里不能显示「正在载入」——那是永远等不到的 */
          <Panel><div className="py-10 text-center text-sm text-ink-placeholder">{t("app.noAgent")}</div></Panel>
        ) : loadError ? null : !snapLoaded ? (
          <Panel><div className="py-10 text-center text-sm text-ink-placeholder">{t("app.loadingConfig")}</div></Panel>
        ) : levels.length === 0 && !levelFree(page) ? (
          /*
            拉完了但一关都没有——空版本的正常起点，不是加载中。
            这里必须给出加第一关的入口：下面各页在 levels 为空时 lv 是 undefined，
            渲染会当场炸，所以不能直接放行；但也不能停在「载入中」，
            那样新建的版本永远填不了东西。

            **但这条闸只对真的要读 levels 的页面成立。** 拼图那两页一行 levels 都不碰，
            却会被这里拦下并显示「这一版还没有关卡 / 新建第一关」——
            文案与页面完全对不上，运营会以为拼图依赖关卡。
          */
          <Panel>
            <div className="flex flex-col items-start gap-3 px-4 py-10">
              <div className="text-base font-medium text-ink">{t("app.emptyVersionTitle")}</div>
              <div className="max-w-2xl text-sm text-ink-regular">{t("app.emptyVersionBody")}</div>
              {editable && (
                <Button tone="primary" disabled={busy} onClick={() => setAddingAt(1)}>
                  {t("app.addFirstLevel")}
                </Button>
              )}
            </div>
          </Panel>
        ) : page === "cost" ? (
          <div className="flex w-full flex-col gap-page">
          {curEcon.model === 2 && <EconModel2Notice />}
          {selectors.buildingSystem !== "pettown" && curEcon.model !== 2 && <div className="mb-3 rounded-base border border-line bg-white p-3 text-sm">
            <label data-api="common.unifiedB" className="flex items-center gap-2"><input type="checkbox" checked={dice.unifiedB === 1} disabled={!editable || !avail.common || (dice.unifiedB !== 1 && !!selectors.costCurve)} onChange={e => setDice({ ...dice, unifiedB: e.target.checked ? 1 : 0 })} />{t("cost.unifiedB.label")}</label>
            <p className="mt-1 text-xs text-ink-secondary">{t(dice.unifiedB === 1 ? "cost.unifiedB.on" : "cost.unifiedB.off")}{selectors.costCurve ? t("cost.unifiedB.curve") : ""}</p>
          </div>}
          <ParamForm groups={costGroups(selectors.buildingSystem, dice.unifiedB === 1, curEcon.model, (dice.targetBase ?? 0) > 0)} value={dice} editable={editable && avail.common} onSave={setDice} defaults={DEFAULT_DICE} api={DICE_LENS} compact placeholders={{ incomeAnchor: incomeAnchorPlaceholder, targetBase: t("p.targetBase.off"), targetGrowth: t("p.targetGrowth.default") }} extra={(group) => <button type="button" className="text-xs font-normal text-primary hover:underline" onClick={() => viewCurve((group.title === "cost.params.income" || group.title === "cost.unifiedB.group") ? "income" : group.title === "cost.params.rolls" ? "rolls" : "cost")}>{t("curve.view")}</button>}
            after={(group) => group.title === "cost.params.algorithm" && dice.unifiedB !== 1 ? <CostCurveEditor value={selectors.costCurve} editable={editable && avail.common && dice.unifiedB !== 1} onChange={(costCurve) => setSelectors(s => ({ ...s, costCurve }))} /> : undefined} />
          <EconomyCurves levels={levels} globals={globals} rivals={rivals} dice={dice.init} metric={curveMetric} onMetric={setCurveMetric} expanded={curveExpanded} onExpanded={setCurveExpanded}
            pace={avail.common && avail.spins && avail.talentNodes ? {spins,dice,nodes:talentNodes,costSource:selectors.costSource}:undefined} />
          <CostPage
            levels={levels}
            globals={globals}
            results={results}
            rivals={rivals}
            editable={editable}
            source={selectors.costSource}
            onSource={setCostSource}
            onActualC={(id, v) => setLevels((ls) => ls.map((l) => (l.id === id ? { ...l, actualC: v } : l)))}
            // 允许暂时清空（沿用 patchLevel）：以前空值被丢掉，删到最后一位就弹回原值、没法重填。
            // 曲线开启时清空覆盖会保存 null；关闭时沿用原 targetRolls 的非空保存规则。
            onTargetRolls={(id, v) => patchLevel(id, (globals.targetRollsBase ?? 0) > 0 ? { targetRollsOverride: v } : { targetRolls: v })}
            onG={(id, gMilli) => patchLevel(id, { gMilli })}
            onJump={(id, to) => { setSelId(id); setPage(to); }}
            onAddLevel={() => setAddingAt(levels.length + 1)}
            onAddRival={addRival}
            onRenameRival={setRenamingRival}
            onRemoveRival={setRemovingRival}
          />
          </div>
        ) : page === "card-item" ? (
          <CardPage
            kind="item" buildingSystem={selectors.buildingSystem} maxStar={pettown.maxStar ?? 0} starsPerLevel={pettown.starsPerLevel ?? 8}
            idolPacks={idolPacks} packs={itemPacks} itemPacks={itemPacks} levels={levels}
            editable={editable && avail.cards}
            onAdd={(p) => setItemPacks((xs) => [...xs, p])}
            onRemove={(id) => setItemPacks((xs) => xs.filter((p) => p.id !== id))}
            onSave={(p) => setItemPacks((xs) => xs.map((x) => (x.id === p.id ? p : x)))}
            onJump={setPage}
          />
        ) : page === "card-drop" ? (
          <ItemDropPage
            packs={itemPacks}
            range={playerLevelRange(selectors.buildingSystem, pettown.maxStar ?? 0, pettown.starsPerLevel ?? 8, levels)}
            editable={editable && avail.cards}
            onSaveLevel={async (level, entries) => {
              // 不进自动保存：专用接口一行原子写，然后读回草稿当新基线（修订号、packs 一起跟上；别处没存的改动三方合并保留）
              await putPackDropsApi(vnum(curVid), revs[curVid] ?? 0, [{ level, entries }]);
              const r = await reloadRef.current({ silent: true });
              setToast(r ? t("drop.saved", { level }) : t("drop.reloadFail"), !r);
            }}
            onJump={setPage}
          />
        ) : page === "card-idol" ? (
          <CardPage
            kind="idol"
            idolPacks={idolPacks} packs={idolPacks} itemPacks={itemPacks} levels={levels}
            editable={editable && avail.cards}
            onAdd={(p) => setIdolPacks((xs) => [...xs, p])}
            onRemove={(id) => setIdolPacks((xs) => xs.filter((p) => p.id !== id))}
            onSave={(p) => setIdolPacks((xs) => xs.map((x) => (x.id === p.id ? p : x)))}
            onJump={setPage}
          />
        ) : page === "reward" ? (
          <RewardPage
            levels={levels}
            globals={globals}
            rewards={rewards.filter((r) => (r.buildingSystem ?? "levels") === (selectors.buildingSystem === "pettown" ? "pettown" : "levels"))}
            buildingSystem={selectors.buildingSystem} maxStar={pettown.maxStar ?? 0}
            editable={editable && avail.rewards}
            itemCards={itemCards}
            idolCards={idolCards}
            focus={rewardFocus}
            onEdit={() => {}}
            onAdd={(lid) => setRewards((xs) => [...xs, { ...newReward(lid), id: `reward-${crypto.randomUUID()}`, buildingSystem: selectors.buildingSystem === "pettown" ? "pettown" : "levels" }])}
            onRemove={(id) => setRewards((xs) => xs.filter((r) => r.id !== id))}
            onSave={(r) => setRewards((xs) => xs.map((x) => (x.id === r.id ? r : x)))}
          />
        ) : page.startsWith("erhe-") ? (
          /* 二合任务成就（2026-10-29）：自己的表与接口，不走配置版本；前四个子页 = 配置表的四页（每日 / 每日宝箱 / 每周 / 成就），标签管理是弹窗底部标签 */
          <ErheTaskPage key={`${agent ?? "-"}/${page}`} module={page.slice("erhe-".length) as "daily" | "chest" | "weekly" | "achievement" | "tabs"} a0={agent} onToast={setToast} />
        ) : page.startsWith("achievement-") || page === "newbie-activity" ? (
          /*
            暂不走配置版本（用户 2026-09-07）：页面自己拉 / 存，不进快照、不看 editable。
            卡包名只是弹窗里的下拉提示，取当前载入的版本即可；真正的引用校验在服务端按生效版本做。
            三个子页（任务 / 成就 / 奖励配置）是同一个组件按 module 切列表与弹窗；key 带页 id，切页重置 tab。
          */
          /* Idol Ludo 那套；10-26 复制给二合用的 erhe-achievement-* 页已撤（二合改走上面的 ErheTaskPage），服务端 ?game=erhe 的接口留着 */
          <AchievementPage
            key={`${agent ?? "-"}/${page}`}
            game="ludo"
            module={page === "newbie-activity" ? "newbie" : page.slice(page.indexOf("achievement-") + "achievement-".length) as Module}
            a0={agent}
            dict={achDict}
            itemCards={itemCards}
            idolCards={idolCards}
            onToast={setToast}
          />
        ) : page === "pettown-params" ? (
          <PettownParamsPage
            system={selectors.buildingSystem}
            pettown={pettown}
            editable={editable && avail.common}
            // 改星数上限 → 星级表按它铺行；调低时超出上限的那些星的覆盖一起截掉
            // 超出星数上限的覆盖行不删（见 doSaveServer 的 starsOut）：参数边改边存，「12」会先经过「1」
            onPettown={setPettown}
            onGotoInit={() => setPage("init-config")}
          />
        ) : page === "pettown-star" ? (
          <div className="flex flex-col gap-page">
          {curEcon.model === 2 && <EconModel2Notice unit="star" />}
          {curEcon.model !== 2 && <div className="mb-3 rounded-base border border-line bg-white p-3 text-sm">
            <label data-api="common.unifiedB" className="flex items-center gap-2"><input type="checkbox" checked={dice.unifiedB === 1} disabled={!editable || !avail.common || (dice.unifiedB !== 1 && !!selectors.costCurve)} onChange={e => setDice({ ...dice, unifiedB: e.target.checked ? 1 : 0 })} />{t("cost.unifiedB.label")}</label>
            <p className="mt-1 text-xs text-ink-secondary">{t(dice.unifiedB === 1 ? "pt.econ.unifiedOn" : "cost.unifiedB.off")}{selectors.costCurve ? t("cost.unifiedB.curve") : ""}</p>
          </div>}
          <ParamForm groups={costGroups(selectors.buildingSystem, dice.unifiedB === 1, curEcon.model, (dice.targetBase ?? 0) > 0)} value={dice} editable={editable && avail.common} onSave={setDice} defaults={DEFAULT_DICE} api={DICE_LENS} compact placeholders={{ incomeAnchor: incomeAnchorPlaceholder, targetBase: t("p.targetBase.off"), targetGrowth: t("p.targetGrowth.default") }} extra={(group) => <button type="button" className="text-xs font-normal text-primary hover:underline" onClick={() => viewCurve((group.title === "cost.params.income" || group.title === "cost.unifiedB.group") ? "income" : group.title === "cost.params.rolls" ? "rolls" : "cost")}>{t("curve.view")}</button>}
            after={(group) => group.title === "cost.params.algorithm" && dice.unifiedB !== 1 ? <CostCurveEditor unit="star" value={selectors.costCurve} editable={editable && avail.common && dice.unifiedB !== 1} onChange={(costCurve) => setSelectors(s => ({ ...s, costCurve }))} /> : undefined} />
          <EconomyCurves unit="star" levels={[]} progress={pettownStarsView.map(r=>r.star)} series={starCurveSeries} globals={globals} rivals={rivals} dice={dice.init} metric={curveMetric} onMetric={setCurveMetric} expanded={curveExpanded} onExpanded={setCurveExpanded} />
          <PettownStarPage
            stars={pettownStarsView}
            onApplyPrices={(rows) => setPettownStars(xs => { const m = new Map(xs.map(r=>[r.star,r])); for(const r of rows) if(!r.bad&&r.formula>0n) m.set(r.star,{...(m.get(r.star)??{star:r.star,steps:null,skinUrl:"",targetRolls:null,bValue:null}),manualPrice:r.formula.toString()}); return [...m.values()].sort((a,b)=>a.star-b.star); })}
            incomeSeries={starCurveSeries.filter(s=>s.metric==="income")}
            slots={pettownSlots}
            editable={editable && avail.pettownStars}
            econ={{ ...curEcon, gByStar: Object.fromEntries(pettownStars.flatMap((x) => (x.gMilli ? [[x.star, x.gMilli]] : []))) }}
            onPatch={(star, patch) => setPettownStars((xs) => setStarOverride(xs, star, patch))}
            onRivalPrice={(rid, star, v) => setPettownRivalPrices((m) => {
              const col = { ...(m[rid] ?? {}) };
              if (v == null) delete col[star]; else col[star] = v;
              return { ...m, [rid]: col };
            })}
            rivals={rivals}
            costSource={selectors.costSource}
            onSource={setCostSource}
            onAddRival={addRival}
            onRenameRival={setRenamingRival}
            onRemoveRival={setRemovingRival}
          />
          </div>
        ) : page === "pettown-slot" ? (
          <PettownSlotPage
            slots={pettownSlots}
            mainSkin={pettownStars.find((r) => r.skinUrl)?.skinUrl}
            maxStar={pettown.maxStar ?? 0}
            editable={editable && avail.pettownSlots}
            itemCards={itemCards}
            idolCards={idolCards}
            onPatch={(i, patch) => setPettownSlots((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onAdd={() => setPettownSlots((xs) => [...xs, newSlot(xs)])}
            onRemove={(i) => setPettownSlots((xs) => xs.filter((_, xi) => xi !== i))}
          />
        ) : page === "pettown-talent" || page === "level-talent" ? (
          <TalentPage key={curVid}
            system={page === "level-talent" ? "levels" : "pettown"}
            cities={levels}
            nodes={talentNodes}
            starsPerLevel={pettown.starsPerLevel}
            start={talentStart({ shieldMax: fight.wardCap, friendLimit: fight.friendLimit, stakeTierBase: fight.stakeTierBase, giftCap: fight.giftCap,
              diceRegenSec: dice.regen != null ? Math.round(dice.regen * 60) : null }, pettownSlots.find((s) => s.kind === "stamina_tree") ?? null)}
            giftCapUnlimited={fight.giftCap === 0}
            stakeTiers={[...new Set(matchModes.flatMap((m) => m.tiers ?? []))].sort((a, b) => a - b)}
            matchMode={selectors.gameMode === "match"}
            editable={editable && avail.talentNodes}
            onPatch={(i, patch) => setTalentNodes((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onAdd={() => setTalentNodes((xs) => [...xs, newTalentNode(xs)])}
            onRemove={(i) => setTalentNodes((xs) => xs.filter((_, xi) => xi !== i))}
          />
        ) : page === "global-dice" ? (
          <>
          <ParamForm
            groups={diceGroups(selectors.buildingSystem)} value={dice} editable={editable && avail.common} onSave={setDice}
            defaults={DEFAULT_DICE} api={DICE_LENS}
          />
          </>
        ) : page === "idle-income" ? (
          <IdleIncomePage dice={dice} editable={editable && avail.common} onDice={setDice} levels={levels} globals={globals} costSource={selectors.costSource} />
        ) : page === "global-fight" ? (
          <>
            {selectors.buildingSystem === "pettown" && <div className="mb-3"><Notice tone="info" actions={<Button size="sm" onClick={() => setPage("pettown-params")}>{t("p.townRangeLink")}</Button>}>{t("p.townRangeHint")}</Notice></div>}
            <ParamForm groups={fightGroups(selectors.buildingSystem)} value={fight} editable={editable && avail.common} onSave={setFight} defaults={DEFAULT_FIGHT} api={FIGHT_LENS} />
          </>
        ) : page === "spin" ? (
          <SpinPage
            econ={{ model: curEcon.model, scale: econScale }}
            spins={spins}
            gameMode={selectors.gameMode}
            tiles={tiles}
            eB={unitE}
            clientPick={dice.subgamePick === 1}
            warpProb={dice.warpProb}
            warpEditable={editable && avail.common}
            onWarpProb={(v) => setDice((d) => ({ ...d, warpProb: v }))}
            editable={editable && avail.spins}
            onPatch={(id, patch) => setSpins((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch } : x)))}
            onAdd={() => setSpins((xs) => [...xs, newSpin(Math.max(0, ...xs.map((x) => x.code)) + 1)])}
            onRemove={(id) => setSpins((xs) => xs.filter((x) => x.id !== id))}
            onJump={setPage}
          />
        ) : page === "init-config" ? (
          <InitConfigPage
            key={curVid}
            value={{ gameMode: selectors.gameMode as "" | "solo" | "match", buildingSystem: selectors.buildingSystem, buildingPresentation: selectors.buildingPresentation }}
            buildingSet={selectors.buildingSet}
            // 还没有任何版本：照样可选，选第一项时先建第一版（见 pendingInitRef）
            editable={(editable && avail.common) || (unconfigured && !busy && !loadError)}
            // 还没有任何版本、而且是超管：可以拿别的代理的生效版本当底稿（只列已有生效配置的代理）
            copyFrom={unconfigured && !loadError && isSuperAdmin ? {
              agents: agentOptions.filter((a) => a.a0 !== agent && a.hasOwnConfig).map((a) => ({ a0: a.a0, name: a.name })),
              busy,
              onCopy: (from) => { if (!pendingInitRef.current) void createFirstVersion(from); },
            } : undefined}
            // 匹配玩法列表不在配置快照里（cfg_match_mode 有自己的管端接口），这里给不出数量
            matchModeCount={null}
            locks={initLocks}
            // 页面只回传它管的两项，建筑方案（building_set）不在这一页，合并回去别覆盖掉
            onSave={(v) => {
              if (unconfigured) {
                // 第一版还在建：记下最新的选择就行（建好后一并存），别再建一次
                const building = !!pendingInitRef.current;
                pendingInitRef.current = { agent, v };
                if (!building) void createFirstVersion();
                return;
              }
              setSelectors({ ...selectors, ...v });
            }}
          />
        ) : page === "puzzle-list" ? (
          /*
            key={curVid}：切版本时整页重挂。页面里有两份跟着版本走的本地状态 ——
            「本次会话新建的行」（决定 idol/seq 能不能改）与 idol/授权批次的缓存。
            不重挂的话，切到另一版后那两份会跟着旧版本的判断走。
          */
          <PuzzlePage
            key={curVid}
            puzzles={puzzles}
            prices={puzzlePrices}
            editable={editable && avail.puzzles}
            onAdd={(p) => setPuzzles((xs) => [...xs, p])}
            // 按 localKey 定位：新建行还没有服务端 ID，用 puzzleId 匹配会让
            // 多个新行（都是 null）互相串行，编辑一个改掉另一个
            onSave={(p) => setPuzzles((xs) => xs.map((x) => (x.localKey === p.localKey ? p : x)))}
            onRemove={(key) => setPuzzles((xs) => xs.filter((x) => x.localKey !== key))}
            onJump={setPage}
          />
        ) : page === "stage-images" ? (
          <StageImagesPage
            key={curVid}
            stages={stages}
            images={stageImages}
            editable={editable && avail.stages}
            onAddStage={(x) => setStages((xs) => [...xs, x])}
            // 按 localKey 定位：新建行还没有服务端 ID
            onSaveStage={(x) => setStages((xs) => xs.map((r) => (r.localKey === x.localKey ? x : r)))}
            // 删关时连同它名下的图一起拿掉（服务端同事务连带删，保存流程据回包摘基线）
            onRemoveStage={(x) => {
              setStages((xs) => xs.filter((r) => r.localKey !== x.localKey));
              setStageImages((xs) => xs.filter((r) => !belongsTo(r, x)));
            }}
            onAddImage={(x) => setStageImages((xs) => [...xs, x])}
            onSaveImage={(x) => setStageImages((xs) => xs.map((r) => (r.localKey === x.localKey ? x : r)))}
            onRemoveImage={(key) => setStageImages((xs) => xs.filter((r) => r.localKey !== key))}
          />
        ) : page === "pet-list" ? (
          // key={curVid}：切版本时整页重挂，否则弹窗里那份 useState 拷贝还是旧版本的行
          <PetPage
            key={curVid}
            pets={pets}
            editable={editable && avail.pets}
            onAdd={(p) => setPets((xs) => [...xs, normalizePet(p)])}
            // 按 localKey 定位：新建行的 petId 可能还是空串，用它匹配会让多个新行互相串行
            onSave={(p) => setPets((xs) => xs.map((x) => (x.localKey === p.localKey ? normalizePet(p) : x)))}
            onRemove={(key) => setPets((xs) => xs.filter((x) => x.localKey !== key))}
          />
        ) : page === "pet-star" ? (
          <PetStarPage
            steps={petStars}
            params={petStar}
            // 档位表与全局参数分属两张表，可编性也各看各的：
            // 升星档块拿不到时表只读，但全局那五个仍跟着 common 走
            editable={editable && avail.petStars}
            onStepsChange={setPetStars}
            onParamsChange={setPetStar}
          />
        ) : page === "idol-intimacy" ? (
          <IntimacyPage
            levels={intimacyLevels}
            sources={intimacySources}
            sourceDefs={intimacySourceDefs}
            params={intimacy}
            // 两张表跟亲密度块走；每日上限那一格跟 common 块走（common 没加载到时保存不发它的 PATCH）
            editable={editable && avail.intimacy}
            paramsEditable={editable && avail.common}
            onLevelsChange={setIntimacyLevels}
            onSourcesChange={setIntimacySources}
            onParamsChange={setIntimacy}
          />
        ) : page === "daily-reward" ? (
          <DailyRewardPage
            value={dailyRewards}
            editable={editable && avail.daily}
            itemCards={itemCards}
            idolCards={idolCards}
            onChange={setDailyRewards}
            onToast={setToast}
          />
        ) : page === "match-mode" ? (
          <MatchModePage
            modes={matchModes}
            itemPacks={itemPacks.map((p) => ({ packId: p.id, name: p.name, i18n: p.i18n, coverImg: p.coverImg }))}
            topBonusImg={selectors.topBonusImg}
            imgEditable={editable && avail.common}
            onTopBonusImg={(topBonusImg) => setSelectors((s) => ({ ...s, topBonusImg }))}
            editable={editable && avail.matchModes}
            onPatch={(i, patch) => setMatchModes((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onAdd={(mode) => setMatchModes((xs) => [...xs, newMatchMode(mode)])}
            onRemove={(i) => setMatchModes((xs) => xs.filter((_, xi) => xi !== i))}
          />
        ) : page === "battle-stage" ? (
          <BattleStagePage
            stages={battleStages}
            cards={abilityCards}
            itemCards={itemCards}
            idolCards={idolCards}
            editable={editable && avail.battleStages}
            onPatch={(i, patch) => setBattleStages((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onPatchCard={(i, patch) => setAbilityCards((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onAdd={() => setBattleStages((xs) => [...xs, newBattleStage(xs)])}
            onAddMany={(ss) => setBattleStages((xs) => [...xs, ...ss.filter((s) => !xs.some((x) => x.stage === s.stage))])}
            onRemove={(i) => setBattleStages((xs) => xs.filter((_, xi) => xi !== i))}
          />
        ) : page === "ability-card" ? (
          // 图上传隔着一次往返才写值，三个回调走函数式更新（同社交频道页）
          <AbilityCardPage
            cards={abilityCards}
            stages={battleStages}
            editable={editable && avail.abilityCards}
            onPatch={(i, patch) => setAbilityCards((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onAdd={() => setAbilityCards((xs) => [...xs, newAbilityCard(xs)])}
            onAddMany={(cs) => setAbilityCards((xs) => [...xs, ...cs.filter((c) => !xs.some((x) => x.effectKind === c.effectKind))])}
            onRemove={(i) => setAbilityCards((xs) => xs.filter((_, xi) => xi !== i))}
          />
        ) : page === "social-channel" ? (
          /*
           * 三个回调一律走函数式更新（同 spin 页）：图标上传隔着一次往返回来才写值，
           * 拿渲染那一刻的数组去 map 会把上传期间的其他编辑一起还原掉。
           */
          <SocialChannelPage
            channels={socialChannels}
            editable={editable && avail.socialChannels}
            itemCards={itemCards}
            idolCards={idolCards}
            onPatch={(i, patch) => setSocialChannels((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onAdd={() => setSocialChannels((xs) => [...xs, newChannel(xs)])}
            onRemove={(i) => setSocialChannels((xs) => xs.filter((_, xi) => xi !== i))}
          />
        ) : page === "social-event-type" ? (
          <SocialEventTypePage
            types={socialEventTypes}
            editable={editable && avail.socialEventTypes}
            onPatch={(i, patch) => setSocialEventTypes((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onAdd={() => setSocialEventTypes((xs) => [...xs, newEventType(xs)])}
            onRemove={(i) => setSocialEventTypes((xs) => xs.filter((_, xi) => xi !== i))}
          />
        ) : page === "frame" ? (
          <FramePage
            frames={frames}
            editable={editable && avail.frames}
            onPatch={(i, patch) => setFrames((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onAdd={() => setFrames((xs) => [...xs, newFrame(xs)])}
            onRemove={(i) => setFrames((xs) => xs.filter((_, xi) => xi !== i))}
          />
        ) : page === "board-cells" ? (
          <BoardCellPage
            cells={boardCells}
            spins={spins}
            gameMode={selectors.gameMode}
            editable={editable && avail.boardCells}
            onPatch={(index, patch) => setBoardCells((xs) => xs.map((x) => (x.index === index ? { ...x, ...patch } : x)))}
            onReset={(cells) => setBoardCells(cells)}
          />
        ) : page === "lamp-drops" ? (
          <LampDropPage
            drops={lampDrops}
            itemPacks={itemPacks.map((p) => ({ packId: p.id, name: p.name }))}
            editable={editable && avail.lampDrops}
            onPatch={(i, patch) => setLampDrops((xs) => xs.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
            onReplace={(i, row) => setLampDrops((xs) => xs.map((x, xi) => (xi === i ? row : x)))}
            threshold={lamp.pityThreshold}
            thresholdEditable={editable && avail.common}
            onThreshold={(v) => setLamp({ pityThreshold: v })}
          />
        ) : page === "idol-photos" ? (
          <IdolPhotoPage
            key={curVid}
            photos={idolPhotos}
            // 第 1 张（拼图完成照）取这里拼图行的完成图只读展示；照片只能挂在已落库的拼图下
            puzzles={puzzles}
            editable={editable && avail.idolPhotos}
            onAdd={(p) => setIdolPhotos((xs) => [...xs, p])}
            // 按 localKey 定位：新建行还没有服务端 ID
            onSave={(p) => setIdolPhotos((xs) => xs.map((x) => (x.localKey === p.localKey ? p : x)))}
            onRemove={(key) => setIdolPhotos((xs) => xs.filter((x) => x.localKey !== key))}
            onJump={setPage}
          />
        ) : page === "idol-vip-photos" ? (
          <IdolVipPhotoPage
            key={curVid}
            rows={idolVipPhotos}
            editable={editable && avail.idolVipPhotos}
            onAdd={(p) => setIdolVipPhotos((xs) => [...xs, p])}
            onSave={(p) => setIdolVipPhotos((xs) => xs.map((x) => (x.localKey === p.localKey ? p : x)))}
            onRemove={(key) => setIdolVipPhotos((xs) => xs.filter((x) => x.localKey !== key))}
          />
        ) : page === "idol-vip-videos" ? (
          <IdolVipVideoPage
            key={curVid}
            rows={idolVipVideos}
            editable={editable && avail.idolVipVideos}
            onAdd={(p) => setIdolVipVideos((xs) => [...xs, p])}
            onSave={(p) => setIdolVipVideos((xs) => xs.map((x) => (x.localKey === p.localKey ? p : x)))}
            onRemove={(key) => setIdolVipVideos((xs) => xs.filter((x) => x.localKey !== key))}
          />
        ) : page === "puzzle-price" ? (
          <PuzzlePricePage
            prices={puzzlePrices}
            puzzles={puzzles}
            editable={editable && avail.puzzles}
            onChange={setPuzzlePrices}
            onJump={setPage}
            onToast={setToast}
          />
        ) : page === "report-spin" ? (
          <ReportPage activeVersion={online ? Number(online.id.slice(1)) : null} levels={levels} />
        ) : page === "sim" ? (
          <SimPage
            levels={levels} spins={spins} tiles={tiles} rivals={rivals}
            globals={{ unifiedB: dice.unifiedB === 1, algorithmAnchorB: levels.find(lv => lv.id === 1)?.B, costCurve: selectors.costCurve, E: dice.E ?? DEFAULT_GLOBALS.E, rho: dice.rho ?? DEFAULT_GLOBALS.rho, incomeGrowthBps: Math.round((dice.incomeK ?? 0) * 10000), incomeAnchorB: dice.incomeAnchor ?? 0, targetRollsBase: dice.targetBase ?? 0, targetRollsGrowthBps: Math.round((dice.targetGrowth ?? 0) * 10000),
              // 金额口径跟着正在看的版本（docs/economy-g-model.md §2.4）：model 2 按 q × 刻度 × 本关 g 模拟
              economyModel: curEcon.model, moneyScaleNum: curEcon.scale ? Number(curEcon.scale.num) : null, moneyScaleDen: curEcon.scale ? Number(curEcon.scale.den) : null }}
            editable={editable}
            onRange={(id, rollsRange) => patchLevel(id, { rollsRange })}
            dice={dice}
          />
        ) : page === "subgame" ? (
          <TileOverview tiles={tiles} onOpen={setPage} />
        ) : page === "act-item" ? (
          /*
            这一页管两件保存即生效的东西：活动道具名词表、活动排期与档位，都由页面自己
            拉自己存（同任务成就页），不进 takeSnap。传 spins 只为算节奏预估——
            掉落项本身在「spin 奖励配置」改。
          */
<div className="flex flex-col gap-page">
            {/*
              活动道具格（副玩法组）并在这一页顶上（2026-10-06 leo：两个「活动道具」入口运营看懵了）。
              它跟配置版本走、下面两块保存即生效，所以单独一块、标明「随配置版本」，保存走顶上的版本条
            */}
            <Panel>
              <SectionTitle extra={<Tag tone="warning">{t("act.tileTag")}</Tag>}>{t("act.tileTitle")}</SectionTitle>
              <div className="text-xs leading-relaxed text-ink-placeholder">{t("act.tileNote")}</div>
              {/* 这一版还没有这一格：可编辑时直接给默认值的表单（改一项就建上），不再先点「新建这一格」 */}
              {!tiles.some((x) => x.key === ACT_ITEM_TILE) && !(editable && avail.tiles) && (
                <div className="mt-3 text-xs text-ink-placeholder">{t("act.tileMissing")} · {t(!editable ? "tile.missingReadonly" : "tile.missingUnavail")}</div>
              )}
            </Panel>
            {(() => {
              const tile = tiles.find((x) => x.key === ACT_ITEM_TILE) ?? (editable && avail.tiles ? blankTile(ACT_ITEM_TILE) : null);
              return tile ? tilePageFor(tile) : null;
            })()}
                      <ActEventPage
            key={`${agent ?? "-"}/act-item`}
            a0={agent}
            matchMode={selectors.gameMode === "match"}
            spins={spins}
            itemCards={itemCards}
            idolCards={idolCards}
            onJump={setPage}
            onToast={setToast}
          />
          </div>
        ) : page.startsWith("subgame-") && !tiles.some((x) => `subgame-${x.key}` === page) && !(editable && avail.tiles && blankTile(page.replace("subgame-", ""))) ? (
          /*
            菜单里列了这个格子，但这一版还没配它、而且现在建不了（只读版本 / tiles 块没读到）：说清楚为什么。
            可编辑时不走这里——下面直接给默认值的表单，改一项就把这一格建上（以前要先点「新建这一格」，实时保存之后那一步是多余的）
          */
          <Panel>
            <div className="flex flex-col items-start gap-3 px-4 py-10">
              <div className="text-base font-medium text-ink">{t("tile.missingTitle")}</div>
              <div className="max-w-2xl text-sm text-ink-regular">{t("tile.missingBody")}</div>
              {!editable ? (
                /* 只读版本（已发布/生效）没有新建入口：不给出路的空状态等于死胡同（截图实锤） */
                <div className="text-xs text-ink-placeholder">{t("tile.missingReadonly")}</div>
              ) : (
                /* 草稿可编辑但 tiles 数据块不可用（接口失败/未加载）：不能谎报成「已发布只读」，
                   那会把人指去建草稿——他明明已经在草稿里 */
                <div className="text-xs text-ink-placeholder">{t("tile.missingUnavail")}</div>
              )}
            </div>
          </Panel>
        ) : page.startsWith("subgame-") ? (
          tilePageFor(tiles.find((x) => `subgame-${x.key}` === page) ?? blankTile(page.replace("subgame-", ""))!)
        ) : page === "auction" ? (
          <AuctionPage
            levels={levels}
            auctions={auctions}
            defaults={aucDefaults}
            editable={editable && avail.auctions}
            onPatch={(levelId, periods) => setAuctions((m) => ({ ...m, [levelId]: periods }))}
            onSaveDefaults={(d) => { setAucDefaults(d); logAction("config.aucDefaults", undefined, d, agent ?? undefined); }}
            onAddPeriod={(d) => {
              // 全关卡一起开：漏掉任何一关，那一关的玩家这一期就没有舞台可拍
              setAuctions((m) => {
                const next: AuctionMap = { ...m };
                for (const l of levels) next[l.id] = [...(m[l.id] ?? []), newPeriod(d)];
                return next;
              });
              logAction("config.aucAddPeriod", `levels:${levels.length}`, d, agent ?? undefined);
            }}
          />
        ) : page === "level" ? (
          <LevelPage
            buildingPresentation={selectors.buildingPresentation}
            levels={levels}
            results={results}
            editable={editable}
            onNames={(id) => { setSelId(id); setNamesFor(id); }}
            onImage={(id, field, url) => patchLevel(id, { [field]: url } as Partial<Level>)}
            onAddLevel={() => setAddingAt(levels.length + 1)}
            onJump={(id) => { setSelId(id); setPage("building"); }}
            onAssets={(id, la) => patchLevel(id, { levelAssets: la })}
            auctionLevels={Object.keys(auctions).map(Number).filter((k) => (auctions[k]?.length ?? 0) > 0)}
            onMove={async (from, to) => {
              /*
               * 拖动改编号是服务端一个事务（config.ts moveLevel），不走逐块自动保存：手上有没存完的改动时先不让动，
               * 否则那些改动按旧编号发出去会落到别的关上。做完读回草稿（按 uid 合，各页跟着新编号走）
               */
              if (dirty || savingRef.current || busyRef.current || remoteReload.current.running || saveState.state === "saving" || saveState.state === "conflict" || pendingAdds.length) {
                throw new Error(t("level.move.waitSave"));
              }
              busyRef.current = true;
              try {
                const r = await moveLevelApi(vnum(curVid), revsRef.current[curVid] ?? 0, from, to);
                setRevs((m) => ({ ...m, [curVid]: Math.max(m[curVid] ?? 0, r.revision) }));
                collabAnnounceRef.current(r.revision, "levels");
                // 服务端已经挪完；读回失败时手上还是旧编号，继续改会落到别的关上——明确报出来、让人刷新
                if (!(await reloadDraft({ silent: true }))) throw new Error(t("level.move.reloadFail"));
                return { unfixable: r.unfixable };
              } finally {
                busyRef.current = false;
              }
            }}
            onRemove={async (level) => {
              // 与拖动同理：删关是服务端一个事务，手上有没存完的改动时先不让删，做完读回草稿
              if (dirty || savingRef.current || busyRef.current || remoteReload.current.running || saveState.state === "saving" || saveState.state === "conflict" || pendingAdds.length) {
                throw new Error(t("level.del.waitSave"));
              }
              busyRef.current = true;
              try {
                const r = await deleteLevelApi(vnum(curVid), revsRef.current[curVid] ?? 0, level);
                setRevs((m) => ({ ...m, [curVid]: Math.max(m[curVid] ?? 0, r.revision) }));
                collabAnnounceRef.current(r.revision, "levels");
                if (!(await reloadDraft({ silent: true }))) throw new Error(t("level.del.reloadFail"));
                return { unfixable: r.unfixable };
              } finally {
                busyRef.current = false;
              }
            }}
          />
        ) : page === "building" ? (
          <BuildingPage
            buildingPresentation={selectors.buildingPresentation}
            levels={levels}
            globals={globals}
            results={results}
            rivals={rivals}
            editable={editable}
            // 竞品列随版本存，逐格手填（2026-09-16）；客户端在用的那列表头打标
            costSource={selectors.costSource}
            selId={selId}
            onSelLevel={setSelId}
            onStructure={(id, field, v) => {
              // N/M 变化会改变建筑配置页的行数：新增的格子留空待填，不外推
              if (field === "N" && typeof v === "number") {
                const lv = levels.find((l) => l.id === id)!;
                const buildings = Array.from({ length: v }, (_, i) =>
                  lv.buildings[i] ?? { code: `${id}-building-${i + 1}`, name: `Building ${i + 1}`, main: false });
                const bldInit = Array.from({ length: v }, (_, i) => lv.bldInit[i] ?? null);
                patchLevel(id, { N: v, buildings, bldInit });
                return;
              }
              if (field === "M" && typeof v === "number") {
                const lv = levels.find((l) => l.id === id)!;
                patchLevel(id, { M: v, star: Array.from({ length: v }, (_, i) => lv.star[i] ?? null) });
                return;
              }
              patchLevel(id, { [field]: v } as Partial<Level>);
            }}
            onBatchGrid={() => setBatching(true)}
            onMatrixSeed={(seed) => {
              const current = levels.find((l) => l.id === selId) ?? levels[0];
              if (seed === 0 && current.costMatrixSeed == null) {
                setLevels((rows) => rows.map((lv) => lv.costMatrixSeed == null ? { ...lv, costMatrixSeed: 0 } : lv));
              } else patchLevel(current.id, { costMatrixSeed: seed });
            }}
            onRival={(rid, k, v) => {
              const lv = levels.find((l) => l.id === selId)!;
              const grid = { ...(lv.rivals[rid] ?? {}) };
              if (v == null) delete grid[k]; else grid[k] = v;
              patchLevel(selId, { rivals: { ...lv.rivals, [rid]: grid } });
            }}
            onBuilding={(b, patch) => {
              const lv = levels.find((l) => l.id === selId)!;
              patchLevel(selId, { buildings: lv.buildings.map((x, i) => (i === b - 1 ? { ...x, ...patch } : x)) });
            }}
            onAddRival={addRival}
            onRenameRival={setRenamingRival}
            onRemoveRival={setRemovingRival}
            onAssets={setAssetCell}
          />
        ) : item?.done ? (
          <div className="rounded-base border border-line-lighter bg-white p-10 text-center text-sm text-ink-placeholder">
            {item.label}
          </div>
        ) : (
          <ComingSoon page={page} />
        )}
        </>
        )}
        </Suspense>
      </ErrorBoundary>

      {showLog && <AuditDrawer onClose={() => setShowLog(false)} />}
      <CollabLayer peers={collab.peers.filter((p) => sameCollabView(p, collabView))} ghosts={collab.ghosts.filter((g) => sameCollabView(g, collabView))} page={page} showCursors={showCursors} me={session.operator ?? ""} />
      {followed && <FollowBanner peer={followed} onStop={() => setFollowing(null)} />}
      <MergedNotice notice={merged} onDone={clearMerged} />
      {logView && (
        <VersionLogDrawer
          key={`${agent ?? ""}|${logView.vid}|${logView.scope}|${logView.who ?? ""}`}
          version={vnum(logView.vid)}
          versions={versions}
          blocks={logView.blocks}
          loadSnapshot={ensureSnapshot}
          undoBlocked={dirty || saveState.state !== "idle"}
          onUndoApplied={(v) => {
            const forAgent = agent;
            setLogView(null);
            switchVersion(vid(v));
            void fetchVersions().then((rows) => { if (agentRef.current === forAgent) setVersions(rows.map(toVersion)); })
              .catch((e) => console.warn("刷新撤销后的版本列表失败", e));
          }}
          onCompare={(v) => { setLogView(null); compareVersionToSource(v); }}
          initScope={logView.scope}
          initWho={logView.who ?? null}
          onClose={() => setLogView(null)}
          onOpenFull={(who, scope) => { setLogFull({ vid: scope === "all" ? null : logView.vid, who }); setLogView(null); }}
          onPickVersion={(id) => { setLogView(null); requestSwitch(id); }}
        />
      )}
      {logFull && (
        <AuditDrawer
          version={logFull.vid ? vnum(logFull.vid) : undefined}
          actor={logFull.who ?? undefined}
          title={logFull.vid ? t("ver.versionLogTitle", { id: logFull.vid }) : undefined}
          onClose={() => setLogFull(null)}
        />
      )}

      {verUi?.kind === "publish" && curVer && (
        <PublishDialog
          levels={levels} results={results} curVid={curVid} from={curVer.from} base={curVer.base} onlineId={online?.id ?? null} diff={online ? pubDiff : undefined}
          isSuper={!!isSuperAdmin}
          draftNote={curVer.note}
          onClose={() => setVerUi(null)} onPublish={doPublish} onCheck={() => setVerUi({ kind: "check" })}
        />
      )}
      {verUi?.kind === "activate" && (
        <ActivateDialog
          v={verUi.v} onlineId={online?.id ?? null} isSuper={!!isSuperAdmin} busy={busy}
          initialMode={lastRefreshVidRef.current === verUi.v.id ? lastRefreshModeRef.current : "normal"}
          onClose={() => setVerUi(null)}
          onActivate={(mode) => doActivateOnly(verUi.v.id, mode)}
        />
      )}
      {verUi?.kind === "mergeConflicts" && (
        <Modal
          title={t("ver.mergeConflictTitle")}
          width={620}
          onClose={() => setVerUi(null)}
          footer={
            <div className="flex justify-end gap-2">
              <Button size="sm" onClick={() => setVerUi(null)}>{t("common.cancel")}</Button>
              <Button size="sm" tone="danger" disabled={busy}
                onClick={() => { const { mode, onto } = verUi; setVerUi(null); runExclusive(() => doPublishServer(mode, onto)); }}>
                {t("ver.mergeKeepDraft")}
              </Button>
            </div>
          }
        >
          <Notice tone="danger">{verUi.message}</Notice>
          <div className="mt-3 text-sm text-ink-secondary">{t("ver.mergeKeepNote", { online: vid(verUi.onto) })}</div>
        </Modal>
      )}
      {verUi?.kind === "staleBase" && online && (
        <Modal
          title={t("ver.staleBaseTitle")}
          width={520}
          onClose={() => setVerUi(null)}
          footer={
            <div className="flex justify-end gap-2">
              <Button size="sm" onClick={() => setVerUi(null)}>{t("common.cancel")}</Button>
              <Button size="sm" onClick={() => { const v = versions.find((x) => x.id === online.id); setVerUi(null); if (v) newDraftFrom(v, false); }}>
                {t("ver.staleBaseUseOnline", { id: online.id })}
              </Button>
              <Button size="sm" tone="danger" disabled={busy}
                onClick={() => { const { v, isRollback } = verUi; setVerUi(null); runExclusive(() => newDraftFromServer(v, isRollback)); }}>
                {t("ver.staleBaseGoOn")}
              </Button>
            </div>
          }
        >
          <Notice tone="danger">{t("ver.staleBaseBody", { base: verUi.v.id, online: online.id })}</Notice>
          <div className="mt-3 text-sm text-ink-secondary">
            {t("ver.staleBaseHint", {
              n: String(versions.filter((x) => vnum(x.id) > vnum(verUi.v.id) && vnum(x.id) <= vnum(online.id)).length),
            })}
          </div>
        </Modal>
      )}
      {verUi?.kind === "delete" && (
        <Modal
          title={t("ver.delConfirmTitle", { id: verUi.id })}
          width={440}
          onClose={() => setVerUi(null)}
          footer={
            <div className="flex justify-end gap-2">
              <Button size="sm" onClick={() => setVerUi(null)}>{t("common.cancel")}</Button>
              <Button size="sm" tone="danger" disabled={busy}
                onClick={() => { const id = verUi.id; deleteDraft(id); }}>
                {busy ? t("ver.working") : t("ver.delete")}
              </Button>
            </div>
          }
        >
          <Notice tone="warning">{t("ver.delConfirmBody")}</Notice>
        </Modal>
      )}
      {verUi?.kind === "trash" && (
        <TrashDialog
          onClose={() => setVerUi(null)}
          onRestored={(v) => { void (async () => {
            setVerUi(null);
            setToast(t("ver.restored", { id: vid(v) }));
            try { setVersions((await fetchVersions()).map(toVersion)); }
            catch (e) { setToast(t("ver.syncFail", { msg: e instanceof Error ? e.message : String(e) }), true); }
          })(); }}
        />
      )}
      {verUi?.kind === "prune" && (
        <Modal
          title={t("ver.pruneDrafts")}
          width={460}
          onClose={() => setVerUi(null)}
          footer={
            <div className="flex justify-end gap-2">
              <Button size="sm" onClick={() => setVerUi(null)}>{t("common.cancel")}</Button>
              <Button size="sm" tone="danger" disabled={busy} onClick={() => pruneDrafts()}>
                {busy ? t("ver.working") : t("ver.pruneDrafts")}
              </Button>
            </div>
          }
        >
          <Notice tone="warning">
            {t("ver.pruneConfirm", {
              id: versions.filter((v) => v.status === "draft").map((v) => v.id).sort((a, b) => vnum(b) - vnum(a))[0] ?? "-",
            })}
          </Notice>
        </Modal>
      )}
      {verUi?.kind === "history" && (
        <VersionHistory
          versions={versions} currentId={curVid} hasOnline={!!online}
          onClose={() => setVerUi(null)}
          onView={(v) => requestSwitch(v.id, "history")}
          onCompare={(v) => { void compareVersionToOnline(v); }}
          onRollback={(v) => { void openRollback(v); }}
          onActivate={(v) => setVerUi({ kind: "activate", v })}
          onLog={(v) => { setVerUi(null); setLogView({ vid: v.id, scope: "one" }); }}
          onPerson={(n) => { setVerUi(null); openPersonLog(n); }}
        />
      )}
      {verUi?.kind === "diffPair" && (
        <Drawer title={t("ver.diff.pair")} width={900} onClose={() => setVerUi(null)}>
          {verUi.leftId && verUi.rightId && <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
            <label>{t("auditChange.leftVersion")} <select aria-label={t("auditChange.leftVersion")} className="rounded-base border border-line px-2 py-1" value={verUi.leftId} onChange={(e) => { void comparePair(e.target.value, verUi.rightId!); }}>
              {versions.map((v) => <option key={v.id} value={v.id}>{v.id} · {v.user || t("auditChange.unknownActor")}</option>)}
            </select></label>
            <label>{t("auditChange.rightVersion")} <select aria-label={t("auditChange.rightVersion")} className="rounded-base border border-line px-2 py-1" value={verUi.rightId} onChange={(e) => { void comparePair(verUi.leftId!, e.target.value); }}>
              {versions.map((v) => <option key={v.id} value={v.id}>{v.id} · {v.user || t("auditChange.unknownActor")}</option>)}
            </select></label>
          </div>}
          <DiffView
            authoritativeChanges={verUi.changes} samePublishedContent={verUi.samePublishedContent} checksumConflict={verUi.checksumConflict} tabbed left={verUi.left} right={verUi.right} leftLabel={verUi.la} rightLabel={verUi.lb}
            onJump={(id) => { setSelId(id); setVerUi(null); }}
          />
        </Drawer>
      )}
      {verUi?.kind === "check" && (
        <PublishCheck
          levels={levels} results={results}
          onClose={() => setVerUi(null)}
          onJump={(id) => { setSelId(id); setPage("building"); setVerUi(null); }}
        />
      )}
      {verUi?.kind === "rollback" && (
        <Drawer
          title={t("ver.rbTitle", { id: verUi.v.id })} width={900} onClose={() => setVerUi(null)}
          extra={<Button tone="danger" onClick={() => newDraftFrom(verUi.v, true)}>{t("ver.rbConfirm")}</Button>}
        >
          <Notice tone="warning">{t("ver.rbNote", { id: verUi.v.id })}</Notice>
          <div className="h-3" />
          <DiffView
            left={verUi.left} right={verUi.right} authoritativeChanges={verUi.changes} tabbed
            leftLabel={online ? t("ver.online", { id: online.id }) : t("ver.noOnline")} rightLabel={verUi.v.id}
          />
        </Drawer>
      )}
      {verUi?.kind === "confirmSwitch" && (() => {
        // 从历史抽屉进来的确认框，取消要回到抽屉，不能整组关掉
        const cancel = () => setVerUi(verUi.back === "history" ? { kind: "history" } : null);
        return (
        <Modal
          title={t("ver.switchDirtyTitle")}
          width={440}
          onClose={cancel}
          footer={
            <>
              <Button onClick={cancel}>{t("common.cancel")}</Button>
              <Button tone="danger" onClick={() => switchVersion(verUi.vid)}>{t("ver.switchDiscard")}</Button>
            </>
          }
        >
          <p className="text-sm text-ink-regular">{t("ver.switchDirtyBody", { id: verUi.vid })}</p>
        </Modal>
        );
      })()}
      {(verUi?.kind === "pubErrors" || verUi?.kind === "actErrors") && (
        <Drawer
          title={t(verUi.kind === "actErrors"
            ? "ver.actErrTitle"
            : verUi.from === "check" ? "ver.checkErrTitle" : "ver.pubErrTitle")}
          width={720}
          onClose={() => setVerUi(null)}
          extra={verUi.kind === "actErrors" ? (
            <>
              <Button disabled={busy} onClick={() => { const v = versions.find((x) => x.id === verUi.v); if (v) newDraftFrom(v, false); }}>
                {t("ver.newDraftFrom")}
              </Button>
              <Button tone="primary" disabled={busy} onClick={() => doActivateOnly(verUi.v, lastRefreshModeRef.current)}>{t("ver.actRetry")}</Button>
            </>
          ) : undefined}
        >
          <Notice tone="danger">
            {verUi.kind === "actErrors"
              ? t("ver.actErrHint", { id: verUi.v, n: verUi.rows.length })
              : verUi.from === "check"
                ? t("ver.checkErrHint", { n: verUi.rows.length })
                : t("ver.pubErrHint", { n: verUi.rows.length })}
          </Notice>
          <div className="mt-2 text-xs text-ink-placeholder">{t("ver.pubErr.jumpHint")}</div>
          {/*
            每条一张卡：先「哪一页 · 哪一项」，再「现在是什么」「怎么改」，右边「去修改」直接打开那一项。
            服务端的 path 与规则码收进「技术信息」：运营要的是去哪儿改什么，那串路径是给排查的人的
            （10-21 a0=1201：两条 rewards[uuid].items[1].ref 平铺在「其他」里，运营不知道怎么办）。
          */}
          {(() => {
            /*
             * 按页分组、按菜单顺序排，每组一行组头「页面 · N 项」（10-10 从零建代理：一上来五十多条平铺，
             * 新人不知道从哪儿下手）。玩法格各页排在「副玩法配置」那一位；翻不出页面的放最后
             */
            const order = ALL_ITEMS.map((it) => it.id);
            const rank = (pg: string | undefined) => {
              if (!pg) return Number.MAX_SAFE_INTEGER;
              const i = order.indexOf(pg);
              return i >= 0 ? i : pg.startsWith("subgame-") ? Math.max(0, order.indexOf("subgame")) + 0.5 : Number.MAX_SAFE_INTEGER - 1;
            };
            const items = verUi.rows.map((r) => {
              const t0 = issueTarget(r.path);
              // 天赋树两套体系各一页：路径分不出来，按本版选的建筑体系送
              const t1 = t0.page === "cost" && selectors.buildingSystem === "pettown" ? { ...t0, page: "pettown-star" } : t0;
              const tgt = t1.page === "pettown-talent" && selectors.buildingSystem === "levels" ? { ...t1, page: "level-talent" } : t1;
              return { r, tgt };
            });
            const groups = new Map<string, typeof items>();
            for (const it of items) {
              const k = it.tgt.page ?? "";
              groups.set(k, [...(groups.get(k) ?? []), it]);
            }
            const keys = [...groups.keys()].sort((a, b) => rank(a || undefined) - rank(b || undefined));
            return keys.map((k) => {
              const list = groups.get(k)!;
              return (
                <section key={`g:${k}`} className="mt-3">
                  <div className="mb-1.5 text-xs font-medium text-ink-secondary">
                    {k ? t(`nav.${k}` as MsgKey) : t("ver.checkOther")} · {t("ver.checkGroupN", { n: list.length })}
                  </div>
                  <div className="flex flex-col gap-2">
                    {list.map(({ r, tgt }, i) => {
                      const pageName = tgt.page ? t(`nav.${tgt.page}` as MsgKey) : "";
                      // 通关奖励按 rewardId 查是哪一关（萌宠小镇是第几星），不然运营得在几十关里挨个点开找
                      const rw = tgt.rowId ? rewards.find((x) => x.id === tgt.rowId) : undefined;
                      const where = rw ? t(rw.buildingSystem === "pettown" ? "card.dropStar" : "issue.rewardLevel", { id: rw.levelId }) : "";
                      // 奖励项按类型叫（「物品卡」）：弹窗按类型分组，「第 2 项」在弹窗里对不上
                      const itemKind = rw ? rw.items[Number(/\.items\[(\d+)\]/.exec(r.path)?.[1] ?? -1)]?.kind : undefined;
                      const field = itemKind ? t(REWARD_LABEL[itemKind]) : tgt.labelKey ? t(tgt.labelKey, tgt.labelParams) : "";
                      const title = [pageName || tgt.fallback, where, field].filter(Boolean).join(" · ");
                      const jump = tgt.page
                        ? () => {
                          if (rw) setRewardFocus({ id: rw.id });
                          setPage(tgt.page as string); setVerUi(null);
                        }
                        : undefined;
                      return (
                        <div key={`${k}:${i}`} className="rounded-base border border-line-lighter px-3 py-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 font-medium text-ink">{title}</div>
                            {jump && <Button size="sm" tone="primary" className="flex-none" onClick={jump}>{t("ver.pubErr.fix")}</Button>}
                          </div>
                          {r.actual && r.actual !== "缺" && (
                            <div className="mt-1 text-sm text-ink-regular">
                              {t("ver.pubErr.now")}<span className="text-danger">{r.actual}</span>
                            </div>
                          )}
                          <div className="mt-0.5 text-sm text-ink-regular">
                            {t(r.actual === "缺" ? "ver.pubErr.missing" : "ver.pubErr.how")}<span className="font-medium text-ink">{r.expected}</span>
                          </div>
                          {!jump && <div className="mt-0.5 text-xs text-ink-placeholder">{t("ver.pubErr.noJump")}</div>}
                          <details className="mt-1 text-[11px] text-ink-placeholder">
                            <summary className="cursor-pointer select-none">{t("ver.pubErr.tech")}</summary>
                            <div className="mt-0.5 break-all font-mono">{r.path} · {r.rule}</div>
                          </details>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            });
          })()}
        </Drawer>
      )}
      {verUi?.kind === "rollbackResult" && (
        <RollbackResult
          id={verUi.id} src={verUi.src} levels={levels}
          onClose={() => setVerUi(null)}
          onFix={(id) => { setSelId(id); setPage("building"); setVerUi(null); }}
          onPublish={() => setVerUi({ kind: "publish" })}
        />
      )}
      {toast && (
        <div
          role={toast.bad ? "alert" : "status"}
          className={`fixed left-1/2 top-4 z-[90] -translate-x-1/2 rounded-base border px-4 py-2 text-sm shadow-light ${
            toast.bad ? "border-danger-200 bg-danger-50 text-danger-700" : "border-success-200 bg-success-50 text-success-700"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {confirmLogout && (
        <Modal
          title={t("logout.title")}
          width={440}
          onClose={() => setConfirmLogout(false)}
          footer={
            <>
              <Button onClick={() => setConfirmLogout(false)}>{t("common.cancel")}</Button>
              <Button
                tone="danger"
                onClick={() => {
                  // 先记日志再清凭据——清完就没 token 可用来记了
                  logAction("session.logout", undefined, undefined, agent ?? undefined);
                  clearSession();
                  // 回自己的登录页（Discord 是主入口），不再送去 super-admin
                  location.replace("/");
                }}
              >
                {t("top.logout")}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-regular">{t("logout.confirm")}</p>
        </Modal>
      )}

      {namesFor != null && levels.find((l) => l.id === namesFor) && (
        <LevelNamesDialog
          lv={levels.find((l) => l.id === namesFor)!}
          editable={editable}
          onClose={() => setNamesFor(null)}
          onChange={(patch) => patchLevel(namesFor, patch)}
        />
      )}

      {assetCell && (
        <AssetDialog
          lv={levels.find((l) => l.id === selId) ?? levels[0]}
          cell={assetCell}
          version={vnum(curVid)}
          revision={revs[curVid] ?? 0}
          editable={editable}
          onRevision={(next) => setRevs((r) => ({ ...r, [curVid]: next }))}
          onLocal={(key, kind, url) =>
            setLevels((ls) =>
              ls.map((l) =>
                l.id !== (selId ?? levels[0]?.id)
                  ? l
                  : { ...l, assets: { ...l.assets, [key]: { ...(l.assets[key] ?? { img: "", anim: "" }), [kind]: url } } },
              ),
            )}
          onClose={() => setAssetCell(null)}
        />
      )}
      {renamingRival && (
        <RenameRivalDialog
          rival={renamingRival}
          onClose={() => setRenamingRival(null)}
          // 边改边存：名字非空就交，随版本自动保存；改了什么记在版本修改记录里
          onChange={(name, color) => setRivals((xs) => xs.map((x) => (x.id === renamingRival.id ? { ...x, name, color } : x)))}
        />
      )}
      {removingRival && (
        <RemoveRivalDialog
          rival={removingRival}
          filled={rivalFilled(removingRival.id)}
          inUse={selectors.costSource === `rival:${removingRival.id}`}
          onClose={() => setRemovingRival(null)}
          onConfirm={() => {
            removeRival(removingRival);
            logAction("config.removeRival", removingRival.id, { name: removingRival.name }, agent ?? undefined);
            setRemovingRival(null);
          }}
        />
      )}
      {batching && (
        <BatchGridDialog
          levels={levels}
          onClose={() => setBatching(false)}
          onSubmit={({ from, to, N, M }) => {
            setLevels((ls) =>
              ls.map((l) => {
                if (l.id < from || l.id > to) return l;
                const next: Level = { ...l };
                // 与逐格改 N/M 同一套语义：补位留空，不外推
                if (N != null) {
                  next.N = N;
                  next.buildings = Array.from({ length: N }, (_, i) =>
                    l.buildings[i] ?? { code: `${l.id}-building-${i + 1}`, name: `Building ${i + 1}`, main: false });
                  next.bldInit = Array.from({ length: N }, (_, i) => l.bldInit[i] ?? null);
                }
                if (M != null) {
                  next.M = M;
                  next.star = Array.from({ length: M }, (_, i) => l.star[i] ?? null);
                }
                return next;
              }),
            );
            logAction("config.batchGrid", `level:${from}-${to}`, { N, M }, agent ?? undefined);
            setBatching(false);
          }}
        />
      )}
      {addingAt != null && (
        <AddLevelDialog
          levels={levels}
          curveTarget={(globals.targetRollsBase ?? 0) > 0 ? (id) => levelTargetRolls({ id, targetRolls: null }, globals) : undefined}
          defaultAt={addingAt}
          onClose={() => setAddingAt(null)}
          onSubmit={(v) => {
            // 保存在路上时不插关：待插清单记的是位置号，这时候插会让在途那一关的号顺延、回执对不上
            if (savingRef.current) { setToast(t("ver.autoBusyAdd"), true); return; }
            setLevels((ls) =>
              renumber([
                ...ls.slice(0, v.at - 1),
                // I18nField 给的是「语言 → 名称」；Level.i18n 是「语言 → {name, stage}」。
                // en 存进 Level.name（默认语言），其余进 i18n。
                // 单掷金币版本：新关的 g 按前几关的走势先填上推荐值，不留空卡发布
                { ...makeLevel(
                  v.at,
                  {
                    name: v.name,
                    i18n: Object.fromEntries(
                      Object.entries(v.i18n)
                        .filter(([code, text]) => code !== "en" && (text ?? "").trim() !== "")
                        .map(([code, text]) => [code, { name: text }]),
                    ),
                    targetRolls: (globals.targetRollsBase ?? 0) > 0 ? null : v.targetRolls,
                    targetRollsOverride: (globals.targetRollsBase ?? 0) > 0 ? v.targetRolls : null,
                  },
                  ls[v.at - 2],
                ), ...(curEcon.model === 2 ? { gMilli: suggestGMilli(ls.map((l) => l.gMilli), v.at) } : {}) },
                ...ls.slice(v.at - 1),
              ]),
            );
            // 已登记的新增位置在插入点之后的要跟着顺延，保存时按最终编号升序插入
            setPendingAdds((xs) => [...xs.map((a) => (a >= v.at ? a + 1 : a)), v.at]);
            setAddingAt(null);
          }}
        />
      )}
    </AppShell>
    </CollabContext.Provider>
    </BScaleContext.Provider>
    </TzProvider>
  );
}

/**
 * model 2（单掷金币）版本的造价页 / 星级页顶部说明：金币与造价都按每关（每星）的 g，E、B、收入曲线不参与计算。
 * 这些值没删，切回旧口径的版本时还用；页面上不再显示，免得以为改了有用
 */
function EconModel2Notice({ unit = "level" }: { unit?: "level" | "star" }) {
  const t = useT();
  return (
    <div className="mb-3 rounded-base border border-line bg-white p-3 text-sm">
      <div className="font-medium text-ink">{t("econ.m2.title")}</div>
      <p className="mt-1 text-xs leading-relaxed text-ink-secondary">{t(unit === "star" ? "econ.m2.bodyStar" : "econ.m2.body")}</p>
    </div>
  );
}
